import YahooFindings from './yahoo/YahooFindings';
import { ensureYahooReviewPrepared } from './yahoo/prepareYahooReview';
import { YAHOO_BULK_PLACEHOLDER } from './yahoo/yahooBulkEdit';
/**
 * CampaignLaunchWorkflow — new Sidekick UI for the fast-track
 * planning-mode flow. Replaces the legacy "Start a Campaign" entrypoint at
 * /campaign/v3/start.
 *
 * End-to-end integration uses existing endpoints — no backend changes:
 *   upload     → POST /api/upload                 (per file, GCS-backed)
 *              → POST /api/v2/orchestration/analyze-files  (recommendedType)
 *   create     → POST /api/v2/campaigns           (planningMode = true)
 *   processing → GET  /api/v2/campaigns/:id/orchestration  (poll status)
 *   summary    → GET  /api/v2/campaigns/:id/planning-review (launcherResult)
 *   review     → /campaigns/:id/plan-review       (existing review surface,
 *                                                   re-skin is a follow-up)
 *
 * Legacy CampaignStarterV3 is left untouched and remains importable.
 */

import {
  isYahooResult, buildYahooReviewRows, summarizeYahooResult,
  breakdownYahooByCampaign, extractYahooCampaignConfig,
  YAHOO_GROUP_COLS, YAHOO_GROUP_KEYS, YAHOO_EDITABLE, YAHOO_FIELD_EDITORS,
  YAHOO_READ_ONLY_REASONS, YAHOO_LIST_FIELDS,
} from './yahoo/yahooReview';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, getApiUrl } from '../../utils/apiConfig';
import { isLocalGtReviewOnly, GT_REVIEW_ONLY_MESSAGE } from '../../utils/localGtReviewOnly';
import { getFolderDisplayLabel } from '../../utils/folderHierarchy';
import { useAccount } from '../../contexts/AccountContext';
import { getUserData } from '../../utils/userUtils';
import { useEnabledChannels } from '../../hooks/useEnabledChannels';
import { useChannelConfigs } from '../../hooks/useChannelConfigs';
import { useAccountSettings } from '../../hooks/useAccountSettings';
import { isEnabledForAnyOfPlatforms } from '../../utils/accountSettingPlatformList';
import { MMGY_PLAN_ONLY_IO_TYPE, MMGY_IO_TYPE_HELP, isMmgyPlanOnlyAvailable, effectiveMmgyIoType } from '../../utils/mmgyIoType';
import './CampaignLaunchWorkflow.css';
// Sidekick's CampaignLaunchWorkflow is the entry point at /campaign/v3/start.
// GroundTruth-specific helpers, constants, and the QA pivot panel live in
// ./groundtruth/. When other Sidekick accounts (e.g. MMGY, Adara) need a
// parallel review flow, add a sibling submodule (./mmgy/, ./adara/, …)
// rather than expanding the GT conditionals in this file.
import {
  GT_GROUP_COLS,
  GT_GROUP_KEYS,
  GT_FIELD_EDITORS,
  GT_STATIC_OPTIONS,
  GT_READ_ONLY_REASONS,
  GT_ROW_TO_AG,
} from './groundtruth/gtConstants';
import { gtLookupTypesFor, gtCampaignSummaryFields } from './groundtruth/gtFieldDescriptor';
import {
  normalizeAdgroupName,
  resolveGtIssueField,
  buildGtReviewRows,
  gtCtsFailure,
  summarizeReviewCtsResult,
} from './groundtruth/gtRowBuilders';
import { GtQaPivotPanel } from './groundtruth/GtQaPivotPanel';
import { isGtLauncherResult, gtLauncherToCtsReview } from './groundtruth/gtLauncherAdapter';
import {
  fmtDate,
  fmtCurrency,
  fmtBadgeClass,
} from './clwFormatters';
import {
  summarizeDv360Result,
  breakdownDv360ByPackage,
  breakdownDv360ByMonth,
  buildDv360ReviewRows,
  extractDv360CampaignConfig,
} from './dv360/dv360RowBuilders';
import {
  summarizeTtdResult,
  breakdownTtdByCampaign,
  buildTtdReviewRows,
  extractTtdCampaignConfig,
} from './ttd/ttdRowBuilders';
import {
  isGadsLauncherResult,
  summarizeGadsResult,
  breakdownGadsByCampaign,
  buildGadsReviewRows,
  extractGadsCampaignConfig,
  gadsResultSubtype,
} from './gads/gadsRowBuilders';
import {
  isGadsPlatform,
  gadsGroupCols,
  gadsGroupKeys,
  gadsEditableFields,
  gadsListFields,
  gadsReadOnlyReasons,
  GADS_FIELD_EDITORS,
} from './gads/gadsConstants';
import {
  isFbLauncherResult,
  summarizeFbResult,
  breakdownFbByPackage,
  buildFbReviewRows,
  extractFbCampaignConfig,
} from './fb/fbRowBuilders';
import {
  isFbPlatform,
  FB_GROUP_COLS,
  FB_GROUP_KEYS,
  FB_EDITABLE,
  FB_FIELD_EDITORS,
  FB_LIST_FIELDS,
  FB_ROW_LABEL,
  FB_READ_ONLY_REASONS,
} from './fb/fbConstants';
// Review-grid column groups + per-platform editable/editor-type sets, and the
// cell detail panel itself. Shared with LaunchCampaignDetails (the Launch
// board's campaign details page) so both surfaces drive the same modals and
// the same editable-field allow-lists from one definition.
import {
  GROUP_COLS,
  GROUP_KEYS,
  DV360_EDITABLE,
  TTD_EDITABLE,
  GT_EDITABLE,
  DV360_FIELD_EDITORS,
  TTD_FIELD_EDITORS,
  DV360_LIST_FIELDS,
  TTD_LIST_FIELDS,
  GT_LIST_FIELDS,
  DV360_READ_ONLY_REASONS,
  TTD_READ_ONLY_REASONS,
} from './clwGridConstants';
import ReviewGrid from './ReviewGrid';
import { findGtLaunchedWorkspace } from '../GtLaunchedWorkspace/gtLaunchState';
import ChangeLogButton from './ChangeLogPanel';
import SourceFilesButton from './SourceFilesPanel';
import { PlanNotices } from './PlanNotices';
import { GtInstructionReviewPanel } from './groundtruth/GtInstructionReviewPanel';
import { displayFinding } from './groundtruth/gtInstructionLabels';
import { DeliveredMetricsStrip } from './DeliveredMetricsStrip';
import { mergePlatformSetup, budgetTile } from './platformSetupSummary';
import { cur } from '../../utils/metricFormat';
import useReviewGridEdits from './useReviewGridEdits';
import { applyGtConfigEdits, applyLauncherResultEdits, droppedGtEdits, gtEditWarnings, GT_CAMPAIGN_ROW_ID } from './reviewEdits';
import { GtCreativeUploadPanel } from './groundtruth/GtCreativeUploadPanel';
import { editsSurvivingUpload } from './groundtruth/gtCreativeUploadEdits';
import { buildLaunchedEditRequests } from './buildLaunchedEditRequests';
import { dispatchLaunchedEdits } from './dispatchLaunchedEdits';
// Sidekick agent sidebar — generic chat + suggested-edits shell. Each
// destination's ReviewPane wires its own agent session and copy through
// the props; the sidebar is platform-agnostic so other platforms can
// reuse it by passing in a different session hook + config.
import { AgentSidebar } from './agent/AgentSidebar';
import { BulkEditPanel } from './agent/BulkEditPanel';
import { buildBulkDigest, rowIdSet, serializeListFields } from './agent/bulkDigest';
import { validateStaging, mergeEdits, mergeListOps, mergeRowOps } from './agent/bulkOps';
import { BULK_TAB, RUNS_TAB, QA_AGENT_TAB_ENABLED, isSelectableMode } from './agent/agentTabs';
import { previewReadyToShow } from './agent/previewGate';
import { buildGtBulkFeedItems } from './agent/gtChatFeed';
import { gtBulkPlaceholder } from './agent/gtBulkPlaceholder';
import { OptimizationRunGate } from './agent/OptimizationRunGate';
import { OptimizationRunBar } from './agent/OptimizationRunBar';
import { OptimizationActionBar } from './agent/OptimizationActionBar';
import { isAgentWorking } from './agent/agentBusy';
import { CampaignRunHistory } from './agent/CampaignRunHistory';
import { useOptimizationSession, OPT_PHASE } from '../../hooks/useOptimizationSession';
import { useCampaignRunHistory } from '../../hooks/useCampaignRunHistory';
import { useBulkEditorSession } from '../../hooks/useBulkEditorSession';
import { useGtCtsReviewerSession } from '../../hooks/useGtCtsReviewerSession';
import { useMmgyClwReviewerSession } from '../../hooks/useMmgyClwReviewerSession';
import OptimizeCampaignModal from '../OptimizeCampaignModal';

// Agent messages arrive as markdown (`**bold**`, headers, code, lists,
// tables). Render natively so the reviewer reads formatted text, not raw
// syntax. Memoised arrays — react-markdown re-renders if remarkPlugins
// changes identity, so we keep these module-scoped.
// — keep the `channelKey` aligned with the keys used in CampaignCreation.js
// (apps/app-frontend/src/components/CampaignCreation.js:111-125) so a single
// admin toggle controls visibility in both UIs.
const CLW_DESTINATIONS = [
  { value: 'dv360', label: 'DV360', channelKey: 'dv360' },
  { value: 'ttd', label: 'The Trade Desk', channelKey: 'ttd' },
  { value: 'yahoo', label: 'Yahoo DSP', channelKey: 'yahoo' },
  { value: 'youtube-dv360', label: 'YouTube DV360', channelKey: 'youtube-dv360' },
  { value: 'facebook', label: 'Facebook', channelKey: 'facebook' },
  { value: 'google-ads-search', label: 'Google Search', channelKey: 'google-ads-search' },
  { value: 'google-ads-pmax', label: 'Google PMax', channelKey: 'google-ads-pmax' },
  { value: 'google-ads-demandgen', label: 'Google DemandGen', channelKey: 'google-ads-demandgen' },
  { value: 'tiktok', label: 'TikTok', channelKey: 'tiktok' },
  { value: 'madhive', label: 'MadHive', channelKey: 'madhive' },
  { value: 'universal-ads', label: 'Universal Ads', channelKey: 'universal-ads' },
  { value: 'amazon-dsp', label: 'Amazon DSP', channelKey: 'amazon-dsp' },
  { value: 'gam', label: 'Google Ad Manager', channelKey: 'google-ad-manager' },
  { value: 'groundtruth', label: 'GroundTruth', channelKey: 'groundtruth' },
  { value: 'reddit', label: 'Reddit', channelKey: 'reddit' },
  { value: 'linkedin', label: 'LinkedIn', channelKey: 'linkedin' },
  { value: 'openai', label: 'OpenAI', channelKey: 'openai' },
];

// OpenAI authenticates via api_key in channel_config only — no per-campaign
// advertiser/profile/page/customer ID. Every gating helper that touches the
// platform-id input branches on this.
const platformRequiresId = (dest) => dest !== 'openai';

// ─── helpers ──────────────────────────────────────────────
const fmtInt = (n) => Number(n || 0).toLocaleString();
const baseName = (filename) => (filename || '').replace(/\.[^.]+$/, '');
const detectPlatformFromFilename = (filename = '') => {
  const n = filename.toLowerCase();
  if (!n.includes('launch') || !n.includes('template')) return null;
  if (n.includes('dv360') || n.includes('dv_360')) return 'dv360';
  if (n.includes('yahoo')) return 'yahoo';
  if (n.includes('ttd') || n.includes('tradedesk')) return 'ttd';
  return null;
};
// MMGY uploads ship a PDF insertion order plus one or more Excel proposal
// sheets — the backend processor (campaign-services/campaign_processor/mmgy)
// treats the Excels as launch templates. Detection is per-account so a
// non-MMGY user uploading an .xlsx still hits the generic plan path.
// MMGY plan-only (IO type 'plan-only'): the Excel media plan IS the insertion
// order — no PDF; the launcher builds the IO from the Excel.
const isPlanFile = (name = '', { isMmgy = false, mmgyPlanOnly = false } = {}) => (
  isMmgy
    ? (mmgyPlanOnly ? /\.(xlsx|xls|xlsm)$/i.test(name) : /\.pdf$/i.test(name))
    : /\.(xlsx|xls|xlsm|csv|pdf)$/i.test(name)
);
const isLaunchTemplate = (name = '', { isMmgy = false } = {}) => (
  /\.json$/i.test(name)
  || /launch.*template/i.test(name)
  || (isMmgy && /\.(xlsx|xls|xlsm)$/i.test(name))
);
const isGtExcel = (name = '') => /\.(xlsx|xls|xlsm)$/i.test(name);
// GT also accepts an insertion-order PDF as a single-file plan input — gt-api
// runs the full PDF → IO JSON → campaign-config → launch pipeline
// (gt_campaign_launcher.launch_campaign_from_pdf). The same `planFiles`
// state holds either kind; downstream code branches on whichever predicate
// matches so the existing Excel path is untouched.
const isGtPdf = (name = '') => /\.pdf$/i.test(name);
// gt-api also accepts a ZIP that wraps the Excel media plan. The handler
// unzips it and chains into the standard Excel-launch pipeline; we wire
// CLW the same way as the PDF dropzone — submit straight to gt-api, skip
// the CTS review checkpoint, render the launch result on return.
const isGtZip = (name = '') => /\.zip$/i.test(name);

// ─── DV360 / TTD field decoders ─────────────────────────────
// DV360-specific decoders, parsers, and row builders live in ./dv360/.
// Shared display helpers (ymdFromObj, fmtDate, fmtCurrency, deriveFormat …)
// live in ./clwFormatters.js. The dispatchers below pick a platform family
// from the `platform` field on launcherResult.

// Which builder family owns a launcherResult: 'ttd' | 'gads' | 'fb' | 'dv360'.
// Google Ads and Facebook are detected from the platform id with a
// payload-shape fallback; Facebook RELIES on that fallback, because the FB
// pipeline never stamps `platform` on parsed_response (only DV360 / Reddit /
// OpenAI / Google Ads / MadHive do — campaign_launcher/reasoning.py). DV360
// stays the default so an unrecognised result behaves exactly as it did before
// either family existed here.
function launcherFamily(result) {
  if (isYahooResult(result)) return 'yahoo';
  const platform = String(result?.platform || result?.Platform || '').toLowerCase();
  if (platform === 'ttd' || platform === 'thetradedesk') return 'ttd';
  if (isGadsPlatform(platform)) return 'gads';
  if (isFbPlatform(platform)) return 'fb';
  if (platform === 'dv360') return 'dv360';
  if (isGadsLauncherResult(result)) return 'gads';
  if (isFbLauncherResult(result)) return 'fb';
  return 'dv360';
}

// Compute parse-summary stats from a launcherResult JSON. Supports DV360
// (`insertionOrders` + `lineItems` + `packages`), TTD (`Campaigns` +
// `AdGroups`) and Google Ads (`campaigns` + `ad_groups` / `assets`).
function summarizeLauncherResult(result) {
  if (!result) return null;
  const family = launcherFamily(result);
  if (family === 'yahoo') return summarizeYahooResult(result);
  if (family === 'ttd') return summarizeTtdResult(result);
  if (family === 'gads') return summarizeGadsResult(result);
  if (family === 'fb') return summarizeFbResult(result);
  return summarizeDv360Result(result);
}

// (GT review-cts summary helpers, issue→field map, row builders, QA
// pivot data, and the QA pivot panel were extracted to ./groundtruth/.)

// One row per IO (DV360) or per campaign (TTD / Google Ads) — drives the
// summary-pane "By IO" / "By campaign" allocation table.
function breakdownByPackage(result) {
  if (!result) return [];
  const family = launcherFamily(result);
  if (family === 'yahoo') return breakdownYahooByCampaign(result);
  if (family === 'ttd') return breakdownTtdByCampaign(result);
  if (family === 'gads') return breakdownGadsByCampaign(result);
  // Facebook plans state ONE campaign, so a per-campaign table would be a
  // single meaningless row — the package is the allocation unit there.
  if (family === 'fb') return breakdownFbByPackage(result);
  return breakdownDv360ByPackage(result);
}

// ─── ROW MAPPING — launcherResult → placement rows ─────────
// One row per line item (DV360) or ad group (TTD). Best-effort field extraction:
// missing fields fall back to '—' so the grid still renders.

// Build flat rows from a launcherResult JSON. One row per LI / AdGroup /
// Google Ads ad group or asset group.
function buildReviewRows(result) {
  if (!result) return [];
  const family = launcherFamily(result);
  if (family === 'yahoo') return buildYahooReviewRows(result);
  if (family === 'ttd') return buildTtdReviewRows(result);
  if (family === 'gads') return buildGadsReviewRows(result);
  if (family === 'fb') return buildFbReviewRows(result);
  // Opt in to the copy-sidecar fill: this is the review page, which has no
  // derivation of its own (unlike the copy wizard) and which honours the
  // read-only marking the fill sets — see the builder's docstring.
  return buildDv360ReviewRows(result, { fillFromTargetingPlan: true });
}

// Group rows by an arbitrary key, summing impressions and counting line
// items. Used for the summary-page breakdown switcher.
function breakdownBy(rows, keyFn, labelFn = (k) => k) {
  const buckets = new Map();
  for (const r of rows) {
    const key = keyFn(r) || 'Other';
    if (!buckets.has(key)) buckets.set(key, { key, label: labelFn(key, r), impressions: 0, lineItemCount: 0, budget: 0 });
    const b = buckets.get(key);
    b.impressions += Number(r.imps) || 0;
    b.lineItemCount += 1;
    b.budget += Number(r.budget) || 0;
  }
  return [...buckets.values()].sort((a, b) => b.impressions - a.impressions);
}

// Extract campaign-level configuration from the parsed JSON for the
// "Configuration" card on the summary page.
function extractCampaignConfig(result) {
  if (!result) return {};
  const family = launcherFamily(result);
  if (family === 'yahoo') return extractYahooCampaignConfig(result);
  if (family === 'ttd') return extractTtdCampaignConfig(result);
  if (family === 'gads') return extractGadsCampaignConfig(result);
  if (family === 'fb') return extractFbCampaignConfig(result);
  return extractDv360CampaignConfig(result);
}

// How long ONE page attachment watches a GT IO->CTS run before giving up.
// Measured from when polling starts, NOT from dispatch: the worker's truth
// lives in GCS and the run stays resumable, so this only bounds this tab's
// watching. (Measuring from dispatch made a resumed run inherit an
// already-spent deadline and fail instantly on reload — the UAT "it fails,
// I refresh, it returns" loop.)
const IO_CTS_TIMEOUT_MS = 4 * 60 * 60 * 1000;
// Past this, tell the user the run is slow rather than leaving the last
// cosmetic tick label on screen for twenty minutes.
const IO_CTS_SLOW_NOTICE_MS = 30 * 60 * 1000;
const IO_CTS_POLL_INTERVAL_MS = 3000;

// Cosmetic progress ticks for the IO→CTS wait — the GCS poll drives real
// completion, these just keep the bar moving. `at` is milliseconds since
// dispatch, so a resumed run shows the bar where it should be rather than
// restarting from the beginning.
const IO_CTS_TICKS = [
  { at: 0, idx: 1, pct: 15 },      // Dispatching CTS review to gt-api
  { at: 2000, idx: 2, pct: 30 },   // Parsing PDF → IO JSON
  { at: 10000, idx: 3, pct: 45 },  // Generating campaign config
  { at: 25000, idx: 4, pct: 60 },  // Validating campaign fields
  { at: 45000, idx: 5, pct: 75 },  // Validating adgroups & audiences
  { at: 70000, idx: 6, pct: 88 },  // Compiling review report
];

// GT config paths are stored as bucket-relative keys; the IO handler reports
// its parsed config as a gs:// URL.
function toGcsKey(raw) {
  if (!raw || typeof raw !== 'string') return null;
  return raw.startsWith('gs://') ? raw.replace(/^gs:\/\/[^/]+\//, '') : raw;
}

// sessionStorage helper — keep restore lightweight and namespaced.
const SS_KEY = (id) => `clw:state:${id || 'pending'}`;
// GT IO review "Upload Creatives" ceiling — same as the Node route's multer limit.
const CREATIVE_UPLOAD_MAX_BYTES = 100 * 1024 * 1024;

// Ceiling for the GroundTruth Additional Instructions field. Matches the
// Node route (MAX_ADDITIONAL_INSTRUCTIONS_CHARS) and the gt-api worker.
export const GT_INSTRUCTIONS_MAX = 4000;

function saveSessionState(state) {
  try {
    sessionStorage.setItem(SS_KEY(state.campaignId), JSON.stringify(state));
  } catch { /* quota or disabled — fine, we're best-effort */ }
}
function loadSessionState(id) {
  try {
    const raw = sessionStorage.getItem(SS_KEY(id));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// Per-budget-segment (per month) view straight off the parsed JSON.
// Only DV360 carries segmented budgets — TTD and Google Ads state a single
// campaign budget, so both return [].
function breakdownByMonth(result) {
  const family = launcherFamily(result);
  // This monthly pivot reads DV360 budget segments. Yahoo flight schedules
  // are shown individually in its review grid.
  if (family === 'ttd' || family === 'gads' || family === 'fb' || family === 'yahoo') return [];
  return breakdownDv360ByMonth(result);
}

// (GROUP_COLS / GROUP_KEYS and the per-platform editable + editor-type
// sets moved to ./clwGridConstants.js — shared with LaunchCampaignDetails.
// GT_GROUP_COLS / GT_GROUP_KEYS live in ./groundtruth/gtConstants.js.)

// Lookup kind (from a list field's `lookup` descriptor) → the campaign-scoped
// search endpoint that resolves it. Shared by both grid hosts.
export const TTD_LOOKUP_PATHS = {
  'ttd-geo': 'ttd-geo-segments',
  'ttd-audience': 'ttd-audiences',
};

export const GT_LOOKUP_PATHS = {
  'gt-taxonomy': 'gt-taxonomy',
};

export const LOOKUP_PATHS = { ...TTD_LOOKUP_PATHS, ...GT_LOOKUP_PATHS, 'yahoo-geo': 'yahoo-geo', 'yahoo-audience': 'yahoo-audience', 'yahoo-category': 'yahoo-category' };

// CLW renders inside NewLayout, which already supplies the global
// Sidekick sidebar. A second sidebar inside CLW would be redundant —
// we drop ours so the user sees one consistent navigation rail.

// ─── Main component ────────────────────────────────────────
export default function CampaignLaunchWorkflow() {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { getAccountIdToUse, currentAccountName } = useAccount();
  const accountId = getAccountIdToUse?.();
  // MMGY accounts use a different upload contract: 1 PDF (insertion order)
  // + one or more Excel files acting as launch templates. Detection mirrors
  // the legacy CampaignCreation.js:186 check so a single naming convention
  // gates both entry points. The backend MMGY pipeline lives in the
  // campaign-services repo (campaign_processor/mmgy/).
  const isMmgy = useMemo(() => {
    const n = (currentAccountName || '').toLowerCase();
    return n === 'mmgy' || n.startsWith('mmgy-');
  }, [currentAccountName]);
  const user = useMemo(() => getUserData(), []);
  const userId = user?.userId || user?.id;

  // Per-account settings — drives MMGY IO Type visibility (mmgy vs
  // traveldesk) via the `enable-mmgy-io-type` flag. Stored as a
  // comma-separated platform list (e.g. 'dv360,ttd') so the selector
  // can be gated per destination. Empty/null = hidden.
  const { settings: accountSettings } = useAccountSettings(accountId, user);
  const planningGridForAllRoles = accountSettings?.['enable-v3-ui-planning-all-roles'] === 'true';

  // Account-scoped channel enablement. Drives the destination dropdown so
  // users only see platforms their account is configured for (e.g. a
  // GroundTruth-only account only sees GroundTruth).
  const { enabledChannels, isLoading: channelsLoading } = useEnabledChannels(accountId, user);
  const {
    getChannelsForPlatform,
    hasMultipleChannels,
    isLoading: channelConfigsLoading,
  } = useChannelConfigs(accountId, user);
  const availableDestinations = useMemo(() => {
    if (channelsLoading) return [];
    return CLW_DESTINATIONS.filter((d) => enabledChannels?.[d.channelKey]);
  }, [enabledChannels, channelsLoading]);

  // Snapshot the URL + sessionStorage at first render so we can lazy-init
  // state without flashing the upload pane on refresh.
  const initialUrlCampaign = useMemo(() => {
    try { return new URLSearchParams(window.location.search).get('campaign'); }
    catch { return null; }
  }, []);
  const initialCached = useMemo(
    () => (initialUrlCampaign ? loadSessionState(initialUrlCampaign) : null),
    [initialUrlCampaign]
  );

  // pane state — when ?campaign=X is in the URL we start on whatever pane
  // sessionStorage remembers (or 'processing' as a sane default) so the
  // user never sees the upload card flash during hydration.
  const [pane, setPane] = useState(() => {
    if (!initialUrlCampaign) return 'upload';
    // Summary pane was consolidated into the review pane; map any stale
    // sessionStorage value back to 'review' so users who refresh after the
    // upgrade don't get stuck on a pane that no longer exists.
    if (initialCached?.pane === 'summary') return 'review';
    if (initialCached?.pane && initialCached.pane !== 'upload') return initialCached.pane;
    return 'processing';
  });
  const [hydrating, setHydrating] = useState(() => Boolean(initialUrlCampaign));

  // summary breakdown view
  const [breakdownView, setBreakdownView] = useState('package'); // 'package' | 'tactic' | 'format' | 'month'

  // file state — each entry: { file, gcsPath, originalName, size, type, kind }
  const [planFiles, setPlanFiles] = useState(() => Array.isArray(initialCached?.planFiles) ? initialCached.planFiles : []);
  // Original GT IO file ({ gcsPath, originalName }) for the review pane and
  // the Sidekick agent payload. Populated from the cts-snapshot on resume
  // (planFiles is empty there) and mirrored into sessionStorage, because the
  // same-tab fast path below skips the snapshot fetch entirely — without the
  // cached copy a second visit has neither planFiles nor a snapshot, and the
  // agent payload comes out null (no autoStart, no session, dead sidebar).
  const [ctsSourceFile, setCtsSourceFile] = useState(
    () => initialCached?.ctsSourceFile || null,
  );
  const [creativeFiles, setCreativeFiles] = useState(() => Array.isArray(initialCached?.creativeFiles) ? initialCached.creativeFiles : []);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // analyze-files result (drives fast-track vs fast-track-shell)
  const [fileAnalysis, setFileAnalysis] = useState(null);

  // campaign details (required for fast-track creation)
  const [campaignName, setCampaignName] = useState(() => initialCached?.campaignName || '');
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [folderId, setFolderId] = useState(() => initialCached?.folderId || '');
  // Inline folder-creation UI state. When the user hits "+ New" the dropdown
  // is swapped for a small name input + Create button.
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolderBusy, setCreatingFolderBusy] = useState(false);
  const [createFolderError, setCreateFolderError] = useState(null);
  const [destination, setDestination] = useState(() => initialCached?.destination || '');
  const [advertiserId, setAdvertiserId] = useState(() => initialCached?.advertiserId || '');
  // Facebook's SECOND id. Every other platform authenticates a campaign with
  // one id, which rides `advertiserId` above; Facebook needs the ad account
  // (where the campaign is created) AND — on an OAuth channel — the Page the
  // ads run as. campaigns.js stores them in different columns (advertiser_id vs
  // secondary_platform_id), so they cannot share one input.
  const [pageId, setPageId] = useState(() => initialCached?.pageId || '');
  const [selectedChannelId, setSelectedChannelId] = useState(() => initialCached?.selectedChannelId || '');
  // MMGY IO Type — 'mmgy' (default), 'traveldesk' or 'plan-only'. The
  // selector is hidden unless the account has `enable-mmgy-io-type` enabled
  // for the selected destination; hidden = always 'mmgy'.
  const [ioType, setIoType] = useState(() => initialCached?.ioType || 'mmgy');
  // 'plan-only' (Excel plan is the IO, no PDF) runs only on the MMGY DV360/TTD
  // pipeline. Declared here, ahead of the analyze-files effect that keys on it.
  const showIoType = isMmgy && Boolean(destination) && isEnabledForAnyOfPlatforms(
    accountSettings?.['enable-mmgy-io-type'],
    [destination],
  );
  const mmgyPlanOnlyAvailable = isMmgyPlanOnlyAvailable([destination]);
  const mmgyIoType = isMmgy ? (showIoType ? effectiveMmgyIoType(ioType, [destination]) : 'mmgy') : undefined;
  const mmgyPlanOnly = mmgyIoType === MMGY_PLAN_ONLY_IO_TYPE;
  // GroundTruth IO PDF "Additional Instructions" — optional free text a system
  // user adds on top of the IO (demographics, audiences, default package
  // names...). The gt-api worker applies it to the generated campaign config
  // and every change is reviewed before approval. Two pieces of state:
  //   additionalInstructions  the field as currently typed
  //   runInstructions         the snapshot the current draft's run was
  //                           dispatched with — what refresh/resume restore and
  //                           what a retry re-sends, whatever the field now says
  const [additionalInstructions, setAdditionalInstructions] = useState(
    () => initialCached?.additionalInstructions || '',
  );
  const [runInstructions, setRunInstructions] = useState(() => initialCached?.runInstructions ?? null);
  const [runRules, setRunRules] = useState(() => initialCached?.runRules ?? null);

  // optional context
  const [targeting, setTargeting] = useState('');
  const [goals, setGoals] = useState('');

  // submit / processing
  const [submitting, setSubmitting] = useState(false);
  const [campaignId, setCampaignId] = useState(() => initialUrlCampaign || null);
  const [orchStatus, setOrchStatus] = useState(null);
  const [procStepIdx, setProcStepIdx] = useState(0);
  const [procPercent, setProcPercent] = useState(10);
  const [procStepLabel, setProcStepLabel] = useState(null);
  const [procError, setProcError] = useState(null);
  const [campaignNameConflict, setCampaignNameConflict] = useState(false);
  // An IO->CTS run that is dispatched but not yet finished. Set either by
  // runGtPdfReview (fresh dispatch) or by hydration when it finds a pending
  // snapshot on the campaign — a single effect polls it in both cases, which
  // is what makes the run survive a reload.
  // Shape: { requestId, startedAt, sourceFile }
  const [pendingIoRun, setPendingIoRun] = useState(null);

  // parse summary
  const [launcherResult, setLauncherResult] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [importWarnings, setImportWarnings] = useState([]);
  // Imported campaigns skip the processing poll entirely — planning-review's
  // import fallback owns their hydration, and a second poll would double-fire it.
  const [isImportedCampaign, setIsImportedCampaign] = useState(false);

  // GroundTruth review-cts result (replaces launcherResult on the GT branch).
  // Shape matches gt_api_handler.review_cts_and_save() with an added `result`
  // key carrying the full GTExcelParser.convert() output for rich rendering.
  const [ctsReview, setCtsReview] = useState(() => initialCached?.ctsReview || null);
  // True when the findings on `ctsReview` were computed against an EARLIER
  // config than the one now on `ctsReview.result` — a save that did not
  // revalidate, so the plan moved on and the findings did not. The panel must
  // say so rather than presenting them as current: findings quoting a date the
  // plan no longer holds read as a blocker that cannot be cleared. Cached to
  // sessionStorage with the envelope, because the mixed envelope survives a
  // reload and the label has to survive with it.
  const [ctsFindingsStale, setCtsFindingsStale] = useState(() => Boolean(initialCached?.ctsFindingsStale));
  // GCS path of the post-edit campaign_config JSON produced by
  // commit_config_edits. Defaults to null (no agent edits applied);
  // populated via onReviewCtsUpdated callback from ReviewPane.
  // launchGroundTruthCampaign sends this JSON to gt-api via the
  // `create-campaign-from-json` sub_action. When null (no edits
  // committed), the original Excel path is used — no regression
  // to the legacy launch flow.
  //
  // Cached to sessionStorage with the envelope: a same-tab reload restores
  // the edited plan from there, and without this Approve & launch then sent
  // the original IO — the plan on screen was not the plan launched.
  const [appliedConfigGcsPath, setAppliedConfigGcsPath] = useState(
    () => initialCached?.appliedConfigGcsPath || null,
  );
  // When the server recorded the GT config head this tab last saw (the
  // `headAt` every GT save returns, or the snapshot's on resume). Two uses:
  // a same-tab resume adopts the server's head when it has moved on (a save
  // from another tab or device), and a reviewer-agent commit the SSE stream
  // replays after a reload is skipped when it predates the restored head.
  const [configHeadAt, setConfigHeadAt] = useState(() => initialCached?.configHeadAt || null);
  const configHeadAtRef = useRef(configHeadAt);
  configHeadAtRef.current = configHeadAt;

  // review grid
  const [reviewRows, setReviewRows] = useState([]);
  // Shared edit-state reducer (editedRows / pending list ops / editing cell).
  // Pending deletions ride the generalized pendingListOps model and are saved
  // via the same saveReviewEdits → PUT planning-review path; the launcher's
  // /planning-launch endpoint uses the resulting edited_parsed_response.json.
  const {
    editedRows, setEditedRows, pendingDeletions, pendingListOps, setPendingListOps,
    pendingRowOps, setPendingRowOps, pendingRowOpCount,
    editingCell, setEditingCell, editingDraft, setEditingDraft,
    getRowValue, commitCellEdit, cancelCellEdit, toggleListDeletion,
    addListItem, editListItem,
    resetEdits,
  } = useReviewGridEdits();
  // Read by async resume work that must not overwrite edits the user started
  // while it was in flight.
  const hasPendingEditsRef = useRef(false);
  hasPendingEditsRef.current = Object.keys(editedRows).length > 0
    || Object.keys(pendingListOps).length > 0
    || pendingRowOps.length > 0;
  const [activeGroup, setActiveGroup] = useState('overview');
  const [gridSearch, setGridSearch] = useState('');
  const [filterIssuesOnly, setFilterIssuesOnly] = useState(false);
  // The reviewer's Show/Hide choice for the findings bar; null follows the
  // default for this review (issuesBarOpen, below). Reset whenever a freshly
  // processed review lands; not kept across a reload.
  const [issuesBarChoice, setIssuesBarChoice] = useState(null);
  // The consolidated summary (hero + 6 stat tiles + campaign-configuration
  // card) collapses on demand — same Show/Hide toggle pattern as the CTS
  // issues bar. Default open so first-time users see the parsed details;
  // reviewers who already know the numbers can collapse it to give the
  // grid more vertical room.
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [detailRowId, setDetailRowId] = useState(null);
  // Which column the user clicked. Drives the cell-focused detail panel
  // (vs the older row-dump view that lived under the same panel).
  const [detailCellField, setDetailCellField] = useState(null);
  // Bulk audience refresh state (TTD / DV360). On CLW load we re-resolve EVERY
  // audience row against the live platform API in one server-side job, overlay
  // the result onto edited_parsed_response.json, and persist it permanently.
  // It runs once per campaign — gated by a permanent server sentinel surfaced
  // as `audiencesBulkRefreshed` — and the manual "Refresh audiences" button
  // forces a fresh run. Non-blocking: the grid stays interactive throughout.
  // Shape: { status: 'idle'|'running'|'done'|'error', matched?, total?, error? }.
  const [bulkAudienceRefresh, setBulkAudienceRefresh] = useState({ status: 'idle' });
  // True once the one-time bulk refresh has already run for this campaign
  // (read from planning-review GET). Suppresses the auto-run on later loads.
  const [audiencesBulkRefreshed, setAudiencesBulkRefreshed] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  // Active QA pivot on the GT summary pane. 'location' = location-filter
  // ZIP assignment; 'dcm' = DCM placement count per ad group.
  const [qaPivotKey, setQaPivotKey] = useState('location');
  // Cross-pane jump target. When set, ReviewPane consumes it on mount /
  // pane activation and runs its internal jumpToCell. Used by summary-
  // page issue bullets so a click there navigates straight to the
  // matching cell in the placement grid.
  const [pendingJump, setPendingJump] = useState(null);
  const [savingReview, setSavingReview] = useState(false);
  // Non-blocking structural warnings the save endpoint returned for the plan
  // we just persisted (dangling refs are rejected outright and surface as a
  // save error instead). Cleared on the next save attempt.
  const [saveWarnings, setSaveWarnings] = useState([]);
  // Structured save/launch blockers, kept alongside the single-string
  // launchError so the notices strip can render one row per problem.
  const [launchBlockers, setLaunchBlockers] = useState([]);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState(null);
  const [launchSuccess, setLaunchSuccess] = useState(false);
  const [launchProgress, setLaunchProgress] = useState(null);
  // True when the backend reports the campaign is no longer in 'review'
  // status — either it was already launched or the orchestration moved on.
  // The Launch button is disabled, and edits route through batched
  // edit-campaign requests (live platform) rather than the plan-template PUT.
  // Set by fetchPlanningReview from the server response.
  const [alreadyLaunched, setAlreadyLaunched] = useState(false);
  // Live platform campaign id, captured from planning-review so launched
  // campaigns can dispatch edit-campaign requests.
  const [platformCampaignId, setPlatformCampaignId] = useState(null);
  // Delivered performance for a launched campaign — the same cached figures the
  // Campaign Grid row shows (spend / impressions / clicks / conversions / KPI).
  // Everything else on the review pane comes from the PLAN, which for a launched
  // or imported campaign has no delivery in it at all.
  const [deliveredMetrics, setDeliveredMetrics] = useState(null);
  // Audience names the platform reports for this campaign, aggregated from the
  // grid's per-ad-group rows. The PLAN carries none for an imported Google Ads
  // campaign — nothing in that pipeline fetched audiences — so the tile read
  // "0 audiences targeted" on campaigns that do target audiences.
  const [platformAudiences, setPlatformAudiences] = useState(null);

  // file input refs
  const planInputRef = useRef(null);
  const creativeInputRef = useRef(null);

  // Lets the IO→CTS poll loop bail on unmount instead of hammering the
  // backend for the rest of its 12-minute window. Safe to stop early because
  // the run is recorded against the campaign and resumes on the next mount.
  // Reset in setup: StrictMode dev remounts run cleanup→setup on the SAME
  // mounted instance, and a latched-true sentinel made the poll bail on its
  // first iteration while the component was actually alive.
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => { unmountedRef.current = true; };
  }, []);

  // True from the moment a fresh GT IO→CTS PDF dispatch starts until
  // pendingIoRun owns the run (or the dispatch fails). The v3 progress poll
  // must stay off for that window too: createDraftCampaign sets campaignId
  // before the dispatch POST returns, and the effect would otherwise fire one
  // "Parsing media plan" tick before pendingIoRun exists.
  const ioCtsDispatchRef = useRef(false);

  // ─── HYDRATION: restore from URL ?campaign=X on mount ────
  // State for campaignId / pane / campaignName / etc. is already lazy-
  // initialized from the URL + sessionStorage above, so we never render
  // the upload pane during hydration. Here we just ask the backend where
  // the campaign actually is and switch to summary if it's review-ready.
  // If the campaign is unknown to the backend we silently drop the URL
  // param so the user can start fresh.
  // Install a GT draft's saved head — the envelope GET /cts-snapshot and
  // planning-review serve, already resolved to the latest saved revision.
  // appliedConfigGcsPath is what launch reads; falling back to the IO
  // handler's parsed config keeps an unedited draft launching from JSON.
  const applyGtHead = useCallback((head) => {
    setCtsReview(head.ctsReview);
    setReviewRows(buildGtReviewRows(head.ctsReview));
    setCtsFindingsStale(Boolean(head.ctsFindingsStale));
    const launchKey = toGcsKey(head.appliedConfigGcsPath) || toGcsKey(head.parsedConfigGcsPath);
    if (launchKey) setAppliedConfigGcsPath(launchKey);
    setConfigHeadAt(head.headAt || null);
  }, []);

  const hydratedRef = useRef(false);
  // Settings may resolve after the first progress request. Rehydrate once
  // when opt-in arrives so resumed terminal launches also get the outcome UI.
  useEffect(() => {
    hydratedRef.current = false;
  }, [planningGridForAllRoles]);
  useEffect(() => {
    if (hydratedRef.current) return;
    if (!initialUrlCampaign) { hydratedRef.current = true; setHydrating(false); return; }
    if (!accountId) return; // wait for account context
    hydratedRef.current = true;
    (async () => {
      const progressRequest = api.get(getApiUrl(`/api/v3/campaigns/${initialUrlCampaign}/progress`), {
        params: { accountId, userId },
      });
      progressRequest.catch(() => {}); // awaited (and its failure handled) below
      // A GroundTruth IO that launched successfully (or the retired draft a
      // launch replaced) opens in its launched campaign workspace, not this
      // review flow. Only ever a navigation: nothing is launched or retried.
      const launchedTarget = await findGtLaunchedWorkspace({ campaignId: initialUrlCampaign, accountId, userId });
      if (unmountedRef.current) return;
      if (launchedTarget) {
        navigate(`/campaigns/${encodeURIComponent(launchedTarget)}`, { replace: true });
        return;
      }
      try {
        const res = await progressRequest;
        const p = res.data || {};
        if (planningGridForAllRoles && !p.isImported && p.phase === 'launch') {
          const status = p.launchStatus || p.status;
          if (status === 'completed') {
            navigate(`/campaigns/${encodeURIComponent(initialUrlCampaign)}`, { replace: true });
            return;
          }
          setLaunchProgress(status);
          setOrchStatus(status);
          setProcError(status === 'failed' ? (p.launchError || p.error || 'Campaign launch failed') : null);
          setPane('processing');
          if (status === 'completed' || status === 'failed') return;
        }
        // Hydrate the Start-Campaign platform account id on resume — without
        // it, a deep-linked / other-device open shows '—' for Platform
        // account on the campaign-configuration card (UAT 0818). Never
        // clobber a value the user is mid-typing in this session.
        if (p.advertiserId) {
          setAdvertiserId((prev) => prev || String(p.advertiserId));
        }
        // Same for the campaign row's name — the GT launch POST sends it, and
        // a resume that never passed the upload pane held '' here, so
        // Approve & launch 400'd 'Campaign name is required' while the page
        // looked fully named (header renders from the CTS review). UAT 0819.
        if (p.campaignName) {
          setCampaignName((prev) => prev || String(p.campaignName));
        }
        // Imported campaigns have no fast-track orchestration, but the
        // planning-review endpoint now derives a launcherResult from the
        // import-campaign message — same shape as a launched campaign, so we
        // hydrate through the identical review-pane path (read-only summary +
        // post-launch grid edits) instead of bouncing back to the legacy page.
        if (p.isImported) {
          setIsImportedCampaign(true);
          fetchPlanningReview(initialUrlCampaign, { imported: true });
          return;
        }
        setOrchStatus(p.status || null);
        if (typeof p.stepIndex === 'number') setProcStepIdx(p.stepIndex);
        if (typeof p.progressPercent === 'number') setProcPercent(p.progressPercent);
        // Absent stepLabel clears the override so the static step list label
        // wins (used by the gt-api flow — see /api/v3/campaigns/:id/progress).
        setProcStepLabel(p.stepLabel || null);
        if (p.status === 'review' || p.planningReviewReady) {
          await fetchPlanningReview(initialUrlCampaign);
        } else if (p.status === 'completed') {
          // Campaign has already been launched — fetch the launcher result
          // snapshot for read-only viewing. fetchPlanningReview will set
          // alreadyLaunched based on the response so the Launch button
          // stays disabled.
          await fetchPlanningReview(initialUrlCampaign);
        } else if (p.status === 'failed') {
          setProcError(p.error || 'Orchestration failed');
          setPane('processing');
        } else if (initialCached?.pane === 'review' && initialCached?.ctsReview) {
          // GT CTS-review draft, same-browser fast path: /progress says
          // 'processing' (no orchestration_message_id yet) but
          // sessionStorage already has the review envelope, so we skip
          // the GCS round-trip.
          setCtsReview(initialCached.ctsReview);
          if (Array.isArray(initialCached.reviewRows)) {
            setReviewRows(initialCached.reviewRows);
          } else {
            setReviewRows(buildGtReviewRows(initialCached.ctsReview));
          }
          // The config the review (and any saved edits) produced. Without it a
          // same-tab reload launched from the raw source file instead — for a
          // PDF that re-converts without the reviewed instructions or edits.
          {
            const raw = initialCached.appliedConfigGcsPath
              || initialCached.ctsReview.parsed_config_gcs_path || null;
            if (raw) setAppliedConfigGcsPath(toGcsKey(raw));
          }
          setPane('review');
          // The cached envelope renders at once, but the server's snapshot is
          // the source of truth: a save from another tab or device moves it
          // on, and entries cached before appliedConfigGcsPath was don't know
          // which revision to launch. Check it behind the rendered pane. The
          // same fetch backfills a GT source the cache lacks, which otherwise
          // silently nulls the Sidekick create-payload.
          const needsSource = !initialCached.ctsSourceFile?.gcsPath
            && !initialCached.planFiles?.some((f) => f?.gcsPath);
          api.get(
            getApiUrl(`/api/v2/campaigns/${initialUrlCampaign}/cts-snapshot`),
            { params: { accountId, userId } },
          ).then((snap) => {
            const data = snap.data || {};
            if (needsSource && data.sourceFile?.gcsPath) setCtsSourceFile(data.sourceFile);
            const serverMovedOn = (data.headAt || null) !== (initialCached.configHeadAt || null)
              || (toGcsKey(data.appliedConfigGcsPath) || null) !== (initialCached.appliedConfigGcsPath || null);
            // Never over edits made (or saved) since the pane rendered.
            if (data.ctsReview && typeof data.ctsReview === 'object'
                && data.status !== 'pending' && data.status !== 'failed'
                && data.appliedConfigGcsPath && serverMovedOn
                && !hasPendingEditsRef.current
                && configHeadAtRef.current === (initialCached.configHeadAt || null)) {
              applyGtHead(data);
            }
          }).catch(() => { /* 404 = pre-snapshot draft; agent stays unavailable */ });
        } else {
          // GT CTS-review draft, cross-device path: nothing in
          // sessionStorage. Try to fetch the snapshot we persisted
          // under campaigns/{id}/cts-review.json. If it's there, the
          // user is resuming a draft from another browser/device —
          // restore the review pane. Otherwise this is a non-draft or
          // a pre-snapshot draft; fall back to the processing pane.
          let restoredFromSnapshot = false;
          try {
            const snap = await api.get(
              getApiUrl(`/api/v2/campaigns/${initialUrlCampaign}/cts-snapshot`),
              { params: { accountId, userId } },
            );
            // An IO→CTS run that was still in flight when the page went
            // away. The gt-api worker keeps going regardless, so pick the
            // poll back up rather than stranding the result in GCS.
            if (snap.data?.status === 'pending' && snap.data?.requestId) {
              // A CTS snapshot only exists for GroundTruth campaigns, so a
              // true cross-device resume (no sessionStorage) can restore the
              // destination too — otherwise the auto-select effect may pick
              // another channel and render generic progress labels.
              if (!destination) setDestination('groundtruth');
              setSubmitting(true);
              setOrchStatus('starting');
              // Step index and percent are painted by the progress ticker
              // from elapsed-since-dispatch; leave the label to the step list.
              setProcStepLabel(null);
              if (snap.data?.sourceFile?.gcsPath) setCtsSourceFile(snap.data.sourceFile);
              // The in-flight run's instruction snapshot, restored with it.
              if (snap.data?.additionalInstructions) {
                setRunInstructions(snap.data.additionalInstructions);
                setAdditionalInstructions(snap.data.additionalInstructions);
              }
              setRunRules(snap.data?.standardRules ?? null);
              setPendingIoRun({
                requestId: snap.data.requestId,
                startedAt: snap.data.startedAt || null,
                sourceFile: snap.data.sourceFile || null,
              });
              setPane('processing');
              return;
            }
            const persistedReview = snap.data?.ctsReview;
            if (persistedReview && typeof persistedReview === 'object') {
              // Same cross-device restoration as the pending branch above.
              if (!destination) setDestination('groundtruth');
              const restoredInstructions = persistedReview.instruction_snapshot?.additional_instructions
                || snap.data?.additionalInstructions || null;
              setRunRules(persistedReview.instruction_snapshot?.standard_rules ?? snap.data?.standardRules ?? null);
              if (restoredInstructions) {
                setRunInstructions(restoredInstructions);
                setAdditionalInstructions(restoredInstructions);
              }
              // The snapshot as last SAVED (the server resolves it to the
              // latest revision), including the revision launch must read.
              applyGtHead(snap.data);
              // Original IO file (PDF/Excel/ZIP) for the review pane's
              // download button — planFiles is empty on this path.
              if (snap.data?.sourceFile?.gcsPath) {
                setCtsSourceFile(snap.data.sourceFile);
              }
              setPane('review');
              restoredFromSnapshot = true;
            }
          } catch (snapErr) {
            // 404 = no snapshot for this campaign (normal for non-draft
            // campaigns). Anything else we silently ignore — the fall
            // back to 'processing' is the safer default.
          }
          if (!restoredFromSnapshot) setPane('processing');
        }
      } catch (e) {
        console.warn('hydrate from URL failed', e?.response?.data?.error || e?.message);
        // Bad campaign id — drop the URL param and let the user start fresh.
        setSearchParams({}, { replace: true });
        setCampaignId(null);
        setPane('upload');
      } finally {
        setHydrating(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, initialUrlCampaign, planningGridForAllRoles]);

  // ─── Persist key UI state to sessionStorage whenever it changes ─
  useEffect(() => {
    if (!campaignId) return;
    saveSessionState({
      campaignId,
      campaignName,
      destination,
      advertiserId,
      pageId,
      selectedChannelId,
      folderId,
      planFiles,
      creativeFiles,
      pane,
      ctsReview,
      // Cached alongside the envelope it describes. The persist effect writes
      // ctsReview itself, and the mixed `{ ...ctsReview, result: editedConfig }`
      // form is exactly what lands there — so without this a reload restores
      // findings that predate the plan and presents them as current, which is
      // the thing the flag exists to prevent.
      ctsFindingsStale,
      // Cache the resolved GT source file alongside the review. planFiles is
      // empty on any resume path, so this is the only thing that keeps the
      // Sidekick payload alive across a second visit in the same tab.
      ctsSourceFile,
      // GT IO PDF instructions: the field, and the snapshot the run used.
      additionalInstructions,
      runInstructions,
      runRules,
      // The revision launch reads, and the server head it came from — kept
      // with the envelope so a same-tab reload launches what it shows.
      appliedConfigGcsPath,
      configHeadAt,
      // MMGY IO type — a reload mid-processing keeps the plan-only wording
      // and analyze-files answer.
      ioType,
    });
  }, [campaignId, campaignName, destination, advertiserId, pageId, selectedChannelId, folderId, planFiles, creativeFiles, pane, ctsReview, ctsFindingsStale, ctsSourceFile, additionalInstructions, runInstructions, runRules, appliedConfigGcsPath, configHeadAt, ioType]);

  // ─── Load folders ────────────────────────────────────────
  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    (async () => {
      setFoldersLoading(true);
      try {
        const res = await api.get(getApiUrl('/api/v2/folders'), {
          params: { accountId, userId, page: 1, limit: 100, sortBy: 'name', sortOrder: 'asc' },
        });
        if (cancelled) return;
        const list = res.data?.folders || res.data || [];
        setFolders(list);
        if (list.length > 0 && !folderId) setFolderId(list[0].id);
      } catch (e) {
        if (!cancelled) console.error('Failed to load folders', e);
      } finally {
        if (!cancelled) setFoldersLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, userId]);

  // ─── Inline folder creation ──────────────────────────────
  const handleCreateFolder = useCallback(async () => {
    const name = newFolderName.trim();
    if (!name) { setCreateFolderError('Enter a folder name'); return; }
    setCreatingFolderBusy(true);
    setCreateFolderError(null);
    try {
      const res = await api.post(getApiUrl('/api/v2/folders'), { name }, {
        params: { accountId, userId },
      });
      const folder = res.data;
      if (!folder?.id) throw new Error('Folder creation returned no id');
      // Insert and select the new folder immediately.
      setFolders((prev) => [folder, ...prev]);
      setFolderId(folder.id);
      setNewFolderName('');
      setCreatingFolder(false);
    } catch (e) {
      console.error('Folder creation failed', e);
      setCreateFolderError(e?.response?.data?.error || e.message || 'Folder creation failed');
    } finally {
      setCreatingFolderBusy(false);
    }
  }, [newFolderName, accountId, userId]);

  // ─── File upload helpers ─────────────────────────────────
  const uploadOne = useCallback(async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('accountId', accountId || '');
    fd.append('userId', userId || '');
    const res = await api.post(getApiUrl('/api/upload'), fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const data = res.data?.data || {};
    return {
      file,
      originalName: file.name,
      gcsPath: data.gcsPath,
      publicUrl: data.publicUrl,
      size: file.size,
      type: file.type,
    };
  }, [accountId, userId]);

  // POST /analyze-files for the uploaded plan files under a given platform.
  // Advisory only — it picks fast-track vs fast-track-shell and reports the
  // account's per-platform `requiresTemplate` / `requiresIo` flags, so a
  // failure leaves fileAnalysis null and the form falls back to its defaults
  // rather than blocking the user.
  //
  // Owned by an effect rather than by the upload handler because the answer is
  // PER PLATFORM: the same PDF needs a launch template on one destination and
  // not on another, and the destination is usually still unset when the files
  // land (it is auto-selected once the channel list loads, and the user can
  // change it afterwards). Keying the effect on (destination, files) re-asks
  // exactly when the answer could differ, and never more often.
  //
  // `campaignName` is deliberately NOT a trigger — it only rides along as
  // context for the server's reasoning string, and re-POSTing on every
  // keystroke would be a request per character.
  const campaignNameRef = useRef(campaignName);
  campaignNameRef.current = campaignName;
  // Guards against an out-of-order response: switching destination twice
  // quickly leaves two requests in flight, and without this the slower (older)
  // one could overwrite the newer answer.
  const analysisRunRef = useRef(0);
  const analyzedForRef = useRef(null);

  useEffect(() => {
    if (!planFiles.length) { setFileAnalysis(null); analyzedForRef.current = null; return; }
    // GroundTruth skips analyze-files entirely — that endpoint chooses between
    // fast-track and fast-track-shell, and GT uses neither.
    if (destination === 'groundtruth') { setFileAnalysis(null); analyzedForRef.current = null; return; }
    // Without a destination there is no per-platform answer to ask for. The
    // form falls back to "no template required", which is also what the server
    // reports for an account with the flag off — and the auto-select effect
    // below fills the destination in a moment anyway.
    if (!destination) return;

    const key = `${destination}|${mmgyIoType || ''}|${planFiles.map((f) => f.gcsPath).join(',')}`;
    if (analyzedForRef.current === key) return;
    analyzedForRef.current = key;

    const run = ++analysisRunRef.current;
    (async () => {
      try {
        const res = await api.post(getApiUrl('/api/v2/orchestration/analyze-files'), {
          files: planFiles.map((m) => ({ gcsPath: m.gcsPath, originalName: m.originalName, size: m.size, type: m.type })),
          platform: destination,
          context: { campaignName: campaignNameRef.current || baseName(planFiles[0]?.originalName || '') },
          // MMGY plan-only: the Excel plan is the IO, so no PDF is required
          ...(mmgyIoType ? { ioType: mmgyIoType } : {}),
          accountId,
          userId,
        });
        if (analysisRunRef.current !== run) return;
        setFileAnalysis(res.data?.analysis || null);
      } catch (e) {
        if (analysisRunRef.current !== run) return;
        // Advisory only — proceed with the default recommendedType, and let the
        // server re-check requiresTemplate at dispatch.
        console.warn('analyze-files failed (non-fatal):', e?.response?.data || e.message);
        setFileAnalysis(null);
      }
    })();
  }, [destination, planFiles, accountId, userId, mmgyIoType]);

  // Plan-only is offered only for DV360/TTD — drop back to the default IO type
  // when the destination moves to one that cannot run it.
  useEffect(() => {
    if (ioType === MMGY_PLAN_ONLY_IO_TYPE && destination && !mmgyPlanOnlyAvailable) setIoType('mmgy');
  }, [ioType, destination, mmgyPlanOnlyAvailable]);

  const handlePlanFiles = useCallback(async (rawFiles) => {
    if (!rawFiles?.length) return;
    setUploadError(null);
    setUploading(true);
    try {
      // Auto-derive campaign name from first plan file if user hasn't typed one
      const planFile = rawFiles.find((f) => isPlanFile(f.name, { isMmgy, mmgyPlanOnly }));
      if (planFile && !campaignName.trim()) setCampaignName(baseName(planFile.name));
      // Auto-detect destination from launch template filename
      for (const f of rawFiles) {
        const p = detectPlatformFromFilename(f.name);
        if (p && !destination) setDestination(p);
      }

      const uploaded = await Promise.all(rawFiles.map(uploadOne));
      const merged = [...planFiles];
      for (const u of uploaded) {
        const idx = merged.findIndex((m) => m.originalName === u.originalName);
        if (idx >= 0) merged[idx] = u; else merged.push(u);
      }
      // analyze-files is NOT called here — the effect above owns it, keyed on
      // (destination, files), so it fires once these files land and again if
      // the destination changes.
      setPlanFiles(merged);
    } catch (e) {
      console.error('Plan upload failed', e);
      setUploadError(e?.response?.data?.error || e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [planFiles, campaignName, destination, uploadOne, isMmgy, mmgyPlanOnly]);

  const handleCreativeFiles = useCallback(async (rawFiles) => {
    if (!rawFiles?.length) return;
    setUploadError(null);
    setUploading(true);
    try {
      const uploaded = await Promise.all(rawFiles.map(uploadOne));
      setCreativeFiles((prev) => {
        const merged = [...prev];
        for (const u of uploaded) {
          const idx = merged.findIndex((m) => m.originalName === u.originalName);
          if (idx >= 0) merged[idx] = u; else merged.push(u);
        }
        return merged;
      });
    } catch (e) {
      console.error('Creative upload failed', e);
      setUploadError(e?.response?.data?.error || e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [uploadOne]);

  // ─── derived: ready to submit? ──────────────────────────
  const isGroundTruth = destination === 'groundtruth';
  // OpenAI authenticates via api_key in channel_config and the launcher
  // does not consume a separate launch-template JSON — the media plan
  // alone is enough to submit, so gate it like GroundTruth.
  const isOpenAI = destination === 'openai';
  // Facebook is the one destination that takes TWO ids (ad account + Page).
  const isFacebook = destination === 'facebook';
  // MMGY IO Type selector visibility — only when the user is in an MMGY
  // account AND the `enable-mmgy-io-type` setting lists the selected
  // destination. The setting is a comma-separated platform list
  // (e.g. 'dv360,ttd'); empty/null hides the selector. Mirrors the
  // legacy CampaignCreation.js:1865 gate so admins flip one switch.
  // GT: a single Excel OR an insertion-order PDF is the entire input. No
  // launch template, no creatives. PDF flow short-circuits the CTS review
  // step (which is Excel-only) and submits straight to gt-api, which runs
  // launch_campaign_from_pdf.
  const gtExcelFile = useMemo(
    () => planFiles.find((f) => isGtExcel(f.originalName)) || null,
    [planFiles]
  );
  const gtPdfFile = useMemo(
    () => planFiles.find((f) => isGtPdf(f.originalName)) || null,
    [planFiles]
  );
  const gtZipFile = useMemo(
    () => planFiles.find((f) => isGtZip(f.originalName)) || null,
    [planFiles]
  );
  // Additional Instructions apply to both GroundTruth review paths — the IO PDF
  // and the CTS Excel — so the field shows for GroundTruth from the start, before
  // a file is picked. A ZIP bundle is the exception: startProcessing launches it
  // directly, with no review for the instructions to be checked in, so a ZIP
  // swaps the field for a note saying so.
  const showGtInstructions = isGroundTruth && !gtZipFile;
  const gtInstructionsUnavailable = isGroundTruth && Boolean(gtZipFile);
  const gtPlanFile = gtExcelFile || gtPdfFile || gtZipFile;
  // The processing pane's step list and subtext choose their labels from
  // gtPdfFile / gtZipFile, which derive from planFiles. A resumed IO→CTS run
  // has no planFiles — the source arrives via ctsSourceFile — so resolve the
  // same PDF/ZIP-ness from ctsSourceFile too. Without this, a resumed PDF run
  // renders the Excel step list ("Uploading Excel to GCS", "Loading & parsing
  // Excel") while the IO→CTS ticker drives PDF-shaped progress.
  const gtSourceFile = gtPlanFile || ctsSourceFile;
  const gtSourceName = gtSourceFile?.originalName || gtSourceFile?.gcsPath || '';
  const gtHasPdfFile = Boolean(gtPdfFile) || isGtPdf(gtSourceName);
  const gtHasZipFile = Boolean(gtZipFile) || isGtZip(gtSourceName);
  // The CTS findings bar is open by default — except on a GroundTruth IO (PDF)
  // review awaiting approval, where the findings start hidden behind their
  // count and open on demand. Keyed on the review itself, not `destination`,
  // which a resume can leave on another platform. A launched campaign's bar
  // (its launch warnings) stays open. The reviewer's toggle overrides this
  // while the review is on screen.
  const issuesBarDefaultOpen = !(ctsReview && gtHasPdfFile && !alreadyLaunched);
  const issuesBarOpen = issuesBarChoice ?? issuesBarDefaultOpen;
  const setIssuesBarOpen = useCallback((next) => setIssuesBarChoice((prev) => {
    const current = prev ?? issuesBarDefaultOpen;
    return typeof next === 'function' ? next(current) : Boolean(next);
  }), [issuesBarDefaultOpen]);
  // Name of the uploaded document, for the review grid's export filename.
  // Resolved the same way handleLaunch resolves its source: the gt*File memos
  // derive from planFiles, which exists only in the uploading tab, so a
  // cts-snapshot resume (other device, expired sessionStorage) has to fall
  // through to ctsSourceFile. Reading only the planFiles-derived state is why
  // 9 of 17 sample exports landed as `campaign_review_<ts>.xlsx`.
  // The snapshot stores `originalName: sourceFile.originalName || null`, so a
  // resumed review can arrive with only a gcsPath; exportFileBase() takes the
  // key's last segment in that case.
  const exportSourceName = useMemo(() => {
    const f = gtExcelFile || gtPdfFile || gtZipFile || ctsSourceFile;
    return f?.originalName || f?.gcsPath || '';
  }, [gtExcelFile, gtPdfFile, gtZipFile, ctsSourceFile]);
  // Promote the in-session upload into ctsSourceFile so one piece of state
  // holds the resolved GT source across every entry path. planFiles lives
  // only in this tab's memory + sessionStorage; ctsSourceFile is what the
  // review pane's download button and the Sidekick create-payload read on a
  // later visit, when planFiles is empty.
  useEffect(() => {
    if (gtPlanFile?.gcsPath) setCtsSourceFile(gtPlanFile);
  }, [gtPlanFile]);
  const planDone = isGroundTruth
    ? Boolean(gtPlanFile)
    : planFiles.some((f) => isPlanFile(f.originalName, { isMmgy, mmgyPlanOnly }));
  // Is a launch template actually required for this (account, platform)?
  //
  // THE SERVER OWNS THIS ANSWER. `launch-template-required` is a per-account,
  // per-platform setting; POST /analyze-files resolves it and reports back as
  // `requiresTemplate` (see routes/v2/orchestration.js). Accounts without the
  // flag never need a template — the launcher auto-detects one from the
  // platform + account (`willUseAutoDetection` in the same response), which is
  // exactly what the classic create form has always relied on
  // (CampaignCreation.js gates on `fileAnalysis.requiresTemplate` and nothing
  // else).
  //
  // CLW used to demand a template file from EVERY non-GT, non-OpenAI upload,
  // regardless of the setting. A Facebook plan — one IO PDF, account flag off,
  // server replying `requiresTemplate: false` — could therefore never be
  // submitted: the button stayed disabled behind "launch template still
  // needed" with nothing the user could upload to satisfy it.
  //
  // Until analyze-files answers (no files yet, request in flight, or it failed
  // — it is advisory and non-fatal), fall back to "not required", matching the
  // classic form: `fileAnalysis?.requiresTemplate` is undefined there too, and
  // the server re-checks at dispatch, so a genuinely-required template still
  // fails loudly instead of launching a broken campaign.
  const templateRequired = !isGroundTruth && !isOpenAI && !isMmgy
    && Boolean(fileAnalysis?.requiresTemplate);
  const launchTemplateDone = isMmgy
    // MMGY is not covered by the flag: its pipeline consumes the Excel proposal
    // AS the launch template (analyze-files hardcodes requiresTemplate:false for
    // MMGY accounts and reports hasTemplate from the Excel), so the requirement
    // is a property of the MMGY pipeline, not of the account setting.
    ? planFiles.some((f) => isLaunchTemplate(f.originalName, { isMmgy }))
    : (!templateRequired
      || planFiles.some((f) => isLaunchTemplate(f.originalName, { isMmgy })));
  // Yahoo implements campaign/line creation only; creative matching has no Yahoo handler.
  const recommendedType = destination === 'yahoo'
    ? 'fast-track-shell'
    : (fileAnalysis?.recommendedType || (creativeFiles.length > 0 ? 'fast-track' : 'fast-track-shell'));

  // Channel-config resolution for the chosen destination.
  // - 0 channels → inline error in the form; submit blocked.
  // - 1 channel  → auto-selected, no UI shown, channel_id flows through to
  //   the campaign at creation. Keeps the upload form lean.
  // - 2+ channels → required dropdown so the user picks explicitly.
  const platformChannels = useMemo(
    () => (destination ? getChannelsForPlatform(destination) : []),
    [destination, getChannelsForPlatform],
  );
  const needsChannelPick = platformChannels.length > 1;
  const autoSelectedChannel = platformChannels.length === 1 ? platformChannels[0] : null;
  // Block submission when the account has zero channels for this platform —
  // the launch would fail downstream with a less-obvious error. We only
  // surface this after the channel-configs hook has finished loading so the
  // form doesn't flash an error on initial paint.
  const hasNoChannel = (
    Boolean(destination)
    && !channelConfigsLoading
    && platformChannels.length === 0
  );

  // Auto-select the first available destination once channels have loaded.
  // Skips when the user has already picked one (or session-storage rehydrated
  // a prior choice). Pairs with the auto-channel-config logic below: a
  // single-channel account lands the user on a fully-populated form without a
  // click.
  useEffect(() => {
    if (channelsLoading) return;
    if (destination) return;
    if (availableDestinations.length === 0) return;
    setDestination(availableDestinations[0].value);
  }, [channelsLoading, availableDestinations, destination]);

  // Auto-select when there's exactly one channel for this (account, platform).
  // Also reset / re-derive when the user switches destination so a stale id
  // from a previous platform can't slip into the create payload.
  useEffect(() => {
    if (channelConfigsLoading || !destination) return;
    if (autoSelectedChannel) {
      if (selectedChannelId !== autoSelectedChannel.id) setSelectedChannelId(autoSelectedChannel.id);
      return;
    }
    if (platformChannels.length === 0) {
      if (selectedChannelId) setSelectedChannelId('');
      return;
    }
    // 2+ channels: if the prior selection doesn't belong to this platform,
    // clear it so the dropdown prompts the user.
    if (selectedChannelId && !platformChannels.some((c) => c.id === selectedChannelId)) {
      setSelectedChannelId('');
    }
  }, [destination, channelConfigsLoading, autoSelectedChannel, platformChannels, selectedChannelId]);

  const requiresPlatformId = platformRequiresId(destination);

  // The channel config this campaign will launch through, once resolved
  // (auto-selected single channel, or the user's pick from the dropdown).
  // Facebook's Page ID requirement hangs off its `oauth` field value.
  const selectedChannelConfig = useMemo(
    () => platformChannels.find((c) => c.id === selectedChannelId) || null,
    [platformChannels, selectedChannelId],
  );
  // Facebook OAuth channels run ads AS a Page, so they need a Page ID on top
  // of the ad account. Non-OAuth (system-user token) channels do not — the
  // Page rides the channel config. Same rule as the classic create form's
  // `isFacebookOAuthChannel` (CampaignCreation.js), read off the same
  // `field_values.oauth === 'true'` flag, so the two forms agree on which
  // Facebook campaigns need a Page.
  const isFacebookOAuthChannel = isFacebook
    && selectedChannelConfig?.field_values?.oauth === 'true';

  // Step 2's ✓. `advertiserId` is not universal (OpenAI has no per-campaign
  // id) and not sufficient (a Facebook OAuth channel also needs a Page), so
  // the tick tracks the same fields canSubmit does rather than a fixed four.
  // A GroundTruth platform account id is a bare number (e.g. 275625). Users
  // have pasted campaign URLs into this field (UAT 0818) — a URL as the
  // account id poisons everything keyed by it downstream (the taxonomy cache
  // lookup, the campaign-configuration card), so it blocks here with a plain
  // explanation instead of failing cryptically later.
  const gtAccountIdInvalid = isGroundTruth
    && Boolean(advertiserId.trim())
    && !/^\d+$/.test(advertiserId.trim());

  const detailsDone = Boolean(
    campaignName && folderId && destination
    && (!requiresPlatformId || advertiserId.trim())
    && !gtAccountIdInvalid
    && (!isFacebookOAuthChannel || pageId.trim()),
  );

  const canSubmit = (
    !submitting && !uploading
    && planDone
    && launchTemplateDone
    && Boolean(campaignName.trim())
    && Boolean(folderId)
    && Boolean(destination)
    && (!requiresPlatformId || Boolean(advertiserId.trim()))
    && !gtAccountIdInvalid
    && (!isFacebookOAuthChannel || Boolean(pageId.trim()))
    && !hasNoChannel
    && (!needsChannelPick || Boolean(selectedChannelId))
  );

  const submitNote = useMemo(() => {
    if (!planDone) {
      if (isGroundTruth) return 'Upload your GroundTruth Excel (.xlsx), insertion-order PDF, or ZIP bundle';
      if (isMmgy) return mmgyPlanOnly
        ? 'Upload the Excel media plan (.xlsx) to continue'
        : 'Upload your MMGY insertion order PDF to continue';
      return 'Upload your media plan to continue';
    }
    if (!launchTemplateDone) return isMmgy
      ? 'Add an MMGY Excel proposal sheet (.xlsx)'
      : 'This account requires a launch template for this platform — add the JSON (or rename an uploaded file to contain "launch template")';
    if (!campaignName.trim()) return 'Give the campaign a name';
    if (!folderId) return 'Select a folder';
    if (!destination) return 'Pick a destination platform';
    if (requiresPlatformId && !advertiserId.trim()) return isGroundTruth
      ? 'Add the GroundTruth platform account ID'
      : isFacebook
        ? 'Add the Facebook ad account ID'
        : 'Add an advertiser/account ID';
    if (gtAccountIdInvalid) return 'The GroundTruth platform account ID is a number (e.g. 275625) — paste just the ID, not a link';
    if (isFacebookOAuthChannel && !pageId.trim()) return 'Add the Facebook Page ID — this channel authenticates via OAuth';
    if (hasNoChannel) return `No ${destination} channel configuration on this account — ask an admin to add one`;
    if (needsChannelPick && !selectedChannelId) return 'Pick a channel configuration';
    if (isGroundTruth && gtPdfFile) return 'Ready — Sidekick will run GT CTS review before launch';
    if (isGroundTruth && gtZipFile) return 'Ready — gt-api will unzip the bundle and launch in one pass';
    if (isGroundTruth) return 'Ready — Sidekick will run GT CTS review before launch';
    return creativeFiles.length > 0
      ? `Ready — fast-track with ${creativeFiles.length} creative${creativeFiles.length === 1 ? '' : 's'}`
      : 'Ready — fast-track shell (no creatives)';
  }, [planDone, launchTemplateDone, campaignName, folderId, destination, advertiserId, creativeFiles.length, isGroundTruth, isMmgy, mmgyPlanOnly, needsChannelPick, selectedChannelId, gtPdfFile, hasNoChannel, gtZipFile, requiresPlatformId, isFacebook, isFacebookOAuthChannel, pageId, gtAccountIdInvalid]);

  // ─── Create draft campaign after CTS review ─────────────
  // Right after CTS validation succeeds (Excel or PDF), insert a
  // campaigns row with status='draft' so the campaign is visible in
  // /campaigns and the user can navigate away + come back. Uses
  // autoStart=false so the gt-api dispatch is NOT triggered — that
  // happens later in launchGroundTruthCampaign after Approve.
  //
  // The draft is soft-deleted at launch time (DELETE /api/v2/campaigns/:id)
  // so the launched campaign appears as a single fresh row in the table,
  // not alongside the lingering draft.
  //
  // Failure is non-fatal — the in-pane review still works without a draft
  // row; the user just won't see it in /campaigns until they Approve.
  const createDraftCampaign = useCallback(async (sourceFile) => {
    if (!sourceFile?.gcsPath) return null;
    try {
      const isPdf = (sourceFile.originalName || '').toLowerCase().endsWith('.pdf');
      const payload = {
        name: campaignName.trim(),
        folderId,
        destination: 'groundtruth',
        platformAccountId: advertiserId.trim(),
        accountId,
        userId,
        files: [{
          originalName: sourceFile.originalName,
          gcsPath: sourceFile.gcsPath,
          publicUrl: sourceFile.publicUrl,
          size: sourceFile.size,
          type: sourceFile.type,
        }],
        orchestration: {
          enabled: true,
          type: 'gt-api',
          priority: 'normal',
          // autoStart=false: campaign row is created, but the gt-api
          // PubSub dispatch is skipped (see routes/v2/campaigns.js:1109).
          autoStart: false,
          metadata: {
            source: 'campaign-launch-workflow',
            ctsReviewed: true,
            inputMode: isPdf ? 'pdf' : 'excel',
          },
        },
      };
      const res = await api.post(getApiUrl('/api/v2/campaigns'), payload);
      const id = res.data?.id;
      if (id) {
        setCampaignId(id);
        setSearchParams({ campaign: id }, { replace: true });
      }
      return id;
    } catch (e) {
      console.warn('Could not create draft campaign after CTS review:', e?.response?.data?.error || e.message);
      return null;
    }
  }, [campaignName, folderId, advertiserId, accountId, userId, setSearchParams]);

  // ─── Persist the CTS snapshot keyed by campaign id ──────
  // Cross-device resume needs a server-side source of truth — sessionStorage
  // doesn't survive a new browser or device. After the draft row is created,
  // upload the CTS review envelope to GCS under campaigns/{id}/cts-review.json
  // via POST /:id/cts-snapshot. Hydration on a different device then fetches
  // it back via GET /:id/cts-snapshot. Failure is non-fatal — the in-pane
  // review still works from in-memory state for the current session.
  const persistCtsSnapshot = useCallback(async (id, review, sourceFile) => {
    if (!id || !review) return;
    // A result-less envelope is a failure, not a review. Writing it as `ready`
    // made every later hydration restore a review pane with nothing in it —
    // including the cross-device path, which has no other state to fall back
    // on. Record it as `failed` so those paths can offer a retry instead.
    const failed = Boolean(gtCtsFailure(review));
    try {
      await api.post(getApiUrl(`/api/v2/campaigns/${id}/cts-snapshot`), {
        // validateAccountAccess (routes/v2/campaigns.js:158) requires
        // accountId in either the query string or the body — without it
        // the middleware 400s before reaching the handler and the GCS
        // write silently never happens. Pass userId too for parity with
        // the other v2 POSTs (delete, gt-review-edits, etc.).
        accountId,
        userId,
        status: failed ? 'failed' : undefined,
        ctsReview: review,
        parsedConfigGcsPath: review?.parsed_config_gcs_path || null,
        requestId: review?.request_id || null,
        // Original IO upload — lets the review pane offer "Download IO
        // file" after a cross-device resume, when planFiles is empty.
        sourceFile: sourceFile?.gcsPath
          ? { gcsPath: sourceFile.gcsPath, originalName: sourceFile.originalName || null }
          : null,
      });
    } catch (e) {
      console.warn('Could not persist CTS snapshot to GCS:', e?.response?.data?.error || e.message);
    }
  }, [accountId, userId]);

  // ─── GT IO→CTS run: pending marker, poll, apply ─────────
  // The IO (PDF) review is a fire-and-poll pipeline: dispatch returns a
  // requestId and the gt-api worker writes the result to GCS 30-90s later.
  // The work is durable server-side, so the only thing a reload can lose is
  // the requestId. We record it against the draft campaign the moment we get
  // it; hydration reads it back and resumes the same poll.

  const markIoRunPending = useCallback(async (id, requestId, sourceFile, startedAt, instructions = null, rules = null) => {
    if (!id || !requestId) return;
    try {
      await api.post(getApiUrl(`/api/v2/campaigns/${id}/cts-snapshot`), {
        accountId,
        userId,
        status: 'pending',
        requestId,
        startedAt,
        // The instruction snapshot this run was dispatched with, persisted with
        // the draft so a reload mid-run restores it and a retry re-sends it.
        ...(instructions ? { additionalInstructions: instructions } : {}),
        ...(rules !== null ? { standardRules: rules } : {}),
        sourceFile: sourceFile?.gcsPath
          ? { gcsPath: sourceFile.gcsPath, originalName: sourceFile.originalName || null }
          : null,
      });
    } catch (e) {
      // Non-fatal: the run still completes and the poll in this tab still
      // works — we just lose the ability to resume it after a reload.
      console.warn('Could not record in-flight IO run:', e?.response?.data?.error || e.message);
    }
  }, [accountId, userId]);

  // Poll until the result lands or `deadlineAt` passes. Returns the review
  // envelope, or null on timeout. Stops early if the component unmounted —
  // safe now that the run is recorded server-side and can be resumed.
  const pollIoCtsResult = useCallback(async (requestId, deadlineAt) => {
    while (Date.now() < deadlineAt) {
      if (unmountedRef.current) return null;
      // A failed poll is NEVER terminal: the worker reports its own failures
      // by writing an error result to GCS (status 'ready', success:false), so
      // anything that goes wrong HERE — an LB 502, a network blip, a backend
      // deploy — is transient by definition and worth another try. One
      // throwing GET out of ~600 used to take down the whole pane.
      const pollRes = await api.get(
        getApiUrl(`/api/upload/review-cts-result/${requestId}`),
        { validateStatus: (s) => s === 200 || s === 202 },
      ).catch((e) => {
        // Auth failures are PERMANENT — retrying a 401/403 for half an hour
        // just hides a dead session. Everything else (network blip, LB 502,
        // backend deploy) has no response or a 5xx and is worth another try.
        const st = e?.response?.status;
        if (st === 401 || st === 403) throw e;
        return null;
      });
      if (pollRes?.status === 200 && pollRes.data?.status === 'ready') {
        return pollRes.data.result;
      }
      await new Promise((r) => setTimeout(r, IO_CTS_POLL_INTERVAL_MS));
    }
    return null;
  }, []);

  // Move the UI into the review pane for a finished review. Shared by the
  // fresh-dispatch path and the resume path so they can't drift.
  const applyIoCtsReview = useCallback((review) => {
    // Every IO envelope (success or failure) carries the instruction snapshot
    // the worker actually used; it is the source of truth for a later retry.
    if (review?.instruction_snapshot) {
      setRunInstructions(review.instruction_snapshot.additional_instructions || null);
      setRunRules(review.instruction_snapshot.standard_rules ?? null);
    }
    // The gt-api worker reports a failed conversion as a 200 with a
    // result-less envelope, so this is the only place that can tell the
    // difference. Landing it on the review pane at 100% presented a dead run
    // as a finished one — the operator saw an empty grid with no reason.
    const failure = gtCtsFailure(review);
    if (failure) {
      setProcError(failure.message);
      setOrchStatus('failed');
      setProcStepLabel(null);
      setPane('processing');
      return;
    }
    setCtsReview(review);
    setReviewRows(buildGtReviewRows(review));
    setEditedRows({});
    setCtsFindingsStale(false);
    // A finished run opens at its default: an IO review's findings hidden.
    setIssuesBarChoice(null);
    // Seed appliedConfigGcsPath from the handler-uploaded parsed config
    // so launchGroundTruthCampaign dispatches via JSON (skipping the
    // PDF re-parse). User edits via the reviewer agent can later
    // overwrite this with a versioned revision under
    // campaigns/{id}/gt-config-revisions/, but for the unedited
    // happy-path this gets us straight to create-campaign-from-json.
    if (review?.parsed_config_gcs_path) {
      // Strip the gs://bucket/ prefix — appliedConfigGcsPath is stored
      // as a GCS key elsewhere in this file (chat.js / gt-review-edits
      // both produce keys, not gs:// URLs).
      const raw = review.parsed_config_gcs_path;
      const key = raw.startsWith('gs://') ? raw.replace(/^gs:\/\/[^/]+\//, '') : raw;
      setAppliedConfigGcsPath(key);
    }
    setOrchStatus('review');
    setProcStepIdx(7);
    setProcPercent(100);
    setProcStepLabel(null);
    setPane('review');
  }, [setEditedRows]);

  // ─── Run GT CTS review (no campaign record yet) ─────────
  // GT does not go through fast-track orchestration. Instead we hit the
  // existing /api/upload/review-cts route, which publishes a review-cts
  // request to the gt-api PubSub topic and polls GCS for the result. The
  // result drives the summary/review pane directly. Once the review is
  // ready, createDraftCampaign() inserts a draft campaigns row so the
  // user can see it in /campaigns and resume it later. The gt-api
  // create-campaign dispatch only happens on Approve in
  // launchGroundTruthCampaign().
  const runGtCtsReview = useCallback(async () => {
    if (!gtExcelFile?.gcsPath) {
      setProcError('Could not find the uploaded Excel file.');
      return;
    }
    setSubmitting(true);
    setProcError(null);
    setOrchStatus('starting');
    setProcStepIdx(1); // Dispatching CTS review to gt-api
    setProcPercent(20);
    setProcStepLabel(null);
    setPane('processing');
    // Walk through GT processing steps while we wait. The real backend doesn't
    // emit progress events for review-cts (it's one PubSub round-trip), so
    // these are cosmetic — they give the user something to watch and keep the
    // perceived stages aligned with the GT-specific step labels above.
    const ticks = [
      { idx: 2, pct: 35, delay: 600 },   // Loading & parsing Excel
      { idx: 3, pct: 50, delay: 1400 },  // Validating campaign fields
      { idx: 4, pct: 65, delay: 2200 },  // Validating adgroups & products
      { idx: 5, pct: 78, delay: 3000 },  // Validating audiences & location lists
      { idx: 6, pct: 90, delay: 3800 },  // Compiling review report
    ];
    const timers = ticks.map((t) => setTimeout(() => {
      setProcStepIdx(t.idx);
      setProcPercent(t.pct);
    }, t.delay));
    try {
      // The CTS review applies the typed Additional Instructions and the
      // workbook's own "Additional Notes from AM" — opted in explicitly, since
      // other callers of this endpoint launch from the workbook. Blank means
      // none; the AM notes still apply.
      const instructions = additionalInstructions.trim() ? additionalInstructions : null;
      const res = await api.post(getApiUrl('/api/upload/review-cts'), {
        gcsPath: gtExcelFile.gcsPath,
        accountId: advertiserId.trim(),
        workspaceAccountId: accountId,
        userId,
        platform: 'groundtruth',
        applyInstructions: true,
        ...(instructions ? { additionalInstructions: instructions } : {}),
      });
      const review = res.data?.result || null;
      if (!review) throw new Error('CTS review returned no result');
      // Parse failures come back 200 with a result-less envelope, same as the
      // PDF path — surface the handler's own reason instead of an empty grid.
      const reviewFailure = gtCtsFailure(review);
      if (reviewFailure) throw new Error(reviewFailure.message);
      setRunInstructions(instructions);
      setCtsReview(review);
      setReviewRows(buildGtReviewRows(review));
      setEditedRows({});
      setCtsFindingsStale(false);
      // A finished run opens at its default: a workbook review's findings shown.
      setIssuesBarChoice(null);
      // When instructions changed the plan, the worker publishes the reviewed
      // config: launch must read it (create-campaign-from-json), not re-parse
      // the workbook without them. Absent, launch uses the workbook as before.
      const reviewedKey = toGcsKey(review.parsed_config_gcs_path);
      if (reviewedKey) setAppliedConfigGcsPath(reviewedKey);
      setOrchStatus('review');
      setProcStepIdx(7);
      setProcPercent(100);
      setProcStepLabel(null);
      // Summary + review consolidated into a single 'review' pane — the
      // ReviewPane renders the hero/tiles/config/breakdown/QA pivot above
      // the grid, so there's no longer a separate summary stop.
      setPane('review');
      // Insert the draft campaigns row so this CTS-reviewed campaign
      // shows up in /campaigns immediately, even before launch. Then
      // persist the CTS snapshot to GCS keyed by campaign id so
      // cross-device resume works. Both calls are non-fatal.
      createDraftCampaign(gtExcelFile).then((id) => {
        if (id) persistCtsSnapshot(id, review, gtExcelFile);
      });
    } catch (e) {
      console.error('GT review-cts failed', e);
      setProcError(e?.response?.data?.error || e.message || 'GT CTS review failed');
      setOrchStatus('failed');
    } finally {
      timers.forEach(clearTimeout);
      setSubmitting(false);
    }
  }, [gtExcelFile, advertiserId, accountId, userId, additionalInstructions, createDraftCampaign, persistCtsSnapshot, setEditedRows]);

  // ─── GroundTruth PDF CTS review ─────────────────────────
  // PDF used to be a one-shot launch (runGtPdfLaunch → /api/v2/campaigns
  // → sub_action='create-campaign-from-pdf'). It now mirrors the Excel
  // two-step flow: dispatch the review job, then poll GCS for the result.
  //
  // We can't share Excel's synchronous /api/upload/review-cts endpoint:
  // the PDF pipeline is parallel Claude + GPT-5 + judge, which typically
  // runs 30-90s and trips nginx / GCP-LB request timeouts. Instead, the
  // backend exposes a dispatch + poll pair:
  //   • POST /api/upload/review-cts-pdf/dispatch  -> returns {requestId}
  //   • GET  /api/upload/review-cts-result/:id    -> 202 (pending) or 200
  //
  // The Python side does the rest: gt_api_handler runs
  // GTIOToCampaignConverter (PDF → IO JSON → campaign_config) +
  // GTSemanticValidator, uploads the parsed campaign_config to GCS as
  // a sibling `.parsed_{ts}.json`, and writes the merged result to
  // validation-results/{request_id}.json.
  //
  // The result envelope is shape-compatible with Excel's review-cts
  // result (top-level `result` is the campaign_config), so ctsReview /
  // ReviewPane / buildGtReviewRows all work unchanged. The PDF-only
  // `parsed_config_gcs_path` field becomes appliedConfigGcsPath so
  // approve-and-launch dispatches via create-campaign-from-json,
  // skipping a re-parse of the PDF.
  const runGtPdfReview = useCallback(async () => {
    if (!gtPdfFile?.gcsPath) {
      setProcError('Could not find the uploaded PDF.');
      return;
    }
    // Suppress the v3 progress poll for the whole dispatch window, before
    // pendingIoRun exists (see ioCtsDispatchRef above).
    ioCtsDispatchRef.current = true;
    setSubmitting(true);
    setProcError(null);
    setOrchStatus('starting');
    setProcStepIdx(1); // Dispatching CTS review to gt-api
    setProcPercent(15);
    setProcStepLabel(null);
    setPane('processing');
    // The progress ticker and the poll both hang off `pendingIoRun` below.
    let draftId = null;
    try {
      // 1. Draft campaigns row FIRST, which puts ?campaign=<id> in the URL.
      //    Every other launch path in this file already does this before its
      //    long-running step, precisely so a refresh keeps the user on the
      //    workflow. The IO review takes 30-90s, so it needs it most.
      draftId = await createDraftCampaign(gtPdfFile);

      // 2. Dispatch — publishes the gt-api PubSub message and returns
      //    immediately with the request id (HTTP 202). A snapshot of the
      //    Additional Instructions rides the dispatch; blank means "none", and
      //    no instruction snapshot is added to the request.
      const instructions = additionalInstructions.trim() ? additionalInstructions : null;
      const dispatchRes = await api.post(getApiUrl('/api/upload/review-cts-pdf/dispatch'), {
        gcsPath: gtPdfFile.gcsPath,
        accountId: advertiserId.trim(),
        workspaceAccountId: accountId,
        userId,
        ...(instructions ? { additionalInstructions: instructions } : {}),
      });
      const requestId = dispatchRes.data?.requestId;
      if (!requestId) throw new Error('PDF review dispatch returned no requestId');
      setRunInstructions(instructions);
      const rules = dispatchRes.data?.standardRules ?? null;
      setRunRules(rules);

      // 3. Record the in-flight run against the campaign before we start
      //    waiting on it. This is the handle a reload uses to find the run.
      const startedAt = new Date().toISOString();
      await markIoRunPending(draftId, requestId, gtPdfFile, startedAt, instructions, rules);

      // 4. Poll. The effect below drives the identical loop when a reload
      //    picks the run back up.
      setPendingIoRun({ requestId, startedAt, sourceFile: gtPdfFile });
      ioCtsDispatchRef.current = false;
    } catch (e) {
      ioCtsDispatchRef.current = false;
      console.error('GT PDF review-cts dispatch failed', e);
      setProcError(e?.response?.data?.error || e.message || 'GT PDF review failed');
      setOrchStatus('failed');
      setSubmitting(false);
      // Nothing was dispatched, so the draft row would be an empty shell in
      // /campaigns with no run behind it. Drop it. (A run that dispatched and
      // then timed out keeps its row — that one is resumable.)
      if (draftId) {
        api.delete(getApiUrl(`/api/v2/campaigns/${draftId}`), {
          params: { accountId, userId },
        }).catch(() => { /* best-effort cleanup */ });
        setCampaignId(null);
        setSearchParams({}, { replace: true });
      }
      return;
    }
    // From here the poll effect owns the lifecycle, including clearing
    // `submitting`.
  }, [
    gtPdfFile, advertiserId, accountId, userId, additionalInstructions,
    createDraftCampaign, markIoRunPending, setSearchParams,
  ]);

  // ─── Progress bar for an in-flight IO→CTS run ───────────
  // Separate from the poll effect below because it's idempotent: restarting
  // the ticker is harmless, whereas restarting the poll is not. Derives the
  // bar position from elapsed time since dispatch, so a resumed run doesn't
  // rewind to 15%.
  useEffect(() => {
    if (!pendingIoRun?.requestId) return undefined;
    const startedMs = Date.parse(pendingIoRun.startedAt || '') || Date.now();
    const paint = () => {
      const elapsed = Date.now() - startedMs;
      const reached = IO_CTS_TICKS.filter((t) => t.at <= elapsed).pop();
      // This run owns the step list — clear any v3-progress stepLabel so the
      // static GT labels render instead of a stale "Parsing media plan".
      // Past the slow-notice threshold, say the run is long-running instead:
      // a silent 20-minute "Compiling review report" reads as a hang.
      setProcStepLabel(elapsed > IO_CTS_SLOW_NOTICE_MS
        ? 'Still working — large IOs can take 30+ minutes'
        : null);
      if (reached) {
        setProcStepIdx(reached.idx);
        setProcPercent(reached.pct);
      }
    };
    paint();
    const timer = setInterval(paint, 1000);
    return () => clearInterval(timer);
  }, [pendingIoRun]);

  // Re-dispatch the IO→CTS run for a campaign whose review already failed.
  // runGtPdfReview cannot be reused here: it reads `gtPdfFile`, which is empty
  // on every resume path (deep link, cross-device, second visit). The source
  // PDF is the one thing the CTS snapshot always keeps, so drive off that and
  // hand the run to the same pending-poll machinery a fresh dispatch uses.
  const retryIoCtsReview = useCallback(async () => {
    const source = ctsSourceFile?.gcsPath ? ctsSourceFile : null;
    if (!source || !campaignId) {
      setProcError('Could not find the uploaded IO file to retry.');
      setOrchStatus('failed');
      setPane('processing');
      return;
    }
    // A retry re-sends the PERSISTED instruction snapshot, never whatever the
    // upload-pane field holds now — revised instructions are a new run, started
    // from the upload pane. Read before the envelope below is cleared.
    const instructions = runInstructions
      ?? (ctsReview?.instruction_snapshot?.additional_instructions || null);
    const rules = ctsReview?.instruction_snapshot?.standard_rules ?? runRules;
    ioCtsDispatchRef.current = true;
    setSubmitting(true);
    setProcError(null);
    // Clear the failed envelope so a second failure re-renders instead of
    // leaving the previous reason on screen.
    setCtsReview(null);
    setReviewRows([]);
    setCtsFindingsStale(false);
    setOrchStatus('starting');
    setProcStepIdx(1);
    setProcPercent(15);
    setProcStepLabel(null);
    setPane('processing');
    try {
      const dispatchRes = await api.post(getApiUrl('/api/upload/review-cts-pdf/dispatch'), {
        gcsPath: source.gcsPath,
        accountId: (advertiserId || '').trim(),
        workspaceAccountId: accountId,
        userId,
        ...(instructions ? { additionalInstructions: instructions } : {}),
        ...(rules !== null ? { standardRules: rules } : {}),
      });
      const requestId = dispatchRes.data?.requestId;
      if (!requestId) throw new Error('PDF review dispatch returned no requestId');
      const startedAt = new Date().toISOString();
      // Overwrites the `failed` snapshot with a `pending` marker, so a reload
      // during the retry reattaches to the new run instead of restoring the
      // old failure.
      const effectiveRules = dispatchRes.data?.standardRules ?? rules;
      setRunRules(effectiveRules);
      await markIoRunPending(campaignId, requestId, source, startedAt, instructions, effectiveRules);
      setPendingIoRun({ requestId, startedAt, sourceFile: source });
      ioCtsDispatchRef.current = false;
    } catch (e) {
      ioCtsDispatchRef.current = false;
      console.error('GT PDF review-cts retry failed', e);
      setProcError(e?.response?.data?.error || e.message || 'GT PDF review retry failed');
      setOrchStatus('failed');
      setSubmitting(false);
    }
  }, [ctsSourceFile, campaignId, advertiserId, accountId, userId, markIoRunPending, setReviewRows, runInstructions, runRules, ctsReview]);

  // ─── Poll an in-flight IO→CTS run ───────────────────────
  // Driven by `pendingIoRun`, set either by a fresh dispatch above or by
  // hydration finding a pending snapshot after a reload. Both paths land
  // here, so a resumed run behaves exactly like one that was never
  // interrupted.
  const ioRunPolledRef = useRef(null);
  useEffect(() => {
    if (!pendingIoRun?.requestId) return;
    // Guard against a second pass for the same run (effect re-entry, or a
    // hydration that races the dispatch path).
    if (ioRunPolledRef.current === pendingIoRun.requestId) return;
    ioRunPolledRef.current = pendingIoRun.requestId;

    const { requestId, sourceFile } = pendingIoRun;
    // Fresh window per attach — see IO_CTS_TIMEOUT_MS for why this must not
    // be anchored to the dispatch time.
    const deadlineAt = Date.now() + IO_CTS_TIMEOUT_MS;

    (async () => {
      try {
        const review = await pollIoCtsResult(requestId, deadlineAt);
        if (unmountedRef.current) return;
        if (!review) {
          throw new Error(
            'The gt-api pipeline is still running after 4 hours of watching. '
            + 'It continues in the background — refresh this page to reattach, '
            + 'or check the gt-api worker logs if it never finishes.',
          );
        }
        applyIoCtsReview(review);
        if (campaignId) {
          // Overwrite the pending marker with the full envelope so the next
          // hydration restores the review pane instead of re-polling.
          persistCtsSnapshot(campaignId, review, sourceFile);
        } else {
          // The draft row couldn't be created before dispatch (so this run
          // was never resumable). Fall back to the old create-after-review
          // order so the campaign still shows up in /campaigns.
          createDraftCampaign(sourceFile).then((id) => {
            if (id) persistCtsSnapshot(id, review, sourceFile);
          });
        }
      } catch (e) {
        if (unmountedRef.current) return;
        console.error('GT PDF review-cts failed', e);
        setProcError(e?.response?.data?.error || e.message || 'GT PDF review failed');
        setOrchStatus('failed');
      } finally {
        if (!unmountedRef.current) {
          setSubmitting(false);
          setPendingIoRun(null);
          // The ticker may have left the slow-run notice in procStepLabel;
          // this run owns that label, so clear it when the run ends on ANY
          // path (success re-nulls it in applyIoCtsReview; failure/timeout
          // otherwise leaves it to leak into the next flow's step list).
          setProcStepLabel(null);
        }
      }
    })();
  }, [
    pendingIoRun, campaignId,
    pollIoCtsResult, applyIoCtsReview, persistCtsSnapshot, createDraftCampaign,
  ]);

  // ─── GroundTruth ZIP launch ─────────────────────────────
  // Mirrors runGtPdfLaunch: submit straight to /api/v2/campaigns with the
  // ZIP file and orchestration.type='gt-api'. The chat.js dispatcher
  // recognises the .zip extension and sets dispatchOptions.zip_file_path;
  // gt_api_handler runs launch_campaign_from_zip, which preprocesses the
  // bundle (extract Excel + upload to Drive) and chains into the standard
  // Excel-launch pipeline. The poll loop then watches it the same way as
  // the PDF flow.
  const runGtZipLaunch = useCallback(async () => {
    if (!gtZipFile?.gcsPath) {
      setProcError('Could not find the uploaded ZIP.');
      return;
    }
    setSubmitting(true);
    setProcError(null);
    setOrchStatus('starting');
    setProcStepIdx(0);
    setProcPercent(10);
    setProcStepLabel('Submitting ZIP to gt-api');
    setPane('processing');
    try {
      const payload = {
        name: campaignName.trim(),
        folderId,
        destination: 'groundtruth',
        platformAccountId: advertiserId.trim(),
        accountId,
        userId,
        files: [{
          originalName: gtZipFile.originalName,
          gcsPath: gtZipFile.gcsPath,
          publicUrl: gtZipFile.publicUrl,
          size: gtZipFile.size,
          type: gtZipFile.type,
        }],
        orchestration: {
          enabled: true,
          type: 'gt-api',
          priority: 'normal',
          autoStart: true,
          metadata: {
            source: 'campaign-launch-workflow',
            inputMode: 'zip',
          },
        },
      };
      const res = await api.post(getApiUrl('/api/v2/campaigns'), payload);
      const created = res.data || {};
      const id = created.id;
      if (!id) throw new Error('GroundTruth campaign creation returned no id');
      setCampaignId(id);
      setOrchStatus(created?.orchestration?.status || 'processing');
      setSearchParams({ campaign: id }, { replace: true });
    } catch (e) {
      console.error('GT ZIP launch failed', e);
      setProcError(e?.response?.data?.error || e.message || 'GT ZIP launch failed');
      setOrchStatus('failed');
    } finally {
      setSubmitting(false);
    }
  }, [gtZipFile, campaignName, folderId, advertiserId, accountId, userId, setSearchParams]);

  // ─── Create campaign ────────────────────────────────────
  const startProcessing = useCallback(async () => {
    if (!canSubmit) return;

    // GroundTruth path: branch on plan-file type.
    // Excel runs CTS review first so the user can approve before launch;
    // PDF and ZIP both run the full pipeline in one gt-api dispatch and
    // land directly on the comprehensive launcher-result viewer.
    if (isGroundTruth) {
      if (gtPdfFile) {
        await runGtPdfReview();
        return;
      }
      if (gtZipFile) {
        await runGtZipLaunch();
        return;
      }
      await runGtCtsReview();
      return;
    }

    setSubmitting(true);
    setProcError(null);
    setCampaignNameConflict(false);
    setOrchStatus('starting');
    setProcStepIdx(0);

    try {
      const files = [...planFiles, ...(destination === 'yahoo' ? [] : creativeFiles)].map((f) => ({
        originalName: f.originalName,
        gcsPath: f.gcsPath,
        publicUrl: f.publicUrl,
        size: f.size,
        type: f.type,
      }));

      // Backend expects platform-specific id fields (advertiserId for
      // DV360/TTD, platformAccountId for GroundTruth, etc.) — map the
      // single UI input to whichever field this destination needs.
      // OpenAI authenticates via api_key only and has no per-campaign ID,
      // so we omit any ID field from the payload.
      const idField = (() => {
        switch (destination) {
          case 'groundtruth': return 'platformAccountId';
          case 'reddit': return 'profileId';
          // Facebook, like LinkedIn, is keyed by its AD ACCOUNT: campaigns.js
          // reads adAccountId into campaigns.advertiser_id for both, and the
          // v2 route rejects a non-planning Facebook create without it. The
          // Page rides `pageId` alongside (secondary_platform_id) when the
          // channel is OAuth — see the payload below.
          case 'facebook': return 'adAccountId';
          case 'linkedin': return 'adAccountId';
          case 'google-ads':
          case 'google-ads-search':
          case 'google-ads-pmax':
          case 'google-ads-demandgen': return 'customerId';
          case 'openai': return null;
          default: return 'advertiserId'; // dv360, ttd, madhive, amazon-dsp
        }
      })();

      // MMGY accounts always tag the campaign with an ioType (mmgyIoType,
      // above) so the backend can route to the MMGY / TravelDesk processor,
      // or skip it for plan-only. The user-selected value when the IO Type
      // selector was visible for this destination; otherwise 'mmgy'.
      const payload = {
        name: campaignName.trim(),
        folderId,
        destination,
        accountId,
        userId,
        ...(idField ? { [idField]: advertiserId.trim() } : {}),
        // Facebook's Page, when the channel authenticates via OAuth. Lands in
        // campaigns.secondary_platform_id and is echoed into the launcher
        // message, exactly as the classic create form sends it.
        ...(isFacebookOAuthChannel && pageId.trim() ? { pageId: pageId.trim() } : {}),
        ...(mmgyIoType ? { ioType: mmgyIoType } : {}),
        ...(selectedChannelId ? { channelId: selectedChannelId } : {}),
        files,
        orchestration: {
          enabled: true,
          type: recommendedType,
          priority: 'normal',
          autoStart: true,
          planningMode: true,
          message: `Campaign Launch Workflow: ${campaignName.trim()}`,
          metadata: {
            source: 'campaign-launch-workflow',
            targeting: targeting.trim() || undefined,
            goals: goals.trim() || undefined,
          },
        },
      };

      setProcStepIdx(2); // Files are already uploaded; this request creates the campaign.
      setPane('processing');
      const res = await api.post(getApiUrl('/api/v2/campaigns'), payload);
      const created = res.data;
      const id = created?.id;
      if (!id) throw new Error('Campaign creation returned no id');
      setCampaignId(id);
      setOrchStatus(created?.orchestration?.status || 'processing');
      // Push the campaignId into the URL so a refresh / back-button keeps
      // the user on this workflow instead of dumping them back to upload.
      setSearchParams({ campaign: id }, { replace: true });
    } catch (e) {
      console.error('Campaign creation failed', e);
      const message = e?.response?.data?.error || e.message || 'Campaign creation failed';
      const nameConflict = e?.response?.status === 409
        && /campaign with this name already exists/i.test(message);
      setCampaignNameConflict(nameConflict);
      setProcError(nameConflict
        ? `A campaign named “${campaignName.trim()}” already exists in the selected folder. Choose a different campaign name or folder, then try again. Your uploaded files are retained; no new campaign was created.`
        : message);
      setOrchStatus('failed');
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, planFiles, creativeFiles, campaignName, folderId, destination, advertiserId, pageId, isFacebookOAuthChannel, selectedChannelId, recommendedType, targeting, goals, accountId, userId, setSearchParams, mmgyIoType, isGroundTruth, gtPdfFile, gtZipFile, runGtPdfReview, runGtZipLaunch, runGtCtsReview]);

  // ─── Poll v3 progress endpoint while in processing pane ─
  useEffect(() => {
    // URL hydration owns the first progress check; a concurrent poll here
    // would double-fire it (and, for imported campaigns, double-fire the
    // planning-review fallback).
    if (hydrating || isImportedCampaign) return;
    // The IO→CTS run owns its own progress: the elapsed-time ticker drives
    // the bar and the review-cts-result poll drives completion. The v3
    // progress endpoint knows nothing about that run and answers
    // stepIndex 2 / 20% ("Parsing media plan") for a no-orchestration draft
    // forever — polling it here makes the step list jump back and forth
    // between the two progress sources every few seconds.
    if (pendingIoRun?.requestId || ioCtsDispatchRef.current) return;
    if (pane !== 'processing' || !campaignId) return;
    if (orchStatus === 'review' || orchStatus === 'failed' || orchStatus === 'completed') return;
    let cancelled = false;
    let timer;
    const tick = async () => {
      try {
        const res = await api.get(getApiUrl(`/api/v3/campaigns/${campaignId}/progress`), {
          params: { accountId, userId },
        });
        if (cancelled) return;
        const p = res.data || {};
        if (planningGridForAllRoles && !p.isImported && p.phase === 'launch') {
          const status = p.launchStatus || p.status;
          if (status === 'completed') {
            navigate(`/campaigns/${encodeURIComponent(campaignId)}`, { replace: true });
            return;
          }
          setLaunchProgress(status);
          setOrchStatus(status);
          setProcError(status === 'failed' ? (p.launchError || p.error || 'Campaign launch failed') : null);
          setPane('processing');
          if (status === 'completed' || status === 'failed') return;
        }
        // Imported campaigns resolve through the planning-review fallback the
        // same way URL hydration does — render the review pane, never park on
        // processing.
        if (p.isImported) {
          setIsImportedCampaign(true);
          fetchPlanningReview(campaignId, { imported: true });
          return;
        }
        if (typeof p.stepIndex === 'number') setProcStepIdx(p.stepIndex);
        if (typeof p.progressPercent === 'number') setProcPercent(p.progressPercent);
        // Absent stepLabel clears the override so the static step list label
        // wins (used by the gt-api flow — see /api/v3/campaigns/:id/progress).
        setProcStepLabel(p.stepLabel || null);
        if (p.status) setOrchStatus(p.status);
        if (p.status === 'review' || p.planningReviewReady) {
          fetchPlanningReview(campaignId);
          return;
        }
        // GT PDF flow goes straight from 'processing' to 'completed' — no
        // 'review' checkpoint. Mirror the hydrate-from-URL behaviour and fetch
        // the launcher result so the comprehensive viewer can render.
        if (p.status === 'completed') {
          // A successful GroundTruth IO launch (the ZIP flow stays here while
          // it runs) continues in its launched campaign workspace; anything
          // else loads the review as before. Not gated on this poll's
          // `cancelled`: setOrchStatus above re-runs the effect, which cancels
          // this run before the lookup returns — only an unmount stops it.
          findGtLaunchedWorkspace({ campaignId, accountId, userId }).then((launchedTarget) => {
            if (unmountedRef.current) return;
            if (launchedTarget) navigate(`/campaigns/${encodeURIComponent(launchedTarget)}`, { replace: true });
            else fetchPlanningReview(campaignId);
          });
          return;
        }
        if (p.status === 'failed') {
          setProcError(p.error || 'Orchestration failed');
          return;
        }
      } catch (e) {
        if (!cancelled) console.warn('progress poll error', e?.response?.data?.error || e?.message);
      }
      if (!cancelled) timer = setTimeout(tick, 3000);
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pane, campaignId, orchStatus, hydrating, isImportedCampaign, pendingIoRun, planningGridForAllRoles]);

  const fetchPlanningReview = useCallback(async (id, opts = {}) => {
    setSummaryLoading(true);
    setSummaryError(null);
    setImportWarnings([]);
    // Fresh load → allow the one-time bulk audience refresh to auto-run again
    // for this campaign (the permanent server sentinel still gates re-runs).
    bulkRefreshStartedRef.current = false;
    setBulkAudienceRefresh({ status: 'idle' });
    // Show the processing/loading screen during fetch. On second-visit hydration
    // the pane was restored from sessionStorage to 'summary'/'review' with stale
    // data — force 'processing' so the loading UI shows until data arrives.
    setPane('processing');
    try {
      const res = await api.get(getApiUrl(`/api/v2/campaigns/${id}/planning-review`), {
        params: { accountId, userId },
      });
      res.data = await ensureYahooReviewPrepared(res.data, { api, getApiUrl, campaignId: id, accountId, userId });
      // URL-hydrated campaigns (grid row clicks, imported campaigns) don't
      // pass through the upload pane, so destination / name can still be
      // empty here. Backfill them from the campaign envelope the endpoint
      // returns — launched-edit dispatch and the review header both read
      // them. Creation flows already hold the same values, so this is a
      // no-op there.
      const campaignEnvelope = res.data?.campaign;
      if (campaignEnvelope?.destination) {
        setDestination((prev) => prev || campaignEnvelope.destination);
      }
      if (campaignEnvelope?.name) setCampaignName((prev) => prev || campaignEnvelope.name);
      // Fidelity warnings the import-plan adapter emitted (light imports with
      // no targeting/creative detail, merged multi-campaign payloads, …).
      if (Array.isArray(res.data?.importWarnings)) setImportWarnings(res.data.importWarnings);
      const lr = res.data?.launcherResult || null;
      // GT PDF launcher results take the io_json + conversion_metadata
      // shape — adapt them into a ctsReview so the existing GT review
      // pane (tiles, grid, breakdown, issues bar, QA pivot) renders them
      // without duplicating any UI. PDF flow is already launched by the
      // time we land here, so flip alreadyLaunched=true regardless of
      // what the backend says.
      if (isGtLauncherResult(lr)) {
        const adapted = gtLauncherToCtsReview(lr);
        setLauncherResult(null);
        setCtsReview(adapted);
        setReviewRows(buildGtReviewRows(adapted));
        setEditedRows({});
        setCtsFindingsStale(false);
        setAlreadyLaunched(true);
      } else if (res.data?.ctsReview && typeof res.data.ctsReview === 'object') {
        // GT IO DRAFT. There is no launcherResult until launch — the plan lives
        // in the CTS envelope, which this endpoint serves for draft campaigns
        // (see the GT snapshot branch in routes/v2/campaigns.js).
        //
        // This branch was missing. The backend learned to serve `ctsReview`
        // here, but this consumer only ever read `launcherResult`, so a GT draft
        // fell through to the generic path below: launcherResult null →
        // buildReviewRows(null) → no rows, ctsReview never set → isGt false →
        // the review pane rendered the DV360-flavoured "No parsed plan data
        // available yet." on a campaign whose 11 ad groups were sitting in the
        // response body.
        setLauncherResult(null);
        // Latest saved revision, its launch path and whether its findings
        // predate it — the same head the cts-snapshot resume installs.
        applyGtHead(res.data);
        setEditedRows({});
        setAlreadyLaunched(Boolean(res.data?.alreadyLaunched));
        // The Sidekick create-payload needs the source PDF/Excel; without it the
        // agent silently goes unavailable on a resumed draft.
        if (res.data?.sourceFile?.gcsPath) setCtsSourceFile(res.data.sourceFile);
      } else if (res.data?.ctsReviewStatus === 'pending' && res.data?.requestId) {
        // The IO→CTS run is still going. Re-attach the ticker rather than
        // landing on an empty review pane — same resume the cts-snapshot path
        // does, and the reason planning-review returns requestId/startedAt.
        if (!destination) setDestination('groundtruth');
        setSubmitting(true);
        setOrchStatus('starting');
        setProcStepLabel(null);
        if (res.data?.sourceFile?.gcsPath) setCtsSourceFile(res.data.sourceFile);
        setPendingIoRun({
          requestId: res.data.requestId,
          startedAt: res.data.startedAt || null,
          sourceFile: res.data.sourceFile || null,
        });
        setPane('processing');
        return;
      } else {
        setLauncherResult(lr);
        setReviewRows(buildReviewRows(lr));
        setEditedRows({});
        // Backend tags the response with `alreadyLaunched` whenever the
        // orchestration has moved past 'review' (status is now in_progress,
        // completed, failed, etc.). The Launch button is disabled, and edits
        // route through edit-campaign requests against the live platform.
        setAlreadyLaunched(Boolean(res.data?.alreadyLaunched));
        setPlatformCampaignId(res.data?.campaign?.platform_campaign_id || null);
        // Whether the one-time bulk audience refresh already ran (permanent
        // server sentinel). Drives the auto-run on load.
        setAudiencesBulkRefreshed(Boolean(res.data?.audiencesBulkRefreshed));
      }
      // Summary + review consolidated — land directly on the unified review
      // pane (which renders the hero/tiles/config/breakdown above the grid).
      setPane('review');
    } catch (e) {
      console.error('planning-review fetch failed', e);
      // Imported campaigns with no derivable plan (still processing, failed,
      // or an unsupported platform) belong on the legacy detail page — it is
      // the surface that supports import activity, live status and retry.
      // Land there instead of stranding the user on an error pane.
      if (opts?.imported && e?.response?.status === 404) {
        navigate(`/campaigns/${encodeURIComponent(id)}`, { replace: true });
        return;
      }
      setSummaryError(e?.response?.data?.error || e.message || 'Could not load parse summary');
      // Imported campaigns have no processing pipeline to fall back to — if
      // the derived plan can't be built (transient failure), land on the
      // review pane so the summary error is visible instead of parking on an
      // endless processing screen.
      if (opts?.imported) setPane('review');
    } finally {
      setSummaryLoading(false);
    }
  }, [accountId, userId, setEditedRows, navigate, applyGtHead]);

  // When processing fails but a campaignId exists, the campaign data was
  // likely built successfully — skip the error screen and go straight to
  // summary + review so the user can inspect and correct.
  useEffect(() => {
    if (procError && campaignId && !launchProgress) fetchPlanningReview(campaignId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procError, campaignId, launchProgress]);

  // ─── effective row (with pending edits applied) ─────────
  // getRowValue / commitCellEdit / cancelCellEdit / toggleListDeletion /
  // pendingDeletionCount come from useReviewGridEdits above (shared with
  // LaunchCampaignDetails and CopyPlanStep).

  // ─── Bulk audience refresh (TTD / DV360 live API, on CLW load) ──────
  // One server-side job re-resolves EVERY audience row against the live
  // platform API (deduped terms), overlays the result onto
  // edited_parsed_response.json, and persists it permanently. The frontend
  // auto-runs it once per campaign; the manual "Refresh audiences" button
  // forces a fresh run. Non-blocking — the grid stays interactive.
  const bulkRefreshStartedRef = useRef(false);

  // Poll the shared GET endpoint until the backend's GCS job lands. Timeout
  // scales with the term count (each unique term is one live API search):
  // ~4 attempts/term (6s), floored at 60 (~90s) and capped at 800 (~20min).
  // TTD pulls can be slow under rate limiting even with the handler's bounded
  // parallelism, and the job keeps running server-side, so we wait generously
  // before giving up.
  const pollBulkAudienceRefresh = useCallback((runId, termCount) => {
    let attempts = 0;
    const MAX_ATTEMPTS = Math.min(800, Math.max(60, Math.ceil((termCount || 0) * 4)));
    const tick = async () => {
      attempts += 1;
      try {
        const res = await api.get(
          getApiUrl(`/api/v2/campaigns/${campaignId}/audiences/refresh/${runId}`),
          { params: { accountId, userId } },
        );
        const data = res.data || {};
        if (data.status === 'running') {
          if (attempts >= MAX_ATTEMPTS) {
            setBulkAudienceRefresh({ status: 'error', error: 'Refresh timed out' });
            return;
          }
          setTimeout(tick, 1500);
          return;
        }
        if (data.status === 'error') {
          setBulkAudienceRefresh({ status: 'error', error: data.error || 'Refresh failed' });
          return;
        }
        if (data.status === 'done') {
          // Swap in the server's overlaid copy. saveReviewEdits always re-reads
          // the latest edited_parsed_response.json on its next save, so an
          // in-flight manual edit won't conflict.
          if (data.launcherResult) {
            setLauncherResult(data.launcherResult);
            setReviewRows(buildReviewRows(data.launcherResult));
          }
          const matches = data.matches || {};
          // Terms whose lookup FAILED are absent from `matches` (they keep
          // their existing audience) — so the requested count, not the size of
          // the response, is the denominator the user needs to see.
          const total = termCount || Object.keys(matches).length;
          const matched = Object.values(matches).filter(Boolean).length;
          setAudiencesBulkRefreshed(true); // server wrote the permanent sentinel
          setBulkAudienceRefresh({ status: 'done', matched, total });
        }
      } catch (e) {
        setBulkAudienceRefresh({ status: 'error', error: e?.response?.data?.error || e.message || 'Refresh failed' });
      }
    };
    setTimeout(tick, 1500);
  }, [campaignId, accountId, userId]);

  // Kick off the bulk refresh. `force` re-runs even if already started this
  // mount (wired to the manual "Refresh audiences" button).
  const startBulkAudienceRefresh = useCallback(async ({ force = false } = {}) => {
    if (!campaignId || !launcherResult) return;
    const platform = String(launcherResult.platform || launcherResult.Platform || '').toLowerCase();
    if (platform !== 'ttd' && platform !== 'dv360') return;
    if (!force && bulkRefreshStartedRef.current) return;
    bulkRefreshStartedRef.current = true;
    setBulkAudienceRefresh({ status: 'running' });
    try {
      const res = await api.post(
        getApiUrl(`/api/v2/campaigns/${campaignId}/audiences/refresh-all`),
        // userId is required by extractUser middleware (router.use'd at the
        // top of routes/v2/campaigns.js); reads from body or query.
        { accountId, userId, platform, force },
      );
      const data = res.data || {};
      if (data.status === 'noop') {
        // No audience terms anywhere — nothing to refresh, no banner.
        setBulkAudienceRefresh({ status: 'idle' });
        setAudiencesBulkRefreshed(true);
        return;
      }
      if (!data.runId) throw new Error('Server did not return a runId');
      pollBulkAudienceRefresh(data.runId, data.termCount);
    } catch (e) {
      console.error('[audience-refresh-all] POST failed', e?.response?.data || e.message);
      setBulkAudienceRefresh({ status: 'error', error: e?.response?.data?.error || e.message || 'Could not start refresh' });
    }
  }, [campaignId, accountId, userId, launcherResult, pollBulkAudienceRefresh]);

  // Auto-run once on load: a not-yet-launched TTD/DV360 campaign whose one-time
  // bulk refresh hasn't run yet (permanent server sentinel gates re-runs).
  useEffect(() => {
    if (alreadyLaunched) return;
    if (audiencesBulkRefreshed) return;
    if (!launcherResult) return;
    if (bulkRefreshStartedRef.current) return;
    const platform = String(launcherResult.platform || launcherResult.Platform || '').toLowerCase();
    if (platform !== 'ttd' && platform !== 'dv360') return;
    startBulkAudienceRefresh();
  }, [alreadyLaunched, audiencesBulkRefreshed, launcherResult, startBulkAudienceRefresh]);

  // ─── Delivered performance (launched campaigns only) ──────
  // The review pane is plan-shaped: its tiles are the impression goal, the plan
  // budget and the parsed audience count. Opened on a launched campaign — which
  // is every campaign reached from the Campaign Grid — those read '—' while the
  // grid row the user just clicked was full of numbers. Pull the same cached
  // figures so the two surfaces agree. Best-effort: a campaign the grid has
  // never pulled simply has no strip.
  useEffect(() => {
    if (!campaignId || !alreadyLaunched || !accountId) {
      setDeliveredMetrics(null);
      setPlatformAudiences(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(
          getApiUrl(`/api/v2/campaign-grid/${campaignId}/delivered-metrics`),
          { params: { accountId, userId } },
        );
        if (!cancelled) {
          setDeliveredMetrics(res.data?.metrics || null);
          setPlatformAudiences(Array.isArray(res.data?.audiences) ? res.data.audiences : null);
        }
      } catch (e) {
        // Never block the review pane on the perf cache.
        if (!cancelled) {
          setDeliveredMetrics(null);
          setPlatformAudiences(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [campaignId, alreadyLaunched, accountId, userId]);

  // ─── Save edits ──────────────────────────────────────────
  // Two backends behind one frontend save action:
  //  - DV360/TTD: PUT /v2/campaigns/:id/planning-review with the edited
  //    launcherResult JSON. Server persists the JSON verbatim.
  //  - GT:        PUT /v2/campaigns/:id/gt-review-edits with the row-keyed
  //    edits + deletions. Server applies them to the parsed config JSON,
  //    conditionally re-runs the CTS validator, and writes a versioned
  //    appliedConfigGcsPath that the launch flow picks up.
  // Both paths refresh the local reviewRows + clear pendingDeletions on
  // success, so the UI state matches the persisted source-of-truth.
  // One-step undo for an AUTO-saved batch. Both save paths are whole-document
  // PUTs, so "undo" is just re-saving the document as it was immediately before
  // — no inverse-operation logic, and it works identically for scalar edits, list
  // ops and row add/deletes.
  //
  // Only auto-saves record one. A manual Save is the user's own deliberate act
  // and they still have version history for it; an auto-save happened because
  // they clicked Confirm on an agent proposal, which is exactly the case where
  // "wait, not that" needs to be one click away.
  const [undoEntry, setUndoEntry] = useState(null); // { kind, snapshot, label, count, ts, previewId }
  const [undoing, setUndoing] = useState(false);
  // GT IO "Remove Creatives" in flight (handleRemoveCreatives). Every other
  // plan write waits for it: one landing meanwhile would either be refused as
  // stale or carry the bundle back in.
  const [removingCreatives, setRemovingCreatives] = useState(false);

  // GroundTruth bulk editor: proposals whose auto-save failed. Their edits stay
  // pending in the grid, so the next successful save commits them and their
  // cards move to "Applied and saved"; a version revert throws them away.
  // Refs + a stable callback because the saves below are declared before the
  // bulk agent that reports the outcome.
  const failedAutoSaveIdsRef = useRef(new Set());
  const reportProposalOutcomeRef = useRef(null); // wired to the bulk agent further down
  const settleFailedAutoSaves = useCallback((committed) => {
    const ids = [...failedAutoSaveIdsRef.current];
    failedAutoSaveIdsRef.current.clear();
    ids.forEach((id) => (committed
      ? reportProposalOutcomeRef.current?.(id, 'saved')
      // Still "Save failed", but no longer "press Save to retry": there is
      // nothing pending to save any more.
      : reportProposalOutcomeRef.current?.(id, 'save_failed', 'Not saved — the pending changes were discarded.')));
    return committed ? ids : [];
  }, []);

  // ─── Launched-campaign save ─────────────────────────────────
  // Once a campaign is live the plan-template PUT is refused server-side and
  // wouldn't touch the live platform anyway, so pending grid edits are folded
  // into batched edit-campaign requests — one per edited row, each a free-form
  // description the Python editor reasons over (same path the optimizer's
  // "apply suggestion" uses). Row add/duplicate/delete ops have no live-edit
  // equivalent and are refused rather than silently dropped.
  const saveLaunchedEdits = useCallback(async () => {
    if (pendingRowOps.length > 0) {
      setLaunchError('Row add / delete / duplicate changes can\'t be applied to a launched campaign — remove them before saving.');
      return false;
    }
    if (!platformCampaignId) {
      setLaunchError('This campaign has no platform campaign id — edits to a launched campaign need it to reach the DSP.');
      return false;
    }
    const requests = buildLaunchedEditRequests({
      editedRows, pendingListOps, reviewRows, destination,
    });
    if (requests.length === 0) return false;

    setSavingReview(true);
    setLaunchError(null);
    try {
      // ONE batch edit-campaign request: the structured ops ride the chat
      // metadata config_payload (bypassing the LLM analyzer), and the Python
      // campaign editor applies each edit and saves an aggregated result.
      const result = await dispatchLaunchedEdits({
        api, getApiUrl, campaignId, accountId, userId, platformCampaignId, requests, destination,
      });
      if (!result.ok) {
        setLaunchError(result.error || 'Edit dispatch failed');
        return false;
      }
      settleFailedAutoSaves(true);
      resetEdits();
      return true;
    } finally {
      setSavingReview(false);
    }
  }, [campaignId, accountId, userId, platformCampaignId, destination, editedRows, pendingListOps, pendingRowOps, reviewRows, resetEdits, settleFailedAutoSaves]);

  // ─── GT IO review: Upload Creatives ──────────────────────
  // Sends the client CTS workbook (or a ZIP with its assets) plus the plan as
  // the user sees it — unsaved edits included — to the gt-api worker, which
  // parses it with the CTS-to-GT readers and, when nothing blocks, writes a
  // NEW config revision. A rejected, failed or stale upload changes nothing
  // here: the current review, edits and revision all stay as they were.
  const [creativeUploadState, setCreativeUploadState] = useState({
    busy: false, phase: null, percent: 0, note: null, error: null, errors: [], stale: false,
  });
  // The grid stays editable while an upload processes. These refs let the
  // commit tell the edits it sent (now folded into the new revision) from ones
  // made afterwards.
  const liveEditsRef = useRef({ editedRows, pendingRowOps, pendingListOps });
  liveEditsRef.current = { editedRows, pendingRowOps, pendingListOps };
  const handleCreativeUploadFile = useCallback(async (file) => {
    if (!file || !ctsReview?.result || !campaignId) return;
    const lower = (file.name || '').toLowerCase();
    if (!lower.endsWith('.xlsx') && !lower.endsWith('.zip')) {
      setCreativeUploadState({ busy: false, phase: null, percent: 0, note: null, errors: [], stale: false,
        error: `${file.name} is not supported — upload a CTS workbook (.xlsx) or a .zip with the workbook and its creative assets.` });
      return;
    }
    if (file.size > CREATIVE_UPLOAD_MAX_BYTES) {
      setCreativeUploadState({ busy: false, phase: null, percent: 0, note: null, errors: [], stale: false,
        error: `${file.name} is larger than ${CREATIVE_UPLOAD_MAX_BYTES / 1048576} MB. Split the assets or host them and reference their URLs.` });
      return;
    }
    setCreativeUploadState({ busy: true, phase: 'uploading', percent: 0, note: null, error: null, errors: [], stale: false });
    const sent = { editedRows, pendingRowOps, pendingListOps };
    try {
      const current = applyGtConfigEdits(ctsReview.result, editedRows, pendingRowOps, pendingListOps);
      const baseRevision = appliedConfigGcsPath || ctsReview?.parsed_config_gcs_path || '';
      const fd = new FormData();
      fd.append('file', file);
      fd.append('editedConfig', JSON.stringify(current));
      fd.append('baseRevision', baseRevision);
      fd.append('platformAccountId', (advertiserId || '').trim());
      // Identity rides the query string: extractUser runs before multer parses the body.
      const res = await api.post(
        getApiUrl(`/api/v2/campaigns/${campaignId}/creatives/upload?accountId=${encodeURIComponent(accountId)}&userId=${encodeURIComponent(userId)}`),
        fd,
        {
          onUploadProgress: (evt) => {
            const total = evt?.total || file.size || 1;
            setCreativeUploadState((prev) => ({ ...prev, percent: Math.min(100, Math.round(((evt?.loaded || 0) * 100) / total)) }));
          },
        },
      );
      const requestId = res.data?.requestId;
      if (!requestId) throw new Error('Creative upload returned no request id');
      setCreativeUploadState((prev) => ({ ...prev, phase: 'processing', percent: 100 }));
      const envelope = await pollIoCtsResult(requestId, Date.now() + 30 * 60 * 1000);
      if (unmountedRef.current) return;
      if (!envelope) throw new Error('Creative processing did not finish within 30 minutes. Nothing was changed.');
      if (envelope.upload_status === 'committed' && envelope.result) {
        // The new revision already contains the unsaved edits that were sent,
        // so they are no longer pending. Cell edits made WHILE it processed are
        // kept on top (the upload never adds or removes rows, so their row keys
        // still point at the same ad groups — and being later, they win). Row
        // or list operations made meanwhile cannot be replayed safely onto the
        // new revision, so they are dropped, and the note says so.
        const { laterCells, structuralChanged, droppedCells } = editsSurvivingUpload(sent, liveEditsRef.current);
        setCtsReview(envelope);
        setReviewRows(buildGtReviewRows(envelope));
        resetEdits();
        // A one-step undo would re-save the plan from before this upload.
        setUndoEntry(null);
        if (Object.keys(laterCells).length) setEditedRows(laterCells);
        setCtsFindingsStale(false);
        if (envelope.new_config_gcs_path) setAppliedConfigGcsPath(envelope.new_config_gcs_path);
        // The worker recorded this revision as the campaign's head on the CTS
        // snapshot (like every other writer), so refresh / another device
        // resume onto it; track the head so the same-tab check agrees.
        if (envelope.head_at) setConfigHeadAt(envelope.head_at);
        const c = envelope.creative_upload?.counts || {};
        setCreativeUploadState({
          busy: false, phase: null, percent: 0, error: null, errors: [], stale: false,
          note: `Imported ${c.imported || 0} item${c.imported === 1 ? '' : 's'}; ${c.assigned || 0} size slot${c.assigned === 1 ? '' : 's'} assigned`
            + `${c.unmatched ? `, ${c.unmatched} unmatched` : ''}. Saved as a new revision.`
            + (Object.keys(laterCells).length ? ' Cell edits made while it processed are still pending.' : '')
            + (droppedCells ? ` ${droppedCells} cell edit${droppedCells === 1 ? '' : 's'} made while it processed could not be kept — the saved rows were renumbered; make ${droppedCells === 1 ? 'it' : 'them'} again.` : '')
            + (structuralChanged ? ' Rows or lists changed while it processed were not included — make those changes again.' : ''),
        });
      } else if (envelope.upload_status === 'stale') {
        setCreativeUploadState({ busy: false, phase: null, percent: 0, note: null, errors: [], stale: true,
          error: 'This configuration was changed since you loaded it.' });
      } else {
        const blocking = (envelope.creative_upload?.issues || []).filter((i) => i.severity === 'blocking');
        setCreativeUploadState({
          busy: false, phase: null, percent: 0, note: null, stale: false,
          error: blocking.length ? null : ((envelope.errors || [])[0] || 'The upload could not be processed. Nothing was changed.'),
          errors: blocking,
        });
      }
    } catch (e) {
      if (unmountedRef.current) return;
      const stale = e?.response?.status === 409 && e?.response?.data?.stale;
      setCreativeUploadState({ busy: false, phase: null, percent: 0, note: null, errors: [], stale: Boolean(stale),
        error: stale ? 'This configuration was changed since you loaded it.'
          : (e?.response?.data?.error || e.message || 'Creative upload failed. Nothing was changed.') });
    }
  }, [campaignId, accountId, userId, advertiserId, ctsReview, editedRows, pendingRowOps, pendingListOps,
    appliedConfigGcsPath, pollIoCtsResult, resetEdits, setEditedRows]);

  // After a stale-revision conflict: load the campaign's active revision so
  // the next upload builds on it. Pending edits were made against the old
  // revision, so they are dropped; findings are marked stale until Re-check.
  const reloadActiveRevision = useCallback(async () => {
    if (!campaignId) return;
    try {
      const res = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}/gt-config-head`), {
        params: { accountId, userId },
      });
      const { config, activeRevision, headAt } = res.data || {};
      if (!config) throw new Error('No active revision was returned');
      // Drop the envelope's own creative report: it describes the superseded
      // revision, and the panel would read it before the reloaded config's.
      const next = { ...ctsReview, creative_upload: undefined, result: config };
      setCtsReview(next);
      setReviewRows(buildGtReviewRows(next));
      resetEdits();
      setAppliedConfigGcsPath(toGcsKey(activeRevision));
      setConfigHeadAt(headAt || null);
      setCtsFindingsStale(true);
      setCreativeUploadState({ busy: false, phase: null, percent: 0, errors: [], stale: false, error: null,
        note: 'Reloaded the current revision. Press Re-check to refresh findings, then upload again.' });
    } catch (e) {
      setCreativeUploadState((prev) => ({ ...prev,
        error: `Could not reload the current revision: ${e?.response?.data?.error || e.message}` }));
    }
  }, [campaignId, accountId, userId, ctsReview, resetEdits]);

  // ─── GT IO review: Remove Creatives ──────────────────────
  // Takes the uploaded creative bundle back out of the plan. The backend
  // (POST /creatives/remove) undoes exactly what the upload changed and saves
  // the result as a revision labelled in History, where the upload itself is
  // listed too — so either one can be reverted from there. Unsaved edits are
  // saved with it, as with an upload; cell edits made while it runs stay pending.
  const handleRemoveCreatives = useCallback(async () => {
    const meta = ctsReview?.result?._creative_upload;
    if (!meta || !campaignId || removingCreatives || creativeUploadState.busy) return;
    const file = meta.source_filename || null;
    const pending = Object.keys(editedRows).length + Object.keys(pendingListOps).length + pendingRowOps.length;
    if (!window.confirm(`Remove the uploaded creatives${file ? ` (${file})` : ''} from this plan?\n\n`
      + 'Their creatives, tags, trackers and pixels, and the placement IDs they assigned, are taken out and the '
      + 'plan is saved as a new version. You can bring them back from History.'
      + (pending ? ` Your ${pending} unsaved edit${pending === 1 ? ' is' : 's are'} saved with it.` : ''))) return;
    setRemovingCreatives(true);
    const sent = { editedRows, pendingRowOps, pendingListOps };
    // Agent batches whose own auto-save failed are pending in the grid and ride
    // this save; ones that fail while it runs stay pending.
    const committingFailed = pending > 0 ? [...failedAutoSaveIdsRef.current] : [];
    try {
      const current = applyGtConfigEdits(ctsReview.result, editedRows, pendingRowOps, pendingListOps);
      const res = await api.post(getApiUrl(`/api/v2/campaigns/${campaignId}/creatives/remove`), {
        accountId,
        userId,
        editedConfig: current,
        baseRevision: appliedConfigGcsPath || ctsReview?.parsed_config_gcs_path || '',
      });
      if (unmountedRef.current) return;
      const data = res.data || {};
      if (!data.config) throw new Error('Remove Creatives returned no plan');
      const { laterCells, structuralChanged, droppedCells } = editsSurvivingUpload(sent, liveEditsRef.current);
      if (data.ctsReview && !gtCtsFailure(data.ctsReview)) {
        setCtsReview(data.ctsReview);
        setReviewRows(buildGtReviewRows(data.ctsReview));
        setCtsFindingsStale(false);
      } else {
        // Saved, but re-validation did not finish: show the plan without the
        // bundle (and without the upload's report), the prior findings stale.
        const next = { ...ctsReview, creative_upload: undefined, result: data.config };
        setCtsReview(next);
        setReviewRows(buildGtReviewRows(next));
        setCtsFindingsStale(
          (Array.isArray(next.errors) ? next.errors.length : 0)
          + (Array.isArray(next.warnings) ? next.warnings.length : 0)
          + (Array.isArray(next.infos) ? next.infos.length : 0) > 0,
        );
      }
      if (data.appliedConfigGcsPath) setAppliedConfigGcsPath(data.appliedConfigGcsPath);
      if (data.headAt) setConfigHeadAt(data.headAt);
      committingFailed.forEach((id) => {
        failedAutoSaveIdsRef.current.delete(id);
        reportProposalOutcomeRef.current?.(id, 'saved');
      });
      resetEdits();
      // A one-step undo would re-save the plan from before this removal.
      setUndoEntry(null);
      if (Object.keys(laterCells).length) setEditedRows(laterCells);
      const kept = data.removed?.keptFields || 0;
      setCreativeUploadState({
        busy: false, phase: null, percent: 0, error: null, errors: [], stale: false,
        note: `Removed the creatives uploaded${file ? ` from ${file}` : ''}. Saved as a new version — History can restore them.`
          + (kept ? ` ${kept} placement field${kept === 1 ? '' : 's'} changed after the upload ${kept === 1 ? 'was' : 'were'} kept.` : '')
          + (Object.keys(laterCells).length ? ' Cell edits made while it ran are still pending.' : '')
          + (droppedCells ? ` ${droppedCells} cell edit${droppedCells === 1 ? '' : 's'} made while it ran could not be kept — the saved rows were renumbered; make ${droppedCells === 1 ? 'it' : 'them'} again.` : '')
          + (structuralChanged ? ' Rows or lists changed while it ran were not included — make those changes again.' : ''),
      });
    } catch (e) {
      if (unmountedRef.current) return;
      const stale = e?.response?.status === 409 && e?.response?.data?.stale;
      setCreativeUploadState({
        busy: false, phase: null, percent: 0, note: null, errors: [], stale: Boolean(stale),
        error: stale ? 'This configuration was changed since you loaded it.'
          : `Could not remove the creatives: ${e?.response?.data?.error || e.message || 'unknown error'}`,
      });
    } finally {
      if (!unmountedRef.current) setRemovingCreatives(false);
    }
  }, [campaignId, accountId, userId, ctsReview, editedRows, pendingRowOps, pendingListOps, appliedConfigGcsPath,
    removingCreatives, creativeUploadState.busy, resetEdits, setEditedRows]);

  const saveReviewEdits = useCallback(async (opts = {}) => {
    // The Save button passes it a click event, so only an explicit object with
    // captureUndo can arm the undo affordance.
    const captureUndo = opts?.captureUndo === true;
    const undoLabel = typeof opts?.undoLabel === 'string' ? opts.undoLabel : null;
    // The GroundTruth bulk-editor proposal this save's undo snapshot belongs
    // to, so an Undo can mark THAT card Undone — and no other save's.
    const undoPreviewId = typeof opts?.undoPreviewId === 'string' ? opts.undoPreviewId : null;
    // Failed auto-saves still pending in the grid ride this save — and its
    // Undo reverts them too, so the undo entry names every proposal it covers.
    const committingFailed = [...failedAutoSaveIdsRef.current];
    // "Re-check" — run the validator against the config as it stands, with no
    // pending edits required. Without it the findings panel is unreachable
    // once you have nothing left to save: the findings only ever refresh as a
    // side effect of a save, Save is disabled at zero pending edits, and
    // Approve & launch is disabled while an error stands. A user looking at a
    // finding they have already fixed had no control left to press.
    const revalidateOnly = opts?.revalidateOnly === true;
    // GT IO instruction blockers the reviewer explicitly acknowledges ("keep the
    // plan's current values"). Recorded in the config's instruction metadata and
    // committed through the same save + re-validation as any edit, so the
    // worker — not this component — decides the blocker is cleared.
    const instructionAcks = Array.isArray(opts?.instructionAcks) ? opts.instructionAcks : [];
    const pendingCount = Object.keys(editedRows).length
      + Object.keys(pendingListOps).length + pendingRowOps.length;
    if (removingCreatives) {
      setLaunchError('Removing the uploaded creatives — save again once it finishes.');
      return false;
    }
    if (!campaignId
      || (!revalidateOnly
        && Object.keys(editedRows).length === 0
        && Object.keys(pendingListOps).length === 0
        && pendingRowOps.length === 0)) return false;
    // Launched campaigns: the plan is already on the DSP, so persist through
    // batched edit-campaign requests rather than re-writing the plan template.
    if (alreadyLaunched) {
      // There is nothing to re-check on a launched plan — the CTS validator
      // gates creation, not a live campaign — and routing an empty batch into
      // the launched-edit path would fire edit-campaign requests for no edits.
      if (revalidateOnly) return false;
      return saveLaunchedEdits();
    }
    const isGt = Boolean(ctsReview);
    // Re-check is the GT validator's affordance. DV360 / TTD have no
    // equivalent, and falling through would PUT an unchanged plan.
    if (revalidateOnly && !isGt) return false;
    if (isGt) {
      setSavingReview(true);
      try {
        // Build the edited config client-side (applyGtConfigEdits deep-clones
        // and maps row keys → adgroup properties) so the backend stays
        // platform-agnostic about row IDs and only persists what it's given.
        const editedConfig = applyGtConfigEdits(ctsReview.result, editedRows, pendingRowOps, pendingListOps);
        if (instructionAcks.length && editedConfig._instruction_enrichment) {
          const at = new Date().toISOString();
          const prior = Array.isArray(editedConfig._instruction_enrichment.acknowledgements)
            ? editedConfig._instruction_enrichment.acknowledgements : [];
          editedConfig._instruction_enrichment = {
            ...editedConfig._instruction_enrichment,
            acknowledgements: [
              ...prior.filter((a) => !instructionAcks.includes(a.issue_id)),
              ...instructionAcks.map((id) => ({ issue_id: id, acknowledged_by: userId || null, acknowledged_at: at })),
            ],
          };
        }
        const gtDropped = droppedGtEdits(editedConfig);
        const gtWarns = gtEditWarnings(editedConfig);
        setSaveWarnings([
          ...gtDropped.map((d) => ({ message: d.reason })),
          ...gtWarns.map((w) => ({ message: `${w.rowId}: ${w.reason}` })),
        ]);
        // Errors and warnings only — deliberately NOT infos, even though the
        // badge counts those too. GTSemanticValidator appends an unconditional
        // "Detected CTS version" info on every run
        // (gt_semantic_validator.py:145), so infos.length is >= 1 for every
        // envelope it has ever produced: counting them here would make this
        // true always, and every save would then wait on the Pub/Sub validator
        // round trip. An info-only plan instead keeps its findings, gets them
        // labelled as predating the save, and Re-check is one click away.
        const preEditIssueCount = (Array.isArray(ctsReview?.errors) ? ctsReview.errors.length : 0)
          + (Array.isArray(ctsReview?.warnings) ? ctsReview.warnings.length : 0);
        const res = await api.put(getApiUrl(`/api/v2/campaigns/${campaignId}/gt-review-edits`), {
          accountId, userId,
          editedConfig,
          revalidate: revalidateOnly || preEditIssueCount > 0,
        });
        const data = res.data || {};
        // A revalidation that failed or timed out comes back as a TRUTHY
        // envelope with no `result` — ctsReviewService returns a stub rather
        // than throwing (ctsReviewService.js:151-158), and the worker writes
        // the same shape on any validator exception (gt_api_handler.py:503-510).
        // Installing it would blank the grid and the campaign card on a save
        // that actually succeeded. Keep the plan, surface the reason.
        const revalidateFailure = data.ctsReview ? gtCtsFailure(data.ctsReview) : null;
        if (revalidateFailure) {
          setSaveWarnings((prev) => [...prev, {
            message: `Saved, but the validator did not finish: ${revalidateFailure.message} `
              + 'The findings below are from before this save — press Re-check to run it again.',
          }]);
        }
        if (data.ctsReview && !revalidateFailure) {
          setCtsReview(data.ctsReview);
          setReviewRows(buildGtReviewRows(data.ctsReview));
          setCtsFindingsStale(false);
        } else {
          // No revalidation — refresh rows from the locally-edited config
          // so the grid reflects the user's changes immediately.
          //
          // This produces a MIXED envelope: the new plan carrying the previous
          // run's findings. That is the least-bad option (dropping them would
          // hide real blockers), but it must not be presented as current — a
          // finding quoting a date the plan no longer holds is what made this
          // panel look unfixable. Flag it so the issues bar can say so and
          // offer Re-check.
          const localCts = { ...ctsReview, result: data.config || editedConfig };
          setCtsReview(localCts);
          setReviewRows(buildGtReviewRows(localCts));
          setCtsFindingsStale(
            (Array.isArray(localCts.errors) ? localCts.errors.length : 0)
            + (Array.isArray(localCts.warnings) ? localCts.warnings.length : 0)
            + (Array.isArray(localCts.infos) ? localCts.infos.length : 0) > 0,
          );
        }
        if (data.appliedConfigGcsPath) setAppliedConfigGcsPath(data.appliedConfigGcsPath);
        if (data.headAt) setConfigHeadAt(data.headAt);
        // Snapshot is the config as it was BEFORE applyGtConfigEdits, read here
        // from the still-unmodified ctsReview this callback closed over.
        if (captureUndo) {
          setUndoEntry({
            kind: 'gt',
            snapshot: ctsReview.result,
            label: undoLabel,
            count: pendingCount,
            ts: Date.now(),
            previewId: undoPreviewId,
            previewIds: [undoPreviewId, ...(pendingCount > 0 ? committingFailed : [])].filter(Boolean),
          });
        }
        // A GroundTruth batch whose auto-save failed was still pending — this
        // save committed it.
        if (pendingCount > 0) settleFailedAutoSaves(true);
        resetEdits();
        return true;
      } catch (e) {
        console.error('save gt-review-edits failed', e);
        setLaunchError(e?.response?.data?.error || e.message || 'Save failed');
        return false;
      } finally {
        setSavingReview(false);
      }
    }
    if (!launcherResult) return false;
    setSavingReview(true);
    setSaveWarnings([]);
    try {
      // Overlay scalar edits + pending list ops (adds / edits / removes)
      // onto a clone of the launcher result. Rows whose underlying entity
      // can't be resolved are left alone, so we still PUT the original
      // rather than dropping edits silently.
      const edited = applyLauncherResultEdits(launcherResult, editedRows, pendingListOps, reviewRows, pendingRowOps);
      const res = await api.put(getApiUrl(`/api/v2/campaigns/${campaignId}/planning-review`), {
        accountId, userId,
        editedLauncherResult: edited,
      });
      setLauncherResult(edited);
      setReviewRows(buildReviewRows(edited));
      if (captureUndo) {
        setUndoEntry({
          kind: 'plan',
          snapshot: launcherResult,
          label: undoLabel,
          count: pendingCount,
          ts: Date.now(),
          previewId: undoPreviewId,
          previewIds: [undoPreviewId, ...(pendingCount > 0 ? committingFailed : [])].filter(Boolean),
        });
      }
      if (pendingCount > 0) settleFailedAutoSaves(true);
      resetEdits();
      // The server's structural sanity pass. The save already succeeded — these
      // are things that would bite at LAUNCH time (a past TTD start date, a bid
      // the platform will clamp), surfaced now while the grid is still open.
      const warnings = res?.data?.warnings;
      if (Array.isArray(warnings) && warnings.length) setSaveWarnings(warnings);
      return true;
    } catch (e) {
      console.error('save planning-review failed', e);
      // A 400 carrying `blockers` is the sanity pass refusing a plan with
      // dangling entity references — spell them out rather than showing the
      // bare summary line.
      // Keep them as a LIST. Joining with a space produced one run-on
      // paragraph — three sentences about three different campaigns reading as
      // a single thought — which is exactly what the notices strip exists to
      // avoid. PlanNotices renders one row per blocker.
      const blockers = e?.response?.data?.blockers;
      if (Array.isArray(blockers) && blockers.length) {
        setLaunchBlockers(blockers);
        setLaunchError(null);
      } else {
        setLaunchBlockers([]);
        setLaunchError(e?.response?.data?.error || e.message || 'Save failed');
      }
      return false;
    } finally {
      setSavingReview(false);
    }
  }, [campaignId, launcherResult, editedRows, pendingListOps, pendingRowOps, reviewRows, accountId, userId, ctsReview, resetEdits, alreadyLaunched, saveLaunchedEdits, settleFailedAutoSaves, removingCreatives]);

  /**
   * Undo the last auto-saved batch by re-saving the snapshot taken just before
   * it. Rides the same PUT as an ordinary save, so the undo is itself a tracked
   * version and is itself reversible from the version history.
   *
   * Refuses while there are unsaved edits: those were made AFTER the batch, and
   * re-PUTing the old document would silently discard them. Better to say so than
   * to quietly throw away work the user can see on screen.
   */
  // Resolves true once the revert landed, false otherwise (the GroundTruth bulk
  // editor marks the undone proposal's card from it).
  const revertUndo = useCallback(async () => {
    if (!undoEntry || !campaignId || removingCreatives) return false;
    if (Object.keys(editedRows).length > 0
      || Object.keys(pendingListOps).length > 0
      || pendingRowOps.length > 0) {
      setLaunchError('Undo skipped — you have unsaved edits that would be lost. Save or discard them first.');
      setLaunchBlockers([]);
      return false;
    }
    setUndoing(true);
    setLaunchError(null);
    setLaunchBlockers([]);
    try {
      if (undoEntry.kind === 'gt') {
        const res = await api.put(getApiUrl(`/api/v2/campaigns/${campaignId}/gt-review-edits`), {
          accountId, userId,
          editedConfig: undoEntry.snapshot,
          revalidate: true,
        });
        const data = res.data || {};
        // Same result-less-envelope guard as the save path.
        if (data.ctsReview && !gtCtsFailure(data.ctsReview)) {
          setCtsReview(data.ctsReview);
          setReviewRows(buildGtReviewRows(data.ctsReview));
          setCtsFindingsStale(false);
        } else {
          // Same mixed envelope as the save path — new plan, previous run's
          // findings. Flag it rather than letting them read as current.
          const localCts = { ...ctsReview, result: undoEntry.snapshot };
          setCtsReview(localCts);
          setReviewRows(buildGtReviewRows(localCts));
          setCtsFindingsStale(
            (Array.isArray(localCts.errors) ? localCts.errors.length : 0)
            + (Array.isArray(localCts.warnings) ? localCts.warnings.length : 0)
            + (Array.isArray(localCts.infos) ? localCts.infos.length : 0) > 0,
          );
        }
        if (data.appliedConfigGcsPath) setAppliedConfigGcsPath(data.appliedConfigGcsPath);
        if (data.headAt) setConfigHeadAt(data.headAt);
      } else {
        await api.put(getApiUrl(`/api/v2/campaigns/${campaignId}/planning-review`), {
          accountId, userId,
          editedLauncherResult: undoEntry.snapshot,
        });
        setLauncherResult(undoEntry.snapshot);
        setReviewRows(buildReviewRows(undoEntry.snapshot));
      }
      setUndoEntry(null);
      return true;
    } catch (e) {
      console.error('undo failed', e);
      setLaunchError(e?.response?.data?.error || e.message || 'Undo failed');
      return false;
    } finally {
      setUndoing(false);
    }
  }, [
    undoEntry, campaignId, accountId, userId, ctsReview,
    editedRows, pendingListOps, pendingRowOps, removingCreatives,
  ]);

  // ─── Platform lookups (TTD geo / audiences, GT taxonomy) ───────────
  // Backs the cell detail panel's async picker: picker-backed adds need a
  // resolved platform id, so the typed term is searched server-side and the
  // user picks a real entity. All endpoints are read-only, campaign-scoped,
  // and GT taxonomy reads come from the account cache, never a live GT call.
  const runLookup = useCallback(async (kind, query, ctx = {}) => {
    const path = LOOKUP_PATHS[kind];
    if (!campaignId || !path || !query) return [];
    try {
      const params = { accountId, userId, q: query };
      // The picker's user-selected type scope wins; otherwise the field's
      // descriptor (or row context, for Drive-To) decides.
      const types = ctx.types || gtLookupTypesFor(ctx.field, ctx.row);
      if (Array.isArray(types) && types.length) params.types = types.join(',');
      const res = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}/lookup/${path}`), {
        params,
      });
      return Array.isArray(res?.data?.results) ? res.data.results : [];
    } catch (e) {
      throw new Error(e?.response?.data?.error || e.message || 'Lookup failed');
    }
  }, [campaignId, accountId, userId]);

  // On-demand GT taxonomy cache warmup: campaigns that were never processed
  // through the launcher have no cached taxonomy file, so the picker 404s
  // ("No GT taxonomy cache…"). This asks app-backend to have
  // campaign-services fetch the taxonomy live and cache it to GCS; the picker
  // re-runs its current search once it resolves.
  const refreshGtLookup = useCallback(async () => {
    if (!campaignId) throw new Error('No campaign to refresh');
    const res = await api.post(
      getApiUrl(`/api/v2/campaigns/${campaignId}/lookup/gt-taxonomy/refresh`),
      { accountId, userId },
    );
    const data = res?.data || {};
    if (data.success !== true) throw new Error(data.error || 'GT taxonomy fetch failed');
  }, [campaignId, accountId, userId]);

  // ─── GT config version history + revert ──────────────────
  // Each saveReviewEdits write to GCS is a tracked version (the writes
  // themselves ARE the history). The list endpoint enumerates the
  // campaigns/{id}/gt-config-revisions/ prefix; revert loads the chosen
  // version, writes it as a fresh save (so the revert is itself reversible),
  // and re-runs CTS validation. The frontend then refreshes the grid and
  // updates appliedConfigGcsPath so launch reads from the reverted state.
  const fetchGtConfigVersions = useCallback(async () => {
    if (!campaignId) return [];
    const res = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}/gt-config-versions`), {
      params: { accountId, userId },
    });
    return Array.isArray(res.data?.versions) ? res.data.versions : [];
  }, [campaignId, accountId, userId]);

  const revertGtConfig = useCallback(async (gcsPath) => {
    if (!campaignId || !gcsPath) return;
    if (removingCreatives) throw new Error('Removing the uploaded creatives — try again once it finishes.');
    const res = await api.post(
      getApiUrl(`/api/v2/campaigns/${campaignId}/gt-config-versions/revert`),
      { accountId, userId, gcsPath },
    );
    const data = res.data || {};
    if (data.ctsReview && !gtCtsFailure(data.ctsReview)) {
      setCtsReview(data.ctsReview);
      setReviewRows(buildGtReviewRows(data.ctsReview));
      setCtsFindingsStale(false);
    } else if (data.config && typeof data.config === 'object') {
      // The revert landed but its revalidation did not: show the reverted
      // plan anyway (the grid used to keep showing the pre-revert one while
      // launch read the reverted file), with the prior findings flagged. The
      // envelope's creative-upload report describes the plan reverted FROM.
      const next = { ...(ctsReview || {}), creative_upload: undefined, result: data.config };
      setCtsReview(next);
      setReviewRows(buildGtReviewRows(next));
      setCtsFindingsStale(
        (Array.isArray(next.errors) ? next.errors.length : 0)
        + (Array.isArray(next.warnings) ? next.warnings.length : 0)
        + (Array.isArray(next.infos) ? next.infos.length : 0) > 0,
      );
    }
    if (data.appliedConfigGcsPath) setAppliedConfigGcsPath(data.appliedConfigGcsPath);
    if (data.headAt) setConfigHeadAt(data.headAt);
    settleFailedAutoSaves(false);
    resetEdits();
    // An earlier upload / removal note describes a plan this one replaced, and
    // a one-step undo would re-save the plan as it was before that batch.
    setCreativeUploadState({ busy: false, phase: null, percent: 0, note: null, error: null, errors: [], stale: false });
    setUndoEntry(null);
  }, [campaignId, accountId, userId, resetEdits, ctsReview, settleFailedAutoSaves, removingCreatives]);

  // DV360/TTD version history — analog of GT but reads/writes the
  // launcher_result + revisions/ tree the planning-review PUT already
  // produces. Revert overwrites edited_parsed_response.json and also
  // writes a fresh revision so the action is itself reversible.
  const fetchConfigVersions = useCallback(async () => {
    if (!campaignId) return [];
    const res = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}/config-versions`), {
      params: { accountId, userId },
    });
    return Array.isArray(res.data?.versions) ? res.data.versions : [];
  }, [campaignId, accountId, userId]);

  const revertConfig = useCallback(async (gcsPath) => {
    if (!campaignId || !gcsPath) return;
    const res = await api.post(
      getApiUrl(`/api/v2/campaigns/${campaignId}/config-versions/revert`),
      { accountId, userId, gcsPath },
    );
    const data = res.data || {};
    if (data.launcherResult) {
      setLauncherResult(data.launcherResult);
      setReviewRows(buildReviewRows(data.launcherResult));
    }
    settleFailedAutoSaves(false);
    resetEdits();
  }, [campaignId, accountId, userId, resetEdits, settleFailedAutoSaves]);

  // ─── Approve & launch — GroundTruth ──────────────────────
  // After the user reviews the review-cts summary, this creates the campaign
  // record and dispatches a `gt-api` single-message orchestration (existing
  // POST /api/v2/campaigns path, lines ~1023-1164 of routes/v2/campaigns.js).
  // That backend route creates a chat + message_type='gt-api' record via v1
  // chat API, which triggers the gt-api PubSub topic with
  // sub_action='create-campaign'. The user lands on /campaigns/<id> where the
  // existing CampaignActivity page polls and renders the gt-api result.
  const launchGroundTruthCampaign = useCallback(async () => {
    if (isLocalGtReviewOnly('groundtruth')) {
      setLaunchError(GT_REVIEW_ONLY_MESSAGE);
      return;
    }
    // Excel: gtExcelFile.gcsPath is the original .xlsx upload, used as
    // launch input when there are no committed edits.
    // PDF:   gtPdfFile is the original .pdf upload; the PDF flow seeds
    //        appliedConfigGcsPath from the review handler's
    //        parsed_config_gcs_path, so the launch ALWAYS goes through
    //        the JSON path (PDF is never re-parsed during launch).
    // Resolve the source the same way the review pane's download button
    // does (gtPlanFile || ctsSourceFile): gtExcelFile/gtPdfFile derive from
    // planFiles, which only exists in the uploading tab — on a cts-snapshot
    // resume (other device, or expired sessionStorage) only ctsSourceFile
    // is populated, and reading just the planFiles-derived state made this
    // guard fail and the launch button a silent no-op.
    const sourceFile = gtExcelFile || gtPdfFile || gtZipFile || ctsSourceFile;
    const useJson = Boolean(appliedConfigGcsPath);
    // The JSON path launches from appliedConfigGcsPath and only needs the
    // source file for the display-name stem, so don't block on it there.
    if (!useJson && !sourceFile?.gcsPath) {
      setLaunchError('No source file available — please re-upload the Excel/PDF.');
      return;
    }
    setLaunchError(null);
    setLaunchBlockers([]);
    setLaunching(true);
    // Flush any pending manual edits first so the launch reads the latest
    // appliedConfigGcsPath. saveReviewEdits is idempotent when there's
    // nothing pending and updates appliedConfigGcsPath inline.
    if (Object.keys(editedRows).length > 0 || Object.keys(pendingListOps).length > 0 || pendingRowOps.length > 0) {
      await saveReviewEdits();
    }
    // Three modes, ranked by source-of-truth preference:
    //   1. appliedConfigGcsPath (.json) — agent committed edits or
    //      (for PDF) the seeded parsed_config_gcs_path. gt-api routes to
    //      create-campaign-from-json based on the .json extension.
    //   2. gtPdfFile.gcsPath (.pdf) — defensive fallback. The PDF review
    //      should have already seeded appliedConfigGcsPath; if it didn't,
    //      we'd hit create-campaign-from-pdf (still supported in chat.js
    //      as a fallback). In practice this branch shouldn't fire.
    //   3. gtExcelFile.gcsPath (.xlsx) — no edits applied. Original
    //      upload, parsed by GTExcelParser in the launcher.
    // Derive the PDF-ness from the resolved file's name, not from gtPdfFile —
    // on resume the source comes from ctsSourceFile and gtPdfFile is null.
    const isPdfSource = /\.pdf$/i.test(sourceFile?.originalName || sourceFile?.gcsPath || '');
    // The PDF fallback re-converts the IO from scratch, without the reviewed
    // instructions, edits, acknowledgements or uploaded creatives — and skips
    // the instruction and creative launch gates. Never take it for a review
    // that applied instructions or carries uploaded creatives. A CTS Excel
    // re-parsed from the workbook loses applied instructions the same way.
    const appliedInstructions = ctsReview?.instruction_enrichment || ctsReview?.result?._instruction_enrichment;
    if (!useJson && (isPdfSource
      ? (appliedInstructions || ctsReview?.result?._creative_upload)
      : (appliedInstructions && appliedInstructions.status !== 'no_instructions'))) {
      setLaunching(false);
      setLaunchError('The reviewed plan could not be located, so it was not launched. '
        + 'Refresh the page to reload the review, then approve again.');
      return;
    }
    const launchPath = appliedConfigGcsPath || sourceFile.gcsPath;
    const stem = (sourceFile?.originalName || 'gt-campaign').replace(/\.(xlsx|xls|xlsm|pdf|zip)$/i, '');
    const launchName = useJson ? `${stem}.edited.json` : sourceFile.originalName;
    const launchType = useJson ? 'application/json' : sourceFile.type;
    // The CTS-review draft row rides the create as replacesCampaignId: the
    // server exempts it from the name-conflict check (draft and launch share
    // the name on purpose) and retires it AFTER a successful insert. Deleting
    // it client-side first meant a failed launch destroyed the user's
    // resumable draft on top of failing (UAT 0819: an empty campaign name
    // 400'd the POST after the draft was already gone).
    const draftIdToDelete = campaignId;
    // The campaign row name. Hydration fills campaignName on resume; if it is
    // still empty (pre-fix drafts, exotic resume paths), fall back to the CTS
    // campaign name, then the source-file stem — never POST '' and dead-end.
    const launchCampaignName = campaignName.trim()
      || String(ctsReview?.result?.campaign?.campaign_name || '').trim()
      || stem;
    try {
      const payload = {
        name: launchCampaignName,
        replacesCampaignId: draftIdToDelete || undefined,
        folderId,
        destination: 'groundtruth',
        platformAccountId: advertiserId.trim(),
        accountId,
        userId,
        files: [{
          originalName: launchName,
          gcsPath: launchPath,
          publicUrl: useJson ? null : sourceFile.publicUrl,
          size: useJson ? null : sourceFile.size,
          type: launchType,
        }],
        orchestration: {
          enabled: true,
          type: 'gt-api',
          priority: 'normal',
          autoStart: true,
          metadata: {
            source: 'campaign-launch-workflow',
            ctsReviewed: true,
            ctsRequestId: ctsReview?.request_id,
            inputMode: useJson ? (isPdfSource ? 'pdf_config_json' : 'config_json') : (isPdfSource ? 'pdf' : 'excel'),
          },
        },
      };
      const res = await api.post(getApiUrl('/api/v2/campaigns'), payload);
      const created = res.data || {};
      const id = created.id;
      if (!id) throw new Error('GroundTruth campaign creation returned no id');
      setCampaignId(id);
      setLaunchSuccess(true);
      // Brief pause for user feedback, then jump to the campaign page where
      // the gt-api message status polling renders.
      setTimeout(() => navigate(`/campaigns/${id}`), 1000);
    } catch (e) {
      console.error('GT launch failed', e);
      setLaunchError(e?.response?.data?.error || e.message || 'Launch failed');
    } finally {
      setLaunching(false);
    }
  }, [gtExcelFile, gtPdfFile, gtZipFile, ctsSourceFile, appliedConfigGcsPath, campaignName, folderId, advertiserId, accountId, userId, ctsReview, navigate, editedRows, pendingListOps, saveReviewEdits, campaignId]);

  // ─── Approve & launch ────────────────────────────────────
  const approveAndLaunch = useCallback(async () => {
    if (!campaignId) return;
    setLaunchError(null);
    setLaunchBlockers([]);
    setLaunching(true);
    try {
      // Persist any pending edits first — scalar edits AND pending list ops
      // (the GT launch path below checks both; this one used to silently
      // drop list changes staged without a scalar edit).
      if (Object.keys(editedRows).length > 0 || Object.keys(pendingListOps).length > 0 || pendingRowOps.length > 0) {
        if (!(await saveReviewEdits())) return;
      }
      await api.post(getApiUrl(`/api/v2/campaigns/${campaignId}/planning-launch`), {
        accountId, userId,
        metadata: { source: 'campaign-launch-workflow' },
      });
      if (planningGridForAllRoles) {
        setLaunchProgress('processing');
        setProcError(null);
        setOrchStatus('processing');
        setPane('processing');
      } else {
        setLaunchSuccess(true);
        setTimeout(() => navigate(`/campaigns/${campaignId}`), 1200);
      }
    } catch (e) {
      console.error('planning-launch failed', e);
      setLaunchError(e?.response?.data?.error || e.message || 'Launch failed');
    } finally {
      setLaunching(false);
    }
  }, [campaignId, editedRows, pendingListOps, accountId, userId, saveReviewEdits, navigate, planningGridForAllRoles]);

  // ─── render ─────────────────────────────────────────────
  return (
    <div className="clw">
      <div className="clw-main">
        {/* UPLOAD PANE */}
        <div className={`clw-pane upload ${pane === 'upload' ? 'active' : ''}`}>
          <div className="clw-upload-card">
            <h2>Campaign <em>Launch</em> Workflow</h2>
            <div className="clw-uc-sub">
              Upload your media plan, drop in creative assets (optional), and add some context about
              targeting and goals. Sidekick handles the rest in the background — you'll land straight on
              the campaign grid, ready to review and approve.
            </div>

            {/* Step 1: Plan */}
            <div className="clw-upload-step">
              <div className="clw-us-header">
                <div className={`clw-us-num ${planDone ? 'done' : ''}`}>{planDone ? '✓' : '1'}</div>
                <div className="clw-us-title">
                  {isGroundTruth
                    ? 'Upload GroundTruth Excel media plan, insertion-order PDF, or ZIP bundle'
                    : isMmgy
                      ? (mmgyPlanOnly ? 'Upload the MMGY Excel media plan' : 'Upload MMGY insertion order PDF + Excel proposal')
                      // The template half of this heading is conditional now: it
                      // is only part of the ask when the account's
                      // launch-template-required flag covers this platform.
                      : templateRequired
                        ? 'Upload media plan + launch template'
                        : 'Upload media plan'}
                </div>
              </div>
              <input
                ref={planInputRef}
                type="file"
                multiple={!isGroundTruth}
                style={{ display: 'none' }}
                accept={isGroundTruth
                  ? '.xlsx,.xls,.xlsm,.pdf,.zip'
                  : isMmgy
                    ? '.pdf,.xlsx,.xls,.xlsm'
                    : '.xlsx,.xls,.xlsm,.csv,.pdf,.json'}
                onChange={(e) => handlePlanFiles(Array.from(e.target.files || []))}
              />
              <div
                className={`clw-dropzone ${planDone ? 'done' : ''}`}
                onClick={() => planInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); handlePlanFiles(Array.from(e.dataTransfer.files || [])); }}
              >
                <div className="clw-dz-icon">{planDone ? '✓' : '📄'}</div>
                <div className="clw-dz-text">
                  {planFiles.length === 0 && (<>
                    <strong>
                      {isGroundTruth
                        ? 'Drop a GroundTruth Excel media plan, insertion-order PDF, or ZIP bundle'
                        : isMmgy
                          ? (mmgyPlanOnly ? 'Drop the Excel media plan here' : 'Drop the MMGY insertion order PDF + Excel proposal here')
                          : templateRequired
                            ? 'Drop your media plan + launch template here'
                            : 'Drop your media plan here'}
                    </strong>
                    <span>
                      {isGroundTruth
                        ? '.xlsx — Sidekick runs GT CTS validation before launch · .pdf — gt-api runs the full PDF → IO → launch pipeline · .zip — gt-api unzips the bundle and launches the inner Excel'
                        : isMmgy
                          ? (mmgyPlanOnly
                            ? 'Plan only — the Excel (.xlsx) is the whole insertion order, no PDF needed'
                            : 'PDF for the insertion order · one Excel for the proposal')
                          : templateRequired
                            ? 'Excel (.xlsx), CSV or PDF for the plan · JSON for the launch template'
                            : 'Excel (.xlsx), CSV or PDF for the plan · a launch template JSON is optional — one is picked automatically'}
                    </span>
                  </>)}
                  {planFiles.length > 0 && (<>
                    <strong>{planFiles.map((f) => f.originalName).join(' · ')}</strong>
                    <span>
                      {planFiles.length} file{planFiles.length === 1 ? '' : 's'} uploaded
                      {!isGroundTruth && !planDone
                        ? (isMmgy
                          ? (mmgyPlanOnly ? ' · Excel media plan still needed' : ' · insertion order PDF still needed')
                          : ' · plan file still needed')
                        : ''}
                      {/* launchTemplateDone is already true when this account
                          doesn't require a template for this platform, so this
                          only ever fires on an account that genuinely does. */}
                      {!isGroundTruth && !isOpenAI && planDone && !launchTemplateDone
                        ? (isMmgy ? ' · Excel proposal still needed' : ' · launch template required for this platform')
                        : ''}
                    </span>
                  </>)}
                </div>
                <div className="clw-dz-badge">
                  <button
                    className="clw-btn clw-btn-secondary clw-btn-sm"
                    type="button"
                    onClick={(e) => { e.stopPropagation(); planInputRef.current?.click(); }}
                  >Browse</button>
                </div>
              </div>
            </div>

            {/* Step 2 — Upload creative assets (optional): hidden from the
                form per UX request. Sidekick's auto-match flow still works
                via the chat upload path, so keeping the code rather than
                deleting it preserves the wiring for re-enable.
            {!isGroundTruth && (
            <div className="clw-upload-step">
              <div className="clw-us-header">
                <div className={`clw-us-num ${creativeFiles.length > 0 ? 'done' : 'muted'}`}>
                  {creativeFiles.length > 0 ? '✓' : '2'}
                </div>
                <div className={`clw-us-title ${planDone ? '' : 'muted'}`}>
                  Upload creative assets <span className="optional">(optional)</span>
                </div>
              </div>
              <input
                ref={creativeInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.html,.zip,.mp4,.mov,.mp3,.wav"
                onChange={(e) => handleCreativeFiles(Array.from(e.target.files || []))}
              />
              <div
                className={`clw-dropzone ${creativeFiles.length > 0 ? 'done' : ''} ${planDone ? '' : 'locked'}`}
                onClick={() => planDone && creativeInputRef.current?.click()}
                onDragOver={(e) => { if (planDone) e.preventDefault(); }}
                onDrop={(e) => { if (!planDone) return; e.preventDefault(); handleCreativeFiles(Array.from(e.dataTransfer.files || [])); }}
              >
                <div className="clw-dz-icon">{creativeFiles.length > 0 ? '✓' : '🎨'}</div>
                <div className="clw-dz-text">
                  {creativeFiles.length === 0 && (<>
                    <strong>Drop all assets here — Sidekick matches them automatically</strong>
                    <span>JPG, PNG, HTML5, MP4, MOV, MP3 · Drag your whole creative folder</span>
                  </>)}
                  {creativeFiles.length > 0 && (<>
                    <strong>{creativeFiles.length} creative asset{creativeFiles.length === 1 ? '' : 's'} uploaded</strong>
                    <span>{creativeFiles.slice(0, 3).map((f) => f.originalName).join(', ')}{creativeFiles.length > 3 ? ` and ${creativeFiles.length - 3} more` : ''}</span>
                  </>)}
                </div>
                <div className="clw-dz-badge">
                  <button
                    className="clw-btn clw-btn-secondary clw-btn-sm"
                    type="button"
                    disabled={!planDone}
                    onClick={(e) => { e.stopPropagation(); creativeInputRef.current?.click(); }}
                  >Browse</button>
                </div>
              </div>
            </div>
            )}
            */}

            {/* Step 2: Campaign details (required for backend). Was step 3
                until the creatives block above was hidden — the form now
                jumps straight from plan upload to details. */}
            <div className="clw-upload-step">
              <div className="clw-us-header">
                <div className={`clw-us-num ${detailsDone ? 'done' : ''}`}>
                  {detailsDone ? '✓' : '2'}
                </div>
                <div className="clw-us-title">Campaign details</div>
              </div>
              <div className="clw-details full">
                <div className="clw-field">
                  <label>Campaign name</label>
                  <input
                    type="text"
                    value={campaignName}
                    placeholder="Auto-derived from the media plan filename"
                    onChange={(e) => setCampaignName(e.target.value)}
                  />
                </div>
                <div className="clw-field">
                  <label>Folder</label>
                  {creatingFolder ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                      <input
                        autoFocus
                        type="text"
                        placeholder="New folder name"
                        value={newFolderName}
                        onChange={(e) => { setNewFolderName(e.target.value); setCreateFolderError(null); }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); handleCreateFolder(); }
                          else if (e.key === 'Escape') { e.preventDefault(); setCreatingFolder(false); setNewFolderName(''); setCreateFolderError(null); }
                        }}
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        className="clw-btn clw-btn-primary clw-btn-sm"
                        onClick={handleCreateFolder}
                        disabled={creatingFolderBusy || !newFolderName.trim()}
                      >
                        {creatingFolderBusy ? '…' : 'Create'}
                      </button>
                      <button
                        type="button"
                        className="clw-btn clw-btn-secondary clw-btn-sm"
                        onClick={() => { setCreatingFolder(false); setNewFolderName(''); setCreateFolderError(null); }}
                        disabled={creatingFolderBusy}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                      <select value={folderId} onChange={(e) => setFolderId(e.target.value)} style={{ flex: 1 }}>
                        {foldersLoading && <option value="">Loading folders…</option>}
                        {!foldersLoading && folders.length === 0 && <option value="">No folders — create one →</option>}
                        {!foldersLoading && folders.map((f) => (
                          <option key={f.id} value={f.id}>{getFolderDisplayLabel(f)}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="clw-btn clw-btn-secondary clw-btn-sm"
                        onClick={() => setCreatingFolder(true)}
                        title="Create a new folder"
                      >
                        + New
                      </button>
                    </div>
                  )}
                  {createFolderError && (
                    <div className="help" style={{ color: 'var(--red)' }}>{createFolderError}</div>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
                  <div className="clw-field">
                    <label>Destination</label>
                    <select value={destination} onChange={(e) => { setDestination(e.target.value); setSelectedChannelId(''); }}>
                      <option value="">
                        {channelsLoading
                          ? 'Loading channels…'
                          : availableDestinations.length === 0
                            ? 'No channels enabled for this account'
                            : 'Pick a platform…'}
                      </option>
                      {availableDestinations.map((d) => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                      ))}
                    </select>
                  </div>
                  {requiresPlatformId ? (
                    <div className="clw-field">
                      <label>{
                        destination === 'groundtruth' ? 'Platform account ID'
                        : destination === 'reddit' ? 'Profile ID'
                        // Facebook's primary id is the AD ACCOUNT — it is where
                        // the campaign is created, and campaigns.js stores it in
                        // advertiser_id (the Page, when one is needed, is a
                        // separate field below). This cell used to be labelled
                        // "Page ID" and sent as pageId, which left the ad account
                        // null on every Facebook campaign CLW created.
                        : destination === 'facebook' ? 'Ad account ID'
                        : destination === 'linkedin' ? 'Ad account ID'
                        : (destination === 'google-ads' || destination === 'google-ads-search' || destination === 'google-ads-pmax' || destination === 'google-ads-demandgen') ? 'Customer ID'
                        : 'Advertiser ID'
                      }</label>
                      <input
                        type="text"
                        value={advertiserId}
                        placeholder={isFacebook ? 'e.g. 123456789' : 'e.g. 1234567'}
                        onChange={(e) => setAdvertiserId(e.target.value)}
                      />
                      {isFacebook && (
                        <div className="help">Your Facebook Ad Account ID (digits only — no `act_` prefix)</div>
                      )}
                      {destination === 'groundtruth' && (
                        gtAccountIdInvalid
                          ? <div className="help" style={{ color: 'var(--red)' }}>That doesn't look like an account ID — it's a number like 275625, not a link.</div>
                          : <div className="help">The numeric GroundTruth account ID (e.g. 275625)</div>
                      )}
                    </div>
                  ) : (
                    <div className="clw-field">
                      <label>&nbsp;</label>
                      <div className="help" style={{ paddingTop: '6px' }}>
                        Auth via API key in channel config — no per-campaign ID required.
                      </div>
                    </div>
                  )}
                </div>
                {/* Page ID — Facebook OAuth channels only. A system-user-token
                    channel carries its Page in the channel config, so asking
                    for one there would be a field with nowhere to go. */}
                {isFacebookOAuthChannel && (
                  <div className="clw-field">
                    <label>Page ID <span style={{ color: '#d44' }}>*</span></label>
                    <input
                      type="text"
                      value={pageId}
                      placeholder="e.g. 1234567890"
                      onChange={(e) => setPageId(e.target.value)}
                    />
                    <div className="help">Your Facebook Page ID — required for OAuth channels</div>
                  </div>
                )}
                {/* Channel Configuration.
                    - 2+ configs for the account on this platform → required dropdown.
                    - Exactly 1 config → auto-selected silently (no UI), so the
                      created campaign still carries channel_id.
                    - 0 configs → inline error label + submit blocked, so the
                      user knows the account has no usable channel before they
                      finish the form. */}
                {needsChannelPick && (
                  <div className="clw-field">
                    <label>Channel Configuration <span style={{ color: '#d44' }}>*</span></label>
                    {channelConfigsLoading ? (
                      <div className="help">Loading channel configurations…</div>
                    ) : (
                      <select
                        value={selectedChannelId}
                        onChange={(e) => setSelectedChannelId(e.target.value)}
                        required
                      >
                        <option value="">Select a channel configuration</option>
                        {platformChannels.map((ch) => {
                          const adAcctId = ch.field_values?.ad_account_id;
                          const label = adAcctId ? `${ch.name} (${adAcctId})` : ch.name;
                          return <option key={ch.id} value={ch.id}>{label}</option>;
                        })}
                      </select>
                    )}
                    <div className="help">Multiple {destination} configurations are available — pick which one to use.</div>
                  </div>
                )}
                {hasNoChannel && (
                  <div className="clw-field">
                    <label style={{ color: '#d44' }}>Channel Configuration</label>
                    <div
                      style={{
                        color: '#d44',
                        background: '#fdecea',
                        border: '1px solid #f5c6c1',
                        padding: '8px 12px',
                        borderRadius: 4,
                        fontSize: 13,
                      }}
                    >
                      No {destination} channel configuration available on this account.
                      Ask an admin to add one before launching.
                    </div>
                  </div>
                )}
                {/* IO Type — MMGY-only. TravelDesk IOs skip MMGY-specific
                    processing in the backend, so the user picks which path
                    a given upload should take. Setting is gated per
                    destination by enable-mmgy-io-type. */}
                {showIoType && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
                    <div className="clw-field">
                      <label>IO Type</label>
                      <select value={ioType} onChange={(e) => setIoType(e.target.value)}>
                        <option value="mmgy">MMGY</option>
                        <option value="traveldesk">TravelDesk</option>
                        {mmgyPlanOnlyAvailable && (
                          <option value={MMGY_PLAN_ONLY_IO_TYPE}>Plan only (Excel)</option>
                        )}
                      </select>
                      <div className="help">{MMGY_IO_TYPE_HELP[ioType] || MMGY_IO_TYPE_HELP.mmgy}</div>
                    </div>
                  </div>
                )}
                {/* Additional Instructions — GroundTruth IO PDF and CTS Excel.
                    Sits in Campaign Details, above Process campaign, because it
                    is part of what that button dispatches. */}
                {showGtInstructions && (
                  <div className="clw-field" data-testid="gt-additional-instructions">
                    <label htmlFor="clw-gt-additional-instructions">
                      Additional Instructions <span className="optional">(optional)</span>
                      <span className={`clw-instr-count ${additionalInstructions.length > GT_INSTRUCTIONS_MAX ? 'over' : ''}`}>
                        {additionalInstructions.length}/{GT_INSTRUCTIONS_MAX}
                      </span>
                    </label>
                    <textarea
                      id="clw-gt-additional-instructions"
                      className="clw-instr-input"
                      rows={4}
                      maxLength={GT_INSTRUCTIONS_MAX}
                      value={additionalInstructions}
                      placeholder={'e.g.\nDemographics: Adults 25-54\nDuplicate package 1 to package 2 and 3. Redistribute package 1 budget to package 1,2,3\nAudience: Sports Fans for package 2'}
                      onChange={(e) => setAdditionalInstructions(e.target.value)}
                    />
                    <div className="help">
                      Anything to set up that isn't in the IO. These instructions — and any
                      “Additional Notes from AM” in the PDF or CTS — modify the generated campaign
                      configuration. Every change is listed for your review, and nothing is
                      created in GroundTruth until you approve and launch. Budgets and dates change
                      only when you state the exact new value and what it applies to.
                    </div>
                  </div>
                )}
                {gtInstructionsUnavailable && (
                  <div className="clw-field" data-testid="gt-additional-instructions-zip">
                    <div className="help">
                      Additional Instructions aren&apos;t available for a ZIP bundle — it launches
                      directly, without a review step to check them in. Upload the CTS Excel or
                      IO PDF to add instructions.
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Step 3 — Campaign context (optional): hidden from the form
                per UX request. Targeting / goals state still exists and is
                passed through to backend orchestration; leaving the markup
                here (commented) keeps the wiring obvious for re-enable.
            <div className="clw-upload-step">
              <div className="clw-us-header">
                <div className="clw-us-num muted">3</div>
                <div className="clw-us-title muted">
                  Campaign context <span className="optional">(optional)</span>
                </div>
              </div>
              <div className="clw-details full">
                <div className="clw-field">
                  <label>Targeting</label>
                  <textarea
                    rows={2}
                    placeholder="e.g. Adults 25–54, US only, in-market for home improvement — exclude existing customers"
                    value={targeting}
                    onChange={(e) => setTargeting(e.target.value)}
                  />
                </div>
                <div className="clw-field">
                  <label>Goals</label>
                  <textarea
                    rows={2}
                    placeholder="e.g. Drive brand awareness in Q2, secondary goal site traffic — target CPM $4.50, VCR 65%+"
                    value={goals}
                    onChange={(e) => setGoals(e.target.value)}
                  />
                  <div className="help">Sidekick uses this to validate that the parsed plan is consistent with your intent.</div>
                </div>
              </div>
            </div>
            */}

            {uploadError && (
              <div className="clw-validation" style={{ marginTop: 20 }}>
                <div className="clw-val-err">
                  <div className="title">Upload error</div>
                  <div className="desc">{uploadError}</div>
                </div>
              </div>
            )}

            <div className="clw-upload-footer">
              <div className={`clw-uf-note ${uploadError ? 'err' : ''}`}>{submitNote}</div>
              <button
                className="clw-btn clw-btn-primary"
                disabled={!canSubmit}
                onClick={startProcessing}
              >
                {uploading ? 'Uploading…' : submitting ? 'Starting…' : 'Process campaign →'}
              </button>
            </div>
          </div>
        </div>

        {/* PROCESSING PANE */}
        <div className={`clw-pane processing ${pane === 'processing' ? 'active' : ''}`}>
          <div className="clw-processing-card">
            {launchProgress ? (
              <div role="status" aria-live="polite">
                <div className="clw-pc-icon">{launchProgress === 'failed' ? '⚠️' : launchProgress === 'completed' ? '✓' : '⚙️'}</div>
                <div className="clw-pc-title">
                  {launchProgress === 'failed' ? 'Campaign launch failed'
                    : launchProgress === 'completed' ? 'Campaign launch completed'
                    : 'Launching your campaign…'}
                </div>
                <div className="clw-pc-sub">
                  {launchProgress === 'failed' ? procError
                    : launchProgress === 'completed' ? 'The platform has finished creating your campaign. Open the campaign to review the result.'
                    : 'Your approved plan has been submitted to the platform. We’re waiting for campaign creation to finish. This can take several minutes.'}
                </div>
                {summaryError && <div role="alert">{summaryError}</div>}
                {(launchProgress === 'completed' || launchProgress === 'failed') && (
                  <button className="clw-btn clw-btn-primary" disabled={summaryLoading} onClick={() => fetchPlanningReview(campaignId)}>
                    {summaryLoading ? 'Loading campaign…' : launchProgress === 'failed' ? 'Review campaign' : 'View campaign'}
                  </button>
                )}
              </div>
            ) : <>
            <div className={`clw-pc-icon ${procError ? 'error' : orchStatus === 'review' ? 'done' : ''}`}>
              {procError ? '⚠️' : '⚙️'}
            </div>
            <div className="clw-pc-title">
              {procError ? (campaignNameConflict ? 'Campaign name already in use' : 'Processing failed')
                : hydrating ? 'Restoring your campaign…'
                : isGroundTruth && gtHasPdfFile ? 'Launching GroundTruth campaign from PDF…'
                : isGroundTruth && gtHasZipFile ? 'Launching GroundTruth campaign from ZIP bundle…'
                : isGroundTruth ? 'Running GroundTruth CTS review…'
                : isMmgy ? 'Processing your MMGY campaign…'
                : 'Processing your campaign…'}
            </div>
            <div className="clw-pc-sub">
              {procError ? procError
                : hydrating ? 'Picking up where you left off.'
                : isGroundTruth && gtHasPdfFile ? 'Sidekick is running the gt-api PDF pipeline — parsing the insertion order, converting it to a campaign config with judge-validated LLMs, then launching ad groups + creatives directly into GroundTruth. This usually takes a few minutes.'
                : isGroundTruth && gtHasZipFile ? 'Sidekick is running the gt-api ZIP pipeline — unzipping the bundle, uploading the inner Excel to Drive, then launching ad groups + creatives directly into GroundTruth. This usually takes a few minutes.'
                : isGroundTruth ? 'Sidekick is validating the GroundTruth Excel against CTS semantic rules — checking the campaign config, adgroups, audiences, location lists, and creative placements. This usually takes a few seconds.'
                : isMmgy && mmgyPlanOnly ? 'Sit tight — Sidekick is building the campaign structure straight from the Excel media plan (no PDF step), then generating line items. This usually takes 5–15 minutes.'
                : isMmgy ? 'Sit tight — Sidekick is parsing the insertion order PDF, extracting line items, and building the campaign structure. MMGY plans usually take 10–20 minutes because of the per-line-item LLM extraction.'
                : 'Sit tight — parsing the plan, building the campaign structure, and validating assets. This usually takes under a minute.'}
            </div>
            <div className="clw-pc-prog-wrap">
              <div className="clw-pc-prog-fill" style={{ width: `${orchStatus === 'review' ? 100 : Math.max(5, procPercent)}%` }} />
            </div>
            <div className="clw-pc-steps">
              {(isGroundTruth && gtHasPdfFile ? [
                'Uploading PDF to GCS',
                'Dispatching to gt-api',
                'Converting PDF pages → images',
                'Parsing PDF → IO JSON',
                'Converting IO JSON → campaign config',
                'Creating campaign + ad groups in GT',
                'Assigning audiences + creatives',
                'Ready for review',
              ] : isGroundTruth && gtHasZipFile ? [
                'Uploading ZIP to GCS',
                'Dispatching to gt-api',
                'Unzipping bundle + extracting Excel',
                'Uploading Excel to Drive',
                'Parsing Excel media plan',
                'Creating campaign + ad groups in GT',
                'Assigning audiences + creatives',
                'Ready for review',
              ] : isGroundTruth ? [
                'Uploading Excel to GCS',
                'Dispatching CTS review to gt-api',
                'Loading & parsing Excel',
                'Validating campaign fields',
                'Validating adgroups & products',
                'Validating audiences & location lists',
                'Compiling review report',
                'Ready for review',
              ] : [
                'Uploading files to GCS',
                'Detecting workflow type',
                'Creating campaign',
                'Parsing media plan',
                'Matching creatives',
                'Building IO / ad-group structure',
                'Confidence-scoring fields',
                'Ready for review',
              ]).map((label, i) => {
                let cls = 'clw-pc-step';
                if (procError && i === procStepIdx) cls += ' error';
                else if (orchStatus === 'review' || i < procStepIdx) cls += ' done';
                else if (i === procStepIdx) cls += ' active';
                return (
                  <div className={cls} key={i}>
                    <div className="clw-pc-dot">{(orchStatus === 'review' || i < procStepIdx) ? '✓' : procError && i === procStepIdx ? '!' : ''}</div>
                    {procStepLabel && i === procStepIdx && orchStatus !== 'review' ? procStepLabel : label}
                  </div>
                );
              })}
            </div>
            {procError && !campaignId && (
              <div style={{ marginTop: 24, display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button className="clw-btn clw-btn-secondary" onClick={() => {
                  try { sessionStorage.removeItem(SS_KEY(campaignId)); } catch {}
                  setSearchParams({}, { replace: true });
                  setCampaignId(null);
                  setPane('upload');
                  setProcError(null);
                  setCampaignNameConflict(false);
                  setOrchStatus(null);
                  setProcStepIdx(0);
                }}>{campaignNameConflict ? 'Change name or folder' : '← Back to upload'}</button>
              </div>
            )}
            </>}
          </div>
        </div>


        {/* REVIEW PANE */}
        <ReviewPane
          active={pane === 'review'}
          pendingJump={pendingJump}
          clearPendingJump={() => setPendingJump(null)}
          campaignName={campaignName}
          exportSourceName={exportSourceName}
          destination={destination}
          launcherResult={launcherResult}
          ctsReview={ctsReview}
          gtExcelFile={gtExcelFile}
          platformAccountId={advertiserId}
          accountId={accountId}
          userId={userId}
          reviewRows={reviewRows}
          editedRows={editedRows}
          // GT IO (insertion-order PDF) reviews only — not CTS Excel / ZIP
          // reviews, which already carry their creatives — and never once launched.
          creativeUpload={isGroundTruth && ctsReview?.result && gtHasPdfFile && !alreadyLaunched ? {
            onFile: handleCreativeUploadFile,
            busy: creativeUploadState.busy,
            disabled: removingCreatives,
            label: creativeUploadState.phase === 'processing' ? 'Processing creatives…'
              : `Uploading creatives… ${creativeUploadState.percent || 0}%`,
          } : null}
          // Same reviews, while the plan carries an uploaded bundle.
          creativeRemove={isGroundTruth && ctsReview?.result?._creative_upload && gtHasPdfFile && !alreadyLaunched ? {
            onRemove: handleRemoveCreatives,
            busy: removingCreatives,
            disabled: creativeUploadState.busy,
          } : null}
          creativeUploadState={creativeUploadState}
          onReloadActiveRevision={reloadActiveRevision}
          appliedConfigGcsPath={appliedConfigGcsPath}
          onReviewCtsUpdated={(next, newConfigGcsPath, meta = {}) => {
            // The reviewer's SSE stream replays its recent events to a tab
            // that reconnects, so after a reload a commit can arrive that is
            // older than the head this tab restored (a later grid save).
            // The server already holds the newer head; don't regress to it.
            const committedAt = meta?.committedAt || null;
            if (committedAt && configHeadAt && committedAt <= configHeadAt) return;
            // Apply the agent's fresh validation to the parent state so
            // every dependent surface (grid, summary, cell panel) refreshes.
            setCtsReview(next);
            setReviewRows(buildGtReviewRows(next));
            // fix-by-name revalidates server-side, so this envelope's findings
            // match its result.
            setCtsFindingsStale(false);
            // Grid edits were applied by the fix — clear them. Staged
            // campaign-card edits were NOT part of the fix-by-name batch, so
            // they stay pending rather than being silently dropped.
            setEditedRows((prev) => (prev[GT_CAMPAIGN_ROW_ID]
              ? { [GT_CAMPAIGN_ROW_ID]: prev[GT_CAMPAIGN_ROW_ID] }
              : {}));
            // appliedConfigGcsPath is the versioned campaign_config JSON
            // that gt-api consumes at launch via create-campaign-from-json.
            if (newConfigGcsPath) setAppliedConfigGcsPath(newConfigGcsPath);
            // Both writers record the head server-side (fix-by-name returns
            // it; the agent commit's time bounds it from below).
            if (meta?.headAt || committedAt) setConfigHeadAt(meta.headAt || committedAt);
          }}
          activeGroup={activeGroup}
          setActiveGroup={setActiveGroup}
          gridSearch={gridSearch}
          setGridSearch={setGridSearch}
          filterIssuesOnly={filterIssuesOnly}
          setFilterIssuesOnly={setFilterIssuesOnly}
          issuesBarOpen={issuesBarOpen}
          setIssuesBarOpen={setIssuesBarOpen}
          editingCell={editingCell}
          setEditingCell={setEditingCell}
          editingDraft={editingDraft}
          setEditingDraft={setEditingDraft}
          commitCellEdit={commitCellEdit}
          cancelCellEdit={cancelCellEdit}
          getRowValue={getRowValue}
          detailRowId={detailRowId}
          setDetailRowId={setDetailRowId}
          detailCellField={detailCellField}
          setDetailCellField={setDetailCellField}
          aiOpen={aiOpen}
          setAiOpen={setAiOpen}
          savingReview={savingReview}
          ctsFindingsStale={ctsFindingsStale}
          saveReviewEdits={saveReviewEdits}
          // Auto-save + one-step undo for agent-confirmed batches (#3).
          undoEntry={undoEntry}
          revertUndo={revertUndo}
          undoing={undoing}
          dismissUndo={() => setUndoEntry(null)}
          // GroundTruth bulk editor: failed auto-saves the next save settles.
          failedAutoSaveIdsRef={failedAutoSaveIdsRef}
          reportProposalOutcomeRef={reportProposalOutcomeRef}
          saveWarnings={saveWarnings}
          dismissSaveWarnings={() => setSaveWarnings([])}
          onLookup={runLookup}
          onRefreshLookup={ctsReview ? refreshGtLookup : undefined}
          launching={launching}
          launchError={launchError}
          launchBlockers={launchBlockers}
          dismissLaunchError={() => { setLaunchError(null); setLaunchBlockers([]); }}
          launchSuccess={launchSuccess}
          alreadyLaunched={alreadyLaunched}
          pendingDeletions={pendingDeletions}
          toggleListDeletion={toggleListDeletion}
          pendingListOps={pendingListOps}
          addListItem={addListItem}
          editListItem={editListItem}
          // Bulk-editor agent staging: an approved batch is folded into the
          // SAME pending-edit state a manual cell edit writes, so it saves
          // through the identical applier + PUT path.
          setEditedRows={setEditedRows}
          setPendingListOps={setPendingListOps}
          setPendingRowOps={setPendingRowOps}
          pendingRowOpCount={pendingRowOpCount}
          bulkAudienceRefresh={bulkAudienceRefresh}
          onRefreshAudiences={() => startBulkAudienceRefresh({ force: true })}
          approveAndLaunch={ctsReview ? launchGroundTruthCampaign : approveAndLaunch}
          fetchGtConfigVersions={fetchGtConfigVersions}
          revertGtConfig={revertGtConfig}
          fetchConfigVersions={fetchConfigVersions}
          revertConfig={revertConfig}
          // Summary content (hero, tiles, config card, breakdown switcher,
          // QA pivot panel) is rendered inside ReviewPane above the grid —
          // these props pipe through the formerly-summary-only state.
          summaryError={summaryError}
          importWarnings={importWarnings}
          dismissImportWarnings={() => setImportWarnings([])}
          summaryOpen={summaryOpen}
          setSummaryOpen={setSummaryOpen}
          breakdownView={breakdownView}
          setBreakdownView={setBreakdownView}
          qaPivotKey={qaPivotKey}
          setQaPivotKey={setQaPivotKey}
          creativeFiles={creativeFiles}
          initialUrlCampaign={initialUrlCampaign}
          setPane={setPane}
          setCtsReview={setCtsReview}
          setPendingJump={setPendingJump}
          isMmgy={isMmgy}
          // Account eligibility for the QA reviewer agents. The backend is the
          // enforcement point (both reviewer session-create routes 403 an
          // ineligible account); these props exist so the sidebar never OFFERS a
          // tab that would fail.
          gtChannelEnabled={Boolean(enabledChannels?.groundtruth)}
          // The `optimize-campaign` gate for the Optimization tab — the same
          // setting the Optimize Campaign button reads.
          accountSettings={accountSettings}
          planFiles={planFiles}
          campaignId={campaignId}
          accountId={accountId}
          userId={userId}
          platformCampaignId={platformCampaignId}
          deliveredMetrics={deliveredMetrics}
          platformAudiences={platformAudiences}
          // Original GT IO upload (PDF/Excel/ZIP) for the header's
          // "Download IO file" button. Same-session: from planFiles
          // (gtPlanFile). Cross-device resume: from the cts-snapshot
          // (ctsSourceFile) — planFiles is empty there.
          sourceFile={gtPlanFile || ctsSourceFile}
          // Re-run the IO→CTS conversion for a review that came back empty
          // because it failed. Only reachable with a persisted source PDF.
          onRetryCtsReview={ctsSourceFile?.gcsPath ? retryIoCtsReview : null}
          onBack={() => setPane(initialUrlCampaign ? 'processing' : 'upload')}
        />
      </div>
    </div>
  );
}

// (CellScalarEditor + CellDetailPanel moved to ./CellDetailPanel.js so the
// Launch board's campaign details page renders the identical cell modals —
// audiences, geo, and every scalar editor. Edit state stays owned by the
// host component and is passed in via props.)

// ─── CONSOLIDATED REVIEW SUMMARY ─────────────────────────────
// The CLW review pane absorbed what used to be a separate summary pane.
// This component renders the full parse-summary content (stat tiles +
// campaign-configuration card + per-platform breakdown) inline above
// the review grid. The data shapes (summary, launcherResult, ctsReview)
// match what summarizeLauncherResult / summarizeReviewCtsResult /
// breakdownByPackage / breakdownBy / breakdownByMonth / extractCampaignConfig
// already produce — same fields the standalone summary pane used to
// consume, just re-rendered next to the grid so the reviewer doesn't
// have to bounce between two screens.
// Campaign-configuration card cell styles (shared by the read-only value,
// the free-text input and the select so an editable card doesn't jump).
const GT_CFG_LBL_STYLE = {
  fontSize: 10.5, color: 'var(--g4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4,
};
const GT_CFG_VAL_STYLE = { fontSize: 13, color: 'var(--g5)', fontFamily: 'var(--font-sans)' };
const GT_CFG_INPUT_STYLE = {
  ...GT_CFG_VAL_STYLE, width: '100%', boxSizing: 'border-box', border: '1px solid var(--g2)', borderRadius: 4, padding: '3px 6px', background: 'transparent',
};

/**
 * One campaign-configuration cell. Editable (text input or static-list
 * select per the field descriptor) when the host passes onEdit; plain value
 * otherwise. Selects match the stored value to an option exactly,
 * case-insensitively ("NO" → "No") or by leading code token (config stores
 * "IAB3-1", the option reads "IAB3-1 Advertising"). IAB writes back the BARE
 * code — gt_campaign_creator_v2 sends iab_content_category verbatim.
 */
function GtCampaignConfigCell({ field, saved, staged, onEdit }) {
  const hasStaged = staged !== undefined;
  const value = hasStaged ? staged : (saved ?? '');
  const editable = typeof onEdit === 'function' && field.editable;
  const stagedStyle = hasStaged ? { borderColor: 'var(--gold)' } : {};
  const writeVal = (opt) => (field.configKey === 'iab_category_resolved' ? opt.split(' ')[0] : opt);
  let control = null;
  if (!editable) {
    control = <div style={GT_CFG_VAL_STYLE}>{(value !== '' && value != null) ? String(value) : '—'}</div>;
  } else if (field.control === 'single-select') {
    const s = String(value ?? '').trim();
    const matched = field.options.find((o) => (
      o.toLowerCase() === s.toLowerCase() || o.split(' ')[0].toLowerCase() === s.toLowerCase()
    )) || null;
    control = (
      <select
        style={{ ...GT_CFG_INPUT_STYLE, ...stagedStyle }}
        value={matched ? writeVal(matched) : s}
        onChange={(e) => onEdit(field.configKey, e.target.value)}
        title={hasStaged ? 'Unsaved edit' : undefined}
      >
        {/* A blank / unrecognised stored value keeps its own entry so
            rendering never silently rewrites the config — only a pick does. */}
        {!matched && <option value={s}>{s || '—'}</option>}
        {field.options.map((o) => <option key={o} value={writeVal(o)}>{o}</option>)}
      </select>
    );
  } else {
    control = (
      <input
        // 'date' renders the native date input (campaign flight cells);
        // everything else stays free text.
        type={field.control === 'date' ? 'date' : 'text'}
        style={{ ...GT_CFG_INPUT_STYLE, ...stagedStyle }}
        value={value == null ? '' : String(value)}
        placeholder="—"
        onChange={(e) => onEdit(field.configKey, e.target.value)}
        title={hasStaged ? 'Unsaved edit' : undefined}
      />
    );
  }
  return (
    <div style={{ padding: '14px 22px', borderBottom: '1px solid var(--g2)', borderRight: '1px solid var(--g2)' }}>
      <div style={GT_CFG_LBL_STYLE}>{field.label}</div>
      {control}
    </div>
  );
}

export function ConsolidatedReviewSummary({
  isGt, summary, summaryError, launcherResult, ctsReview, reviewRows,
  creativeFiles, breakdownView, setBreakdownView, qaPivotKey, setQaPivotKey,
  destination, campaignName, blocking, okCount, qCount, mCount, totalImps,
  // Campaign-configuration card editing (GT only). campaignEdits is the
  // staged { campaignKey: value } map; onCampaignEdit stages one. Hosts that
  // pass neither (LaunchCampaignDetails, launched campaigns) get the
  // read-only card.
  campaignEdits, onCampaignEdit,
  // The GT platform account id the user entered in the Start Campaign
  // workflow (UAT 0817). The config's own account_id is null by design (a
  // launch-time input), so the card shows this instead.
  platformAccountId,
  // The campaign's cached grid row (delivered metrics + platform setup). Fills
  // the plan tiles for an already-launched campaign whose payload carries no
  // budget / flight — see platformSetupSummary.js.
  platformSetup,
  // Audience names from the platform, for the tile the plan can't fill.
  platformAudiences,
  // 'tiles'   → hero + stat tiles + campaign-configuration card
  // 'pivot'   → breakdown switcher / GT ad-groups table + QA pivot panel
  // omitted  → render everything (legacy behaviour, only used by tests).
  slot,
}) {
  const slotIs = (k) => slot == null || slot === k;
  if (summaryError) {
    return (
      <div className="clw-ps-wrap">
        <div className="clw-ps-hero">
          <div className="clw-ps-hero-icon" style={{ background: 'var(--cell-m)', color: 'var(--red)' }}>!</div>
          <div className="clw-ps-hero-body">
            <div className="clw-ps-hero-title">Could not load parse summary</div>
            <div className="clw-ps-hero-sub">{summaryError}</div>
          </div>
        </div>
      </div>
    );
  }

  // GroundTruth branch: ctsReview-driven summary block.
  if (isGt && ctsReview) {
    const r = ctsReview.result || {};
    const s = summary;
    return (
      <div className="clw-ps-wrap">
        {slotIs('tiles') && (
        <>
        {/* Stat tiles (formerly summary pane). 6 tiles vs the prior
            4-tile summary-strip — adds creatives, location lists, etc. */}
        <div className="clw-ps-stat-grid">
          <div className="clw-ps-stat highlight">
            <div className="clw-ps-stat-val">{fmtInt(s?.adGroupCount)}</div>
            <div className="clw-ps-stat-lbl">Ad groups parsed</div>
            <div className="clw-ps-stat-sub">From GroundTruth Excel</div>
          </div>
          <div className="clw-ps-stat">
            <div className="clw-ps-stat-val">{s?.totalBudget != null ? fmtCurrency(s.totalBudget) : '—'}</div>
            <div className="clw-ps-stat-lbl">IO budget</div>
            <div className="clw-ps-stat-sub">{s?.flightStart && s?.flightEnd ? `${s.flightStart} → ${s.flightEnd}` : 'Flight TBC'}</div>
          </div>
          <div className="clw-ps-stat">
            <div className="clw-ps-stat-val">{fmtInt(s?.creativeCount)}</div>
            <div className="clw-ps-stat-lbl">Creatives</div>
            <div className="clw-ps-stat-sub">{s?.tagCount ? `${fmtInt(s.tagCount)} DCM tag${s.tagCount === 1 ? '' : 's'}` : 'No tags'}</div>
          </div>
          <div className="clw-ps-stat">
            <div className="clw-ps-stat-val">{fmtInt(s?.audienceCount)}</div>
            <div className="clw-ps-stat-lbl">Audiences</div>
            <div className="clw-ps-stat-sub">Custom + 3rd-party</div>
          </div>
          <div className="clw-ps-stat">
            <div className="clw-ps-stat-val">{fmtInt(s?.locationCount)}</div>
            <div className="clw-ps-stat-lbl">Location lists</div>
            <div className="clw-ps-stat-sub">GroundTruth POI groups</div>
          </div>
          <div className="clw-ps-stat">
            <div className="clw-ps-stat-val" style={s?.issueCount ? { color: blocking ? 'var(--red)' : 'var(--gold)' } : undefined}>{fmtInt(s?.issueCount ?? 0)}</div>
            <div className="clw-ps-stat-lbl">Validation flags</div>
            <div className="clw-ps-stat-sub">{s?.issueCount ? `${s.errorCount} error · ${s.warningCount} warning` : 'No required fields missing'}</div>
          </div>
        </div>

        {/* Campaign configuration card — descriptor-driven: the fields the
            Fields sheet marks "Add to Summary" (kpi_1/kpi_2 were removed per
            the same sheet), editable when the host stages campaign edits.
            The Flight dates come through the same projection (real storage on
            campaign.start_date/end_date — see GT_CAMPAIGN_FLIGHT_FIELDS for
            why they are not in the generated sheet); Platform account is a
            launch-time input and stays read-only. */}
        <div className="clw-ps-breakdown">
          <div className="clw-ps-break-head">Campaign configuration</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0 }}>
            {gtCampaignSummaryFields().map((f) => (
              <GtCampaignConfigCell
                key={f.key}
                field={f}
                saved={r.campaign?.[f.configKey]}
                staged={campaignEdits && Object.prototype.hasOwnProperty.call(campaignEdits, f.configKey)
                  ? campaignEdits[f.configKey] : undefined}
                onEdit={onCampaignEdit}
              />
            ))}
            {[
              // Start Campaign's Platform Account ID — the config's own
              // account_id is a launch-time input and stays null (UAT 0817).
              // '#' prefixes only a real numeric id; legacy junk (a pasted
              // URL, UAT 0818) renders as-is rather than as "#https://…".
              {
                l: 'Platform account',
                v: (() => {
                  const acct = String(platformAccountId || s?.advertiserId || '').trim();
                  return acct ? (/^\d+$/.test(acct) ? `#${acct}` : acct) : '—';
                })(),
              },
            ].map((cfg) => (
              <div key={cfg.l} style={{ padding: '14px 22px', borderBottom: '1px solid var(--g2)', borderRight: '1px solid var(--g2)', minWidth: 0 }}>
                <div style={GT_CFG_LBL_STYLE}>{cfg.l}</div>
                {/* overflowWrap: a legacy pasted-URL account id must wrap
                    inside its grid cell, not widen the whole card sideways. */}
                <div style={{ ...GT_CFG_VAL_STYLE, overflowWrap: 'anywhere' }}>{cfg.v || '—'}</div>
              </div>
            ))}
          </div>
        </div>
        </>
        )}

        {slotIs('pivot') && (
        /* QA pivot panel — GT only. The standalone ad-groups summary
           table that used to live above this was removed: it duplicated
           data already available in the main review grid, and the
           Breakdown tab is meant for cross-cutting pivots, not row
           re-rendering. */
        <GtQaPivotPanel
          ctsReview={ctsReview}
          activeKey={qaPivotKey}
          setActiveKey={setQaPivotKey}
        />
        )}
      </div>
    );
  }

  // DV360 / TTD branch: launcherResult-driven summary block.
  if (!isGt && launcherResult) {
    // Display-only merge: fills budget / flight from the platform setup the grid
    // caches, when the plan states none. Never written back to launcherResult —
    // the plan is editable and saved, and these are not the user's numbers.
    const s = mergePlatformSetup(summary, platformSetup);
    // Money in the campaign's own currency when we know it; fmtCurrency's
    // hardcoded '$' otherwise (unchanged for plans that carry no currency).
    const money = (v, code) => (code ? cur(v, code) : fmtCurrency(v));
    const budget = budgetTile(s, money);
    const packageRows = breakdownByPackage(launcherResult);
    const tacticRows = breakdownBy(reviewRows, (r) => r.tactic || 'Other');
    const formatRows = breakdownBy(reviewRows, (r) => r.fmt || 'Other');
    const monthRows = breakdownByMonth(launcherResult);
    const config = extractCampaignConfig(launcherResult);
    const totalImpsBreakdown = packageRows.reduce((a, r) => a + (r.impressions || 0), 0);
    const totalBudget = packageRows.reduce((a, r) => a + (r.budget || 0), 0);

    return (
      <div className="clw-ps-wrap">
        {slotIs('tiles') && (
        <>
        {/* Stat tiles */}
        <div className="clw-ps-stat-grid">
          <div className="clw-ps-stat highlight">
            <div className="clw-ps-stat-val">{fmtInt(s?.adGroupCount)}</div>
            {/* Google Ads summaries carry their own wording (`rowLabel` /
                `parentLabel`) because a row is an ad group on Search and an
                asset group on PMax / Demand Gen — neither is a "line item". */}
            <div className="clw-ps-stat-lbl">{s?.rowLabel || (s?.platform === 'TTD' ? 'Ad groups created' : 'Line items created')}</div>
            <div className="clw-ps-stat-sub">Across {fmtInt(s?.ioCount)} {s?.parentLabel || (s?.platform === 'TTD' ? 'campaigns' : 'insertion orders')}</div>
          </div>
          {/* Impression goal, or — when the plan has none and never will (every
              Google Ads plan is currency-budgeted) — the platform's own daily
              budget, which is a real number for the same slot. */}
          {s?.totalImpressions == null && s?.dailyBudget != null ? (
            <div className="clw-ps-stat">
              <div className="clw-ps-stat-val">{money(s.dailyBudget, s.currencyCode)}</div>
              <div className="clw-ps-stat-lbl">Daily budget</div>
              <div className="clw-ps-stat-sub">Live from the platform</div>
            </div>
          ) : (
            <div className="clw-ps-stat">
              <div className="clw-ps-stat-val">{s?.totalImpressions == null ? '—' : fmtInt(s.totalImpressions)}</div>
              <div className="clw-ps-stat-lbl">Total impressions</div>
              <div className="clw-ps-stat-sub">{s?.avgCpm
                ? `${money(s.avgCpm, s?.currencyCode)} avg CPM`
                : (s?.totalImpressions == null ? 'No impression goal in plan' : 'Impressions-based budget')}</div>
            </div>
          )}
          {/* Stated total, an estimate over a bounded flight, or the monthly
              maximum of an open-ended daily budget — labelled for which. */}
          <div className="clw-ps-stat">
            <div className="clw-ps-stat-val">{budget.value}</div>
            <div className="clw-ps-stat-lbl">{budget.label}</div>
            <div className="clw-ps-stat-sub">{budget.sub}</div>
          </div>
          {/* The plan's audiences when it has any; otherwise the platform's, which
              is the only source for an imported Google Ads campaign. */}
          <div className="clw-ps-stat">
            <div className="clw-ps-stat-val">{fmtInt(s?.audienceCount || platformAudiences?.length || 0)}</div>
            <div className="clw-ps-stat-lbl">Audiences targeted</div>
            <div className="clw-ps-stat-sub">{s?.audienceCount
              ? (s.audienceBasis || '1P/partner + custom audiences')
              : (platformAudiences?.length
                ? `${platformAudiences.slice(0, 2).join(', ')}${platformAudiences.length > 2 ? ` +${platformAudiences.length - 2} more` : ''}`
                : 'No audience targeting')}</div>
          </div>
          {/* Creative the CAMPAIGN carries (images / videos / text assets) when
              the plan knows them — the upload count is a create-flow number and
              is always 0 on a launched or imported campaign. */}
          {s?.creativeAssetCount != null && !creativeFiles?.length ? (
            <div className="clw-ps-stat">
              <div className="clw-ps-stat-val">{fmtInt(s.creativeAssetCount)}</div>
              <div className="clw-ps-stat-lbl">Creative assets</div>
              <div className="clw-ps-stat-sub">{[
                s.imageCount ? `${fmtInt(s.imageCount)} image${s.imageCount === 1 ? '' : 's'}` : null,
                s.videoCount ? `${fmtInt(s.videoCount)} video${s.videoCount === 1 ? '' : 's'}` : null,
                s.textAssetCount ? `${fmtInt(s.textAssetCount)} text` : null,
              ].filter(Boolean).join(' · ') || 'No creative in the plan'}</div>
            </div>
          ) : (
            <div className="clw-ps-stat">
              <div className="clw-ps-stat-val">{fmtInt(creativeFiles?.length || 0)}</div>
              <div className="clw-ps-stat-lbl">Creative assets uploaded</div>
              <div className="clw-ps-stat-sub">{(creativeFiles?.length || 0) > 0 ? `${creativeFiles.length} file${creativeFiles.length === 1 ? '' : 's'} attached` : 'No creatives uploaded'}</div>
            </div>
          )}
          <div className="clw-ps-stat">
            <div className="clw-ps-stat-val" style={s?.issueCount ? { color: 'var(--gold)' } : undefined}>{fmtInt(s?.issueCount ?? 0)}</div>
            <div className="clw-ps-stat-lbl">Annotations flagged</div>
            <div className="clw-ps-stat-sub">{s?.issueCount ? `${s.errorCount} error · ${s.warningCount} warning` : 'No required fields missing'}</div>
          </div>
        </div>

        {/* Campaign configuration card */}
        <div className="clw-ps-breakdown">
          <div className="clw-ps-break-head">Campaign configuration</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0 }}>
            {[
              { l: 'Campaign goal', v: config.goal },
              { l: 'KPI', v: config.kpi },
              { l: 'Frequency cap', v: config.freqCap },
              { l: 'Viewability target', v: config.viewabilityTarget },
              { l: 'Floodlight pixels', v: config.conversionPixels ? `${config.conversionPixels} unique` : 'None' },
              { l: 'Advertiser ID', v: config.advertiserId ? `#${config.advertiserId}` : '—' },
              { l: 'Standard exclusions', v: config.standardExclusions != null ? `${config.standardExclusions} rules` : '—' },
              // An open-ended campaign HAS a start date; rendering the pair as
              // '—' hid it. Google's 2037-12-30 sentinel reads as "no end date".
              { l: 'Flight', v: s?.flightStart ? `${s.flightStart} → ${s.flightEnd || 'no end date'}` : '—' },
              { l: 'Platform', v: s?.platform || destination?.toUpperCase() || '—' },
              { l: 'Geo markets', v: config.geoSummary },
              { l: 'Tactics', v: config.tacticSummary },
              { l: 'Formats', v: config.formatSummary },
              { l: 'Budget type', v: config.budgetType },
              { l: 'Pacing', v: config.topPacing },
              { l: 'Bid strategy', v: config.topBid },
              { l: 'Language targeting', v: config.languageSummary },
              { l: 'Devices', v: config.deviceSummary },
              ...(config.partnerId ? [{ l: 'Partner ID', v: `#${config.partnerId}` }] : []),
            ].map((cfg) => (
              <div key={cfg.l} style={{ padding: '14px 22px', borderBottom: '1px solid var(--g2)', borderRight: '1px solid var(--g2)' }}>
                <div style={{ fontSize: 10.5, color: 'var(--g4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{cfg.l}</div>
                <div style={{ fontSize: 13, color: 'var(--g5)', fontFamily: 'var(--font-sans)' }}>{cfg.v || '—'}</div>
              </div>
            ))}
          </div>
        </div>
        </>
        )}

        {slotIs('pivot') && (
        <>
        {/* Breakdown switcher */}
        <div className="clw-ps-table-wrap">
          <div className="clw-ps-table-toolbar">
            <span className="clw-ps-table-label">Breakdown</span>
            <select className="clw-ps-table-select" value={breakdownView} onChange={(e) => setBreakdownView(e.target.value)}>
              <option value="package">{s?.parentLabel === 'campaigns' || s?.platform === 'TTD' ? 'By campaign — ad-group allocation, flight' : 'By IO — line-item allocation, sizes, flight'}</option>
              <option value="tactic">By tactic — line-item targeting flavor</option>
              <option value="format">By format — channel mix</option>
              <option value="month">By month — budget pacing segments</option>
            </select>
            <span className="clw-ps-table-badge">
              {breakdownView === 'package' && `${packageRows.length} ${s?.parentLabel === 'campaigns' || s?.platform === 'TTD' ? `campaign${packageRows.length === 1 ? '' : 's'}` : `IO${packageRows.length === 1 ? '' : 's'}`}`}
              {breakdownView === 'tactic' && `${tacticRows.length} tactics`}
              {breakdownView === 'format' && `${formatRows.length} formats`}
              {breakdownView === 'month' && `${monthRows.length} segments`}
            </span>
          </div>
          <div className="clw-ps-tbl-scroll">
            {breakdownView === 'package' && (
              <table className="clw-ps-tbl">
                <thead><tr>
                  <th>Format</th><th>{s?.parentLabel === 'campaigns' || s?.platform === 'TTD' ? 'Campaign · Tactic' : 'Insertion Order'}</th><th className="num">{s?.rowLabel ? s.rowLabel.replace(/ parsed$/, '') : (s?.platform === 'TTD' ? 'Ad groups' : 'Line items')}</th>
                  <th className="num">Impressions</th><th className="num">CPM</th><th className="num">Budget</th>
                  <th>Sizes</th><th>Flight</th>
                </tr></thead>
                <tbody>
                  {packageRows.map((r) => (
                    <tr key={r.key}>
                      <td><span className={`clw-fmt-pill ${fmtBadgeClass(r.formatCode)}`}>{r.formatCode}</span></td>
                      <td>{r.packageName}{r.categoryLabel && r.categoryLabel !== '—' ? ` · ${r.categoryLabel}` : ''}</td>
                      <td className="num">{fmtInt(r.lineItemCount)}</td>
                      <td className="num">{fmtInt(r.impressions)}</td>
                      <td className="num">{r.rate != null ? fmtCurrency(r.rate) : '—'}</td>
                      <td className="num">{r.budget != null ? fmtCurrency(r.budget) : '—'}</td>
                      <td title={r.sizes} style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.sizes}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{r.flightStart && r.flightEnd ? `${r.flightStart} → ${r.flightEnd}` : '—'}</td>
                    </tr>
                  ))}
                  <tr className="row-total">
                    <td colSpan={2}>TOTAL</td>
                    <td className="num">{fmtInt(packageRows.reduce((a, r) => a + r.lineItemCount, 0))}</td>
                    <td className="num">{fmtInt(totalImpsBreakdown)}</td>
                    <td className="num">{s?.avgCpm ? fmtCurrency(s.avgCpm) : '—'}</td>
                    <td className="num">{totalBudget ? fmtCurrency(totalBudget) : '—'}</td>
                    <td>—</td>
                    <td>{s?.flightStart && s?.flightEnd ? `${s.flightStart} → ${s.flightEnd}` : '—'}</td>
                  </tr>
                </tbody>
              </table>
            )}
            {breakdownView === 'tactic' && (
              <table className="clw-ps-tbl">
                <thead><tr>
                  <th>Tactic</th><th className="num">Line items</th><th className="num">Impressions</th>
                  <th className="num">% of total</th>
                </tr></thead>
                <tbody>
                  {tacticRows.map((r) => (
                    <tr key={r.key}>
                      <td>{r.label}</td>
                      <td className="num">{fmtInt(r.lineItemCount)}</td>
                      <td className="num">{fmtInt(r.impressions)}</td>
                      <td className="num">{totalImpsBreakdown > 0 ? ((r.impressions / totalImpsBreakdown) * 100).toFixed(1) + '%' : '—'}</td>
                    </tr>
                  ))}
                  {tacticRows.length === 0 && (
                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: 16, color: 'var(--g4)' }}>No tactic data</td></tr>
                  )}
                  <tr className="row-total">
                    <td>TOTAL</td>
                    <td className="num">{fmtInt(tacticRows.reduce((a, r) => a + r.lineItemCount, 0))}</td>
                    <td className="num">{fmtInt(tacticRows.reduce((a, r) => a + r.impressions, 0))}</td>
                    <td className="num">100.0%</td>
                  </tr>
                </tbody>
              </table>
            )}
            {breakdownView === 'format' && (
              <table className="clw-ps-tbl">
                <thead><tr>
                  <th>Format</th><th className="num">Line items</th><th className="num">Impressions</th>
                  <th className="num">% of total</th>
                </tr></thead>
                <tbody>
                  {formatRows.map((r) => (
                    <tr key={r.key}>
                      <td><span className={`clw-fmt-pill ${fmtBadgeClass(r.label)}`}>{r.label}</span></td>
                      <td className="num">{fmtInt(r.lineItemCount)}</td>
                      <td className="num">{fmtInt(r.impressions)}</td>
                      <td className="num">{totalImpsBreakdown > 0 ? ((r.impressions / totalImpsBreakdown) * 100).toFixed(1) + '%' : '—'}</td>
                    </tr>
                  ))}
                  <tr className="row-total">
                    <td>TOTAL</td>
                    <td className="num">{fmtInt(formatRows.reduce((a, r) => a + r.lineItemCount, 0))}</td>
                    <td className="num">{fmtInt(formatRows.reduce((a, r) => a + r.impressions, 0))}</td>
                    <td className="num">100.0%</td>
                  </tr>
                </tbody>
              </table>
            )}
            {breakdownView === 'month' && (
              <table className="clw-ps-tbl">
                <thead><tr>
                  <th>Segment</th><th>Start</th><th>End</th>
                  <th className="num">Line items</th><th className="num">Impressions</th><th className="num">% of total</th>
                </tr></thead>
                <tbody>
                  {monthRows.map((r) => (
                    <tr key={r.key}>
                      <td>{r.label}</td>
                      <td>{fmtDate(r.start)}</td>
                      <td>{fmtDate(r.end)}</td>
                      <td className="num">{fmtInt(r.lineItemCount)}</td>
                      <td className="num">{fmtInt(r.impressions)}</td>
                      <td className="num">{totalImpsBreakdown > 0 ? ((r.impressions / totalImpsBreakdown) * 100).toFixed(1) + '%' : '—'}</td>
                    </tr>
                  ))}
                  {monthRows.length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: 16, color: 'var(--g4)' }}>No budget segments in plan</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
          <div className="clw-ps-tbl-footer">
            <span>{breakdownView === 'package' ? (s?.platform === 'TTD' ? 'Source: Campaigns[] + AdGroups[]' : 'Source: insertionOrders[] + lineItems[]') : breakdownView === 'tactic' ? 'Source: lineItems[].tactic' : breakdownView === 'format' ? 'Source: lineItems[].lineItemType' : 'Source: insertionOrders[].budget.budgetSegments[]'}</span>
            <span>·</span>
            <span>Campaign: <strong>{s?.campaignName || campaignName}</strong></span>
            {s?.advertiserId && (<><span>·</span><span>Advertiser <strong>#{s.advertiserId}</strong></span></>)}
          </div>
        </div>
        </>
        )}
      </div>
    );
  }

  return null;
}

// ─── GT VERSION HISTORY BUTTON ───────────────────────────────
// Lazy-loaded dropdown listing the saved revisions of a GT campaign's
// edited config. Clicking a row opens a window.confirm dialog — Revert is
// destructive enough (reloads the entire config + re-runs CTS) that an
// explicit OK is worth the friction. The dropdown re-fetches every time
// it's opened so the list reflects the latest save without manual refresh.
function GtVersionHistoryButton({ fetchVersions, onRevert, disabled, readOnly }) {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reverting, setReverting] = useState(null);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchVersions();
      setVersions(list);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load history');
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [fetchVersions]);

  const onToggle = () => {
    if (!open) refresh();
    setOpen((v) => !v);
  };

  const onClickRevert = async (v) => {
    // Confirm before reverting — the action overwrites the current edited
    // state with the chosen version (the revert itself is tracked, so it
    // remains undoable, but it does reset every pending edit).
    const label = formatRevisionLabel(v);
    if (!window.confirm(`Revert to "${label}"?\n\nThis discards any unsaved edits and reloads the campaign at that version.`)) return;
    setReverting(v.gcsPath);
    try {
      await onRevert(v.gcsPath);
      await refresh();
      setOpen(false);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Revert failed');
    } finally {
      setReverting(null);
    }
  };

  return (
    <div className="clw-version-history" style={{ position: 'relative' }}>
      <button
        type="button"
        className="clw-btn clw-btn-secondary clw-btn-sm"
        onClick={onToggle}
        disabled={disabled}
        title={readOnly ? 'Campaign is already launched — history is read-only' : 'View past versions'}
      >
        History ▾
      </button>
      {open && (
        <div className="clw-version-history-menu" style={{
          position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 1000,
          minWidth: 440, maxWidth: 560, maxHeight: 480, overflowY: 'auto',
          background: 'var(--bg-elevated, #fff)', border: '1px solid var(--g3, #d0d0d0)',
          borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: 8,
        }}>
          <div style={{ padding: '6px 8px', fontSize: 12, color: 'var(--g4)', borderBottom: '1px solid var(--g2)' }}>
            Saved versions {loading ? '· loading…' : versions ? `· ${versions.length}` : ''}
          </div>
          {error && (
            <div style={{ padding: 8, color: 'var(--red)', fontSize: 12 }}>{error}</div>
          )}
          {!loading && versions && versions.length === 0 && (
            <div style={{ padding: 12, color: 'var(--g4)', fontSize: 12, textAlign: 'center' }}>
              No saved versions yet. Each Save creates one.
            </div>
          )}
          {versions && versions.map((v) => (
            <VersionHistoryRow
              key={v.gcsPath}
              version={v}
              onClickRevert={onClickRevert}
              reverting={reverting === v.gcsPath}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Badges for versions an action wrote (GET gt-config-versions `action`):
// Upload Creatives and Remove Creatives, so either can be found and undone.
const VERSION_ACTION_BADGES = {
  'upload-creatives': { text: 'Uploaded creatives', background: '#E6F7F8', color: '#00737C' },
  'remove-creatives': { text: 'Removed creatives', background: '#FDECEC', color: '#A12C2C' },
};

// One version entry inside the History dropdown. Renders the timestamp +
// user, an Initial Save / Reverted / creatives badge where applicable, and a
// bullet list of what changed compared to the prior version (computed
// server-side in loadAndDiffRevisions). The diff list collapses to "+N more"
// when there are many changes so the dropdown doesn't blow up vertically.
function VersionHistoryRow({ version, onClickRevert, reverting, readOnly }) {
  const [expanded, setExpanded] = useState(false);
  const changes = Array.isArray(version.changes) ? version.changes : null;
  const actionBadge = VERSION_ACTION_BADGES[version.action] || null;
  const isInitial = version.changes === null && !version.isRevert && !actionBadge;
  const visibleLimit = expanded ? Infinity : 5;
  const visible = changes ? changes.slice(0, visibleLimit) : [];
  const overflow = changes ? Math.max(0, changes.length - visible.length) : 0;
  return (
    <div className="clw-version-row" style={{
      display: 'flex', flexDirection: 'column', gap: 6,
      padding: '10px 8px', borderBottom: '1px solid var(--g1)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, fontSize: 12 }}>
          <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
            {formatRevisionLabel(version)}
            {version.isRevert && (
              <span style={{
                fontSize: 10, padding: '1px 6px', borderRadius: 3,
                background: '#FFF5E6', color: '#A56300', fontWeight: 500,
              }}>Revert</span>
            )}
            {isInitial && (
              <span style={{
                fontSize: 10, padding: '1px 6px', borderRadius: 3,
                background: 'var(--g1)', color: 'var(--g4)', fontWeight: 500,
              }}>Initial</span>
            )}
            {actionBadge && (
              <span style={{
                fontSize: 10, padding: '1px 6px', borderRadius: 3,
                background: actionBadge.background, color: actionBadge.color, fontWeight: 500,
              }}>{actionBadge.text}</span>
            )}
          </div>
          {version.label && (
            <div style={{ color: 'var(--g5)', marginTop: 2, wordBreak: 'break-word' }}>{version.label}</div>
          )}
          {version.userId && (
            <div style={{ color: 'var(--g4)', marginTop: 2 }}>by {version.userId}</div>
          )}
        </div>
        <button
          type="button"
          className="clw-btn clw-btn-secondary clw-btn-sm"
          onClick={() => onClickRevert(version)}
          disabled={reverting || readOnly}
          title={readOnly ? 'Read-only after launch' : 'Revert to this version'}
        >
          {reverting ? 'Reverting…' : 'Revert'}
        </button>
      </div>
      {changes && changes.length > 0 && (
        <ul style={{
          margin: 0, paddingLeft: 14, fontSize: 11, lineHeight: 1.5,
          color: 'var(--g5)', fontFamily: 'var(--font-mono)',
        }}>
          {visible.map((c, i) => (
            <li key={i} style={{ wordBreak: 'break-word' }}>
              <span style={{ color: 'var(--g4)' }}>{humanizeDiffPath(c.path)}</span>
              {': '}
              <span style={{ textDecoration: 'line-through', color: 'var(--g4)' }}>{c.before}</span>
              <span style={{ margin: '0 4px', color: 'var(--g4)' }}>→</span>
              <span style={{ color: 'var(--blue, #2563EB)', fontWeight: 500 }}>{c.after}</span>
            </li>
          ))}
          {overflow > 0 && (
            <li style={{ listStyle: 'none', marginLeft: -14, marginTop: 2 }}>
              <button
                type="button"
                onClick={() => setExpanded(true)}
                style={{
                  background: 'none', border: 'none', color: 'var(--blue, #2563EB)',
                  cursor: 'pointer', padding: 0, fontSize: 11, fontFamily: 'inherit',
                }}
              >
                + {overflow} more change{overflow === 1 ? '' : 's'}
              </button>
            </li>
          )}
        </ul>
      )}
      {changes && changes.length === 0 && !isInitial && (
        <div style={{ fontSize: 11, color: 'var(--g4)', fontStyle: 'italic' }}>
          No detectable changes from previous version.
        </div>
      )}
      {isInitial && (
        <div style={{ fontSize: 11, color: 'var(--g4)', fontStyle: 'italic' }}>
          First saved version.
        </div>
      )}
    </div>
  );
}

// Translate a JSON path like `adgroups[2].bid_price` into a friendlier
// shorthand `Adgroup 3 · bid_price`. Handles the three campaign-shape
// roots (adgroups for GT, lineItems for DV360, AdGroups for TTD) and
// leaves anything else as-is so unfamiliar paths stay traceable.
function humanizeDiffPath(p) {
  if (!p) return '?';
  return p
    .replace(/^adgroups\[(\d+)\]\.?/, (_m, i) => `Adgroup ${Number(i) + 1} · `)
    .replace(/^AdGroups\[(\d+)\]\.?/, (_m, i) => `Adgroup ${Number(i) + 1} · `)
    .replace(/^lineItems\[(\d+)\]\.?/, (_m, i) => `Line item ${Number(i) + 1} · `)
    .replace(/\.+$/, '')
    .replace(/^$/, '(root)');
}

function formatRevisionLabel(v) {
  if (!v?.timestamp) return v?.gcsPath || 'Unknown';
  // Try to render the timestamp in the viewer's local timezone for
  // readability. Falls back to the raw ISO string if parsing fails.
  try {
    const d = new Date(v.timestamp);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    }
  } catch { /* fall through */ }
  return v.timestamp;
}

// ─── REVIEW PANE ─────────────────────────────────────────────
function ReviewPane({
  active, pendingJump, clearPendingJump,
  campaignName, exportSourceName, destination, launcherResult, ctsReview,
  gtExcelFile, platformAccountId, accountId, userId,
  reviewRows, editedRows, appliedConfigGcsPath, onReviewCtsUpdated,
  creativeUpload,
  // GT IO review "Remove Creatives": { onRemove, busy, disabled } | null — see
  // handleRemoveCreatives in the host.
  creativeRemove,
  creativeUploadState,
  onReloadActiveRevision,
  activeGroup, setActiveGroup, gridSearch, setGridSearch,
  filterIssuesOnly, setFilterIssuesOnly,
  issuesBarOpen, setIssuesBarOpen,
  editingCell, setEditingCell, editingDraft, setEditingDraft,
  commitCellEdit, cancelCellEdit, getRowValue,
  detailRowId, setDetailRowId, detailCellField, setDetailCellField,
  aiOpen, setAiOpen,
  savingReview, saveReviewEdits, launching, launchError, launchBlockers, dismissLaunchError, launchSuccess, alreadyLaunched, approveAndLaunch,
  // True when the findings on ctsReview predate ctsReview.result — see the
  // host's ctsFindingsStale.
  ctsFindingsStale,
  undoEntry, revertUndo, undoing, dismissUndo,
  failedAutoSaveIdsRef, reportProposalOutcomeRef,
  saveWarnings, dismissSaveWarnings, onLookup, onRefreshLookup,
  pendingDeletions, toggleListDeletion,
  pendingListOps, addListItem, editListItem,
  setEditedRows, setPendingListOps, setPendingRowOps, pendingRowOpCount,
  bulkAudienceRefresh, onRefreshAudiences,
  fetchGtConfigVersions, revertGtConfig,
  fetchConfigVersions, revertConfig,
  // Consolidated summary-into-review props.
  summaryError, importWarnings, dismissImportWarnings, summaryOpen, setSummaryOpen,
  breakdownView, setBreakdownView, qaPivotKey, setQaPivotKey,
  creativeFiles, initialUrlCampaign, setPane, setCtsReview, setPendingJump,
  isMmgy, gtChannelEnabled, accountSettings, planFiles, campaignId,
  platformCampaignId,
  // Cached delivered performance for a launched campaign (null otherwise) —
  // rendered above the plan tiles by DeliveredMetricsStrip.
  deliveredMetrics,
  // Audience names the platform reports for this campaign (null when the grid
  // has never pulled it). Fills the audiences tile, which the plan cannot.
  platformAudiences,
  sourceFile,
  onRetryCtsReview,
  onBack,
}) {
  // Local navigate hook so the header's "Back to Campaigns" button can
  // jump straight to the campaigns list page without having to thread a
  // prop down from the parent component.
  const reviewNavigate = useNavigate();
  // The back button returns to whichever surface opened the workflow, named by
  // the `?from=` param: the Launch board (?from=launch) or the Campaign Grid
  // (?from=grid). Read the URL directly (same approach as initialUrlCampaign) so
  // we don't depend on a threaded prop. Those flows never hit the create-flow
  // setSearchParams that would drop the param, so it persists for the session.
  // NB: only `from=launch` also switches the route to LaunchCampaignDetails
  // (App.js CampaignStartSwitch) — every other value renders this workflow.
  const cameFrom = (() => {
    try { return new URLSearchParams(window.location.search).get('from') || ''; } catch (e) { return ''; }
  })();
  const BACK_TARGETS = {
    launch: { to: '/launch', label: '← Back to Launch', title: 'Back to the launch board' },
    grid: { to: '/campaign-grid', label: '← Back to Campaign Grid', title: 'Back to the campaign grid' },
  };
  const backTarget = BACK_TARGETS[cameFrom]
    || { to: '/campaigns', label: '← Back to Campaigns', title: 'Back to the campaigns list' };
  const { to: backTo, label: backLabel } = backTarget;
  const isGt = Boolean(ctsReview);
  const localReviewOnly = isLocalGtReviewOnly(destination);
  // Remove Creatives saves the plan: no other write may start meanwhile.
  const removingCreatives = Boolean(creativeRemove?.busy);
  // Stage a campaign-configuration card edit under the GT_CAMPAIGN_ROW_ID
  // sentinel row, so campaign edits share the grid's pending-count, Save and
  // undo machinery. Re-typing the saved value un-stages the edit.
  const stageCampaignEdit = useCallback((key, value) => {
    setEditedRows((prev) => {
      const saved = ctsReview?.result?.campaign?.[key];
      const row = { ...(prev[GT_CAMPAIGN_ROW_ID] || {}) };
      if (String(value ?? '') === String(saved ?? '')) delete row[key];
      else row[key] = value;
      const next = { ...prev };
      if (Object.keys(row).length === 0) delete next[GT_CAMPAIGN_ROW_ID];
      else next[GT_CAMPAIGN_ROW_ID] = row;
      return next;
    });
  }, [ctsReview, setEditedRows]);
  // Which agent the sidebar is showing. 'bulk' is the default on every
  // platform — it's what people actually come here to do — and 'qa' switches to
  // the platform's reviewer agent where one exists (GT CTS, MMGY DV360/TTD).
  // Google Ads and non-MMGY DV360/TTD have no reviewer, so QA is offered but
  // disabled there rather than hidden, which is how the user learns it exists.
  const [agentMode, setAgentMode] = useState('bulk');
  // MadHive's optimizer reads an uploaded reporting-metrics ZIP. The CLW hosts
  // the upload through OptimizeCampaignModal when the Optimization tab's gate
  // offers it, so removing the Campaign Grid panel did not leave a dead end.
  const [optimizeUploadOpen, setOptimizeUploadOpen] = useState(false);
  // ── QA reviewer eligibility (ACCOUNT, not just plan shape) ──────────
  // The reviewers are account-specific products: the MMGY agents reason about
  // the MMGY pipeline (10% buffer, cross-DSP filter, AV budget formula) and the
  // GT agent about GroundTruth CTS rules. Running either outside its account
  // means explaining values by citing processing that never ran, so the gate is
  // the account — not, as before, the DSP shape alone.
  //
  // These are SEPARATE booleans rather than a redefinition of `isGt` /
  // `isMmgyClw`, which drive the whole review pane (columns, appliers, row
  // builders) and must not change. Only agent wiring reads these.
  //
  // The backend enforces the same rule at session create; this is what stops the
  // sidebar offering a tab that would 403.
  const gtQaAllowed = isGt && Boolean(gtChannelEnabled);
  // One-click bulk-fix state — replaces the old textarea modal. Pending
  // fixes come from two sources, unioned and deduped by (name, field):
  //   1. editedRows  — user's manually-typed grid cell edits
  //   2. gtAgent.suggestions filtered to status not in (applied / dismissed / applying)
  // On click everything is sent as one fix-by-name batch; the response
  // feeds back through onReviewCtsUpdated so the grid + summary refresh.
  const [bulkFixRunning, setBulkFixRunning] = useState(false);
  const [bulkFixToast, setBulkFixToast] = useState(null);
  const bulkFixToastTimer = useRef(null);
  // "Download IO file" — fetches a fresh signed URL for the original
  // upload key (files/{chatId}/…) via GET /api/upload/download-url and
  // opens it. The publicUrl captured at upload time expires after 24h,
  // so we always re-sign instead of reusing it.
  const [downloadingSource, setDownloadingSource] = useState(false);
  const [downloadSourceError, setDownloadSourceError] = useState(null);
  const downloadSourceFile = useCallback(async () => {
    if (!sourceFile?.gcsPath) return;
    setDownloadingSource(true);
    setDownloadSourceError(null);
    try {
      const res = await api.get(getApiUrl('/api/upload/download-url'), {
        params: { gcsPath: sourceFile.gcsPath, accountId, userId },
      });
      const url = res.data?.url;
      if (!url) throw new Error('No download URL returned');
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      console.error('Could not fetch IO file download URL', e);
      setDownloadSourceError(e?.response?.data?.error || e.message || 'Download failed');
    } finally {
      setDownloadingSource(false);
    }
  }, [sourceFile, accountId, userId]);
  const summary = isGt
    ? summarizeReviewCtsResult(ctsReview)
    : (launcherResult ? summarizeLauncherResult(launcherResult) : null);
  // PMP/deal data shows for TTD, and for DV360 MMGY plans that carry matched
  // private-marketplace deals. The MMGY DV360 post-processor attaches the
  // deal mapping to each line item as `pmpInventorySource`
  // ([{ inventorySourceId, displayName }]); when present we keep the PMP
  // column so reviewers can verify the deals. Plans with only a bare
  // placement code (no matched deals) still strip the column so the targeting
  // tab isn't cluttered.
  const platformIsTtd = !isGt && summary?.platform === 'TTD';
  // Google Ads plans (g-ads-search / -pmax / -demandgen) have their own
  // column sets and editable allow-lists — buildReviewRows already builds
  // Google Ads rows, so rendering them against the DV360 columns produced
  // an empty grid with the wrong editable set. Same four-way pick as
  // LaunchCampaignDetails.
  const launcherPlatformFamily = isGt ? null : launcherFamily(launcherResult);
  const platformIsYahoo = !isGt && launcherPlatformFamily === 'yahoo';
  const yahooAlreadyLaunched = platformIsYahoo && alreadyLaunched;
  const platformIsGads = !isGt && !platformIsTtd && launcherPlatformFamily === 'gads';
  // Facebook plans (campaign → ad_sets) get their own column set for the same
  // reason Google Ads did: buildReviewRows already builds Facebook rows, and
  // rendering them against DV360's line-item columns yields a grid of em
  // dashes with an editable set whose writers would never fire.
  const platformIsFb = !isGt && !platformIsTtd && !platformIsGads && launcherPlatformFamily === 'fb';
  const gadsKind = platformIsGads ? gadsResultSubtype(launcherResult) : null;
  const dv360HasPmpDeals = !isGt && !platformIsTtd && !platformIsGads && !platformIsFb
    && Array.isArray(launcherResult?.lineItems)
    && launcherResult.lineItems.some((li) => Array.isArray(li?.pmpInventorySource) && li.pmpInventorySource.length > 0);
  const showPmpColumn = platformIsTtd || dv360HasPmpDeals;
  const groupCols = platformIsYahoo ? YAHOO_GROUP_COLS : isGt
    ? GT_GROUP_COLS
    : (platformIsGads
        ? gadsGroupCols(gadsKind)
        : (platformIsFb
            ? FB_GROUP_COLS
            : (showPmpColumn
                ? GROUP_COLS
                : Object.fromEntries(Object.entries(GROUP_COLS).map(([g, cols]) => [
                    g,
                    cols.filter((c) => c.k !== 'pmp'),
                  ])))));
  const groupKeys = platformIsYahoo ? YAHOO_GROUP_KEYS : isGt
    ? GT_GROUP_KEYS
    : (platformIsGads ? gadsGroupKeys(gadsKind) : (platformIsFb ? FB_GROUP_KEYS : GROUP_KEYS));
  // Round-trip allow-list: only show the inline editor for fields that
  // saveReviewEdits / saveGtReviewEdits know how to persist back to the
  // underlying JSON. Field-editor map drives the input type (text /
  // number / currency / date) inside CellDetailPanel.
  const editableFields = platformIsYahoo ? YAHOO_EDITABLE : isGt
    ? GT_EDITABLE
    : (platformIsGads
        ? gadsEditableFields(gadsKind)
        : (platformIsFb ? FB_EDITABLE : (platformIsTtd ? TTD_EDITABLE : DV360_EDITABLE)));
  const fieldEditors = platformIsYahoo ? YAHOO_FIELD_EDITORS : isGt
    ? GT_FIELD_EDITORS
    : (platformIsGads
        ? GADS_FIELD_EDITORS
        : (platformIsFb ? FB_FIELD_EDITORS : (platformIsTtd ? TTD_FIELD_EDITORS : DV360_FIELD_EDITORS)));
  // Fixed option sets for the GT 'select' editors; other platforms have none.
  const fieldOptions = isGt ? GT_STATIC_OPTIONS : null;
  // Why each read-only cell is read-only — shown in the cell detail panel so
  // a missing Edit button reads as a decision, not an omission.
  const readOnlyReasons = platformIsYahoo ? YAHOO_READ_ONLY_REASONS : isGt
    ? GT_READ_ONLY_REASONS
    : (platformIsGads
        ? gadsReadOnlyReasons(gadsKind)
        : (platformIsFb ? FB_READ_ONLY_REASONS : (platformIsTtd ? TTD_READ_ONLY_REASONS : DV360_READ_ONLY_REASONS)));
  // Per-platform list add/edit/remove capabilities for the cell detail
  // panel. GT's come from the field descriptor (goal 3) — the two old
  // `!isGt` panel gates are gone.
  const listFields = platformIsYahoo ? YAHOO_LIST_FIELDS : isGt
    ? GT_LIST_FIELDS
    : (platformIsGads
        ? gadsListFields(gadsKind)
        : (platformIsFb ? FB_LIST_FIELDS : (platformIsTtd ? TTD_LIST_FIELDS : DV360_LIST_FIELDS)));
  // '__breakdown' is the pivot/breakdown tab — handled separately
  // from the per-column group views (it has its own data shape and
  // table layout), so don't normalize it back to 'overview'.
  const effectiveActiveGroup = activeGroup === '__breakdown'
    ? '__breakdown'
    : (groupCols[activeGroup] ? activeGroup : 'overview');
  const cols = groupCols[effectiveActiveGroup] || groupCols.overview;

  // Click-to-jump from the issues bar to the cell that owns the issue.
  // If the field lives in a different column group, switch the group
  // tab first so the cell is actually visible, then scrollIntoView the
  // row + open the cell detail panel + briefly flash the cell so the
  // user sees where the navigation landed.
  const jumpToCell = useCallback((rowId, field) => {
    if (!rowId || !field) return;
    // Switch group tab if the field isn't in the active one.
    let nextGroup = effectiveActiveGroup;
    if (!(cols || []).some((c) => c.k === field)) {
      for (const [name, gcols] of Object.entries(groupCols || {})) {
        if ((gcols || []).some((c) => c.k === field)) { nextGroup = name; break; }
      }
      if (nextGroup !== effectiveActiveGroup) setActiveGroup(nextGroup);
    }
    setDetailRowId(rowId);
    setDetailCellField(field);
    // Vertical scroll: row exists after the next frame (post group-tab
    // switch + filter reset). Run the row scroll immediately so the
    // vertical motion overlaps with the panel's 200ms open animation.
    requestAnimationFrame(() => {
      const row = document.querySelector(`[data-clw-rowid="${rowId}"]`);
      if (!row) return;
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    // Horizontal scroll + flash: defer until AFTER the cell-detail
    // panel finishes its slide-open animation (~200ms, see
    // .clw-dpanel transition). If we measure the scroller before the
    // panel reflows, the right edge still reflects the pre-open width
    // — we then scroll the cell to a position that gets immediately
    // covered when the panel takes its 420px slice. Waiting 260ms lets
    // the transition + .clw-grid-wrap padding-right reflow settle, so
    // sRect.right is the *visible* right edge once the panel is open.
    setTimeout(() => {
      const row = document.querySelector(`[data-clw-rowid="${rowId}"]`);
      if (!row) return;
      const cell = row.querySelector(`[data-clw-cell="${field}"]`);
      if (cell) {
        const scroller = cell.closest('.clw-grid-scroll');
        if (scroller) {
          // Sticky-column width (idx 52px + name column ~220px). When the
          // target cell would land beneath them after a horizontal
          // scroll, leave at least this much room so it sits to the right
          // of the sticky pair.
          const STICKY_LEFT = 280;
          const sRect = scroller.getBoundingClientRect();
          const cRect = cell.getBoundingClientRect();
          // Cell is hidden to the right of the viewport — pull the
          // scroller forward so the cell aligns with a comfortable left
          // edge inside the scroller (past the sticky columns).
          if (cRect.right > sRect.right - 16) {
            const offset = (cRect.left - sRect.left) - STICKY_LEFT - 16;
            scroller.scrollBy({ left: offset, behavior: 'smooth' });
          // Cell is hidden behind the sticky left columns (or off the
          // visible left edge) — scroll back enough to bring it just to
          // the right of the sticky pair.
          } else if (cRect.left < sRect.left + STICKY_LEFT) {
            const offset = (cRect.left - sRect.left) - STICKY_LEFT - 16;
            scroller.scrollBy({ left: offset, behavior: 'smooth' });
          }
        }
      }
      const target = cell || row;
      target.classList.add('clw-flash');
      // Match the CSS animation duration (5s) so the highlight rides
      // out completely before the class is yanked.
      setTimeout(() => target.classList.remove('clw-flash'), 5000);
    }, 260);
  }, [cols, effectiveActiveGroup, groupCols, setActiveGroup, setDetailCellField, setDetailRowId]);

  // Cross-pane jump consumer. When the summary pane hands off a target
  // (via setPendingJump in the parent), wait for this pane to be
  // active + reviewRows to be populated, then run the full jumpToCell
  // (group-tab switch + scroll + flash) and clear the pending state.
  useEffect(() => {
    if (!active || !pendingJump || !reviewRows.length) return;
    jumpToCell(pendingJump.rowId, pendingJump.field);
    clearPendingJump?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, pendingJump, reviewRows.length]);


  // ── GT CTS Reviewer Managed Agent session + suggestion bookkeeping.
  //    Lives here (not inside AgentSidebar) so the apply/dismiss
  //    protocol — which references GT-specific tool names like
  //    apply_config_edit / commit_config_edits — stays with the
  //    platform. AgentSidebar is platform-agnostic and consumes the
  //    `gtAgent` session + the GT_AGENT_CONFIG copy via props.
  // Source file for the agent session — works across every way the GT
  // review pane can be reached: fresh Excel upload, the PDF IO → CTS
  // flow, a ZIP upload, and a cross-device snapshot resume where
  // planFiles is empty. The parent passes all of those through the
  // `sourceFile` prop (gtPlanFile || ctsSourceFile — the same value the
  // Download IO button uses). Gating on the gtExcelFile prop alone left
  // the Sidekick dead on the PDF-IO and resume paths (no payload → no
  // autoStart → Enter silently dropped).
  const gtAgentSourceFile = sourceFile;
  const gtAgentCreatePayload = useMemo(() => (
    gtQaAllowed && gtAgentSourceFile?.gcsPath
      ? {
          // The frontend already holds the full review-cts payload in
          // state (returned inline from POST /api/upload/review-cts).
          // Pass it through so the backend can upload it directly to
          // the Managed Agents Files API — no GCS round-trip, no race
          // with the validator's write, no bucket/auth dependency.
          // requestId stays as a fallback for older sessions / debug
          // tooling.
          reviewCtsJson: ctsReview || null,
          requestId: ctsReview?.request_id || null,
          // Key name kept as excelGcsPath for backend/DB back-compat,
          // but it now carries whichever source the review came from
          // (Excel, IO PDF, or ZIP). sourceFileName lets the backend
          // mount it with the right extension for the agent.
          excelGcsPath: gtAgentSourceFile.gcsPath,
          sourceFileName: gtAgentSourceFile.originalName || null,
          platformAccountId: platformAccountId || null,
          campaignName: campaignName || null,
          // Lets the agent's commit_config_edits save its result as this
          // draft's head, so a reload restores it.
          campaignId: campaignId || null,
        }
      : null
  ), [gtQaAllowed, gtAgentSourceFile, ctsReview, platformAccountId, campaignName, campaignId]);
  const gtAgent = useGtCtsReviewerSession({
    accountId,
    userId,
    // Gated on the QA MODE, not just the sidebar being open. Without the mode
    // check, opening the sidebar in bulk mode also spun up the reviewer and ran
    // its auto-QA pass in the background — minutes of tokens for a pane the user
    // never looked at. The reviewer now starts only when its tab is selected.
    //
    // Also waits for the draft campaign id: the Excel flow sets ctsReview
    // before its draft row exists, and the session is created ONCE with
    // whatever payload it saw first — without the id, its commits can never
    // become the draft's saved head and are lost on the next reload.
    autoStart: aiOpen && agentMode === 'qa' && gtQaAllowed && Boolean(gtAgentCreatePayload) && Boolean(campaignId),
    createPayload: gtAgentCreatePayload || {},
  });

  // ── Lightweight CLW Reviewer wiring (MMGY DV360 / TTD) ──────
  // Lightweight per-DSP review agent. Reads the finalized plan + source
  // IO, suggests targeted edits, hands off to the existing launcher.
  // Mirrors the GT branch above.
  //
  // The gate is DSP-only (not account-name) because the agents reason
  // over the final_plan.json shape (camelCase for DV360, PascalCase for
  // TTD), which is DSP-standard. MMGY-specific knowledge in the prompts
  // (10% buffer, cross-DSP filter, AV budget formula) is informational
  // — it tells the agent NOT to flag those as bugs; non-MMGY launches
  // just won't see those notes apply.
  //
  // DSP is sourced in priority order: `launcherResult.platform` first
  // (the canonical post-launcher value — and the platform of the exact plan
  // we mount into the reviewer as finalPlanJson; same signal the row-builder
  // dispatcher uses at line 154), then the `destination` state as a fallback.
  // The plan must win: a single MMGY campaign can produce both a DV360 and a
  // TTD plan, and the user-selected `destination` can disagree with what the
  // launcher actually produced — trusting `destination` here routed a TTD plan
  // to the DV360 reviewer agent (and vice versa). `destination` is only the
  // fallback for deep-link loads where the launcher hasn't stamped a platform.
  //
  // Foundation v1: the agent is unaware of step1..step6 pipeline
  // artifacts; the heavy mmgy-plan-reviewer agent stays in place for
  // deep provenance tracing.
  const launcherPlatform = String(
    launcherResult?.platform || launcherResult?.Platform || ''
  ).toLowerCase();
  const effectiveDsp = (launcherPlatform || destination || '').toLowerCase();
  const isMmgyDv360 = effectiveDsp === 'dv360';
  const isMmgyTtd = effectiveDsp === 'ttd';
  const isMmgyClw = isMmgyDv360 || isMmgyTtd;
  // See the gtQaAllowed note above: DSP shape decides which reviewer COULD
  // apply; the account decides whether it may run at all.
  const mmgyQaAllowed = isMmgyClw && Boolean(isMmgy);
  // Source IO = the PDF the user uploaded (insertion order). MMGY
  // accounts ship a PDF as the IO; non-MMGY DV360/TTD launches may not
  // have one — that's fine, the agent runs with just the plan.
  const mmgySourceIoFile = useMemo(
    () => planFiles.find((f) => /\.pdf$/i.test(f.originalName || '')) || null,
    [planFiles],
  );
  const mmgyAgentCreatePayload = useMemo(() => (
    mmgyQaAllowed && launcherResult
      ? {
          // Send the EFFECTIVE DSP so the backend resolves the right
          // agent ID even when `destination` state is empty (deep-link
          // load — see comment on isMmgyDv360 above).
          destination: effectiveDsp,                    // 'dv360' | 'ttd'
          // campaignId doubles as processing_id for the lightweight reviewer
          // — the agent does NOT touch step1..step6 artifacts, so the
          // canonical {chat_id}_{message_id}_campaign format isn't required.
          processingId: campaignId || `clw-${Date.now()}`,
          finalPlanJson: launcherResult,                // inline — no GCS round-trip
          sourceIoGcsPath: mmgySourceIoFile?.gcsPath || null,
          campaignName: campaignName || null,
        }
      : null
  ), [mmgyQaAllowed, launcherResult, effectiveDsp, campaignId, campaignName, mmgySourceIoFile]);
  const mmgyAgent = useMmgyClwReviewerSession({
    accountId,
    userId,
    // Same mode gate as the GT branch above — no background auto-QA.
    autoStart: aiOpen && agentMode === 'qa' && mmgyQaAllowed && Boolean(mmgyAgentCreatePayload),
    createPayload: mmgyAgentCreatePayload || {},
  });

  // ── Bulk editor agent (the DEFAULT sidebar mode, every platform) ──────
  //
  // Unlike the reviewer agents above, this one is platform-agnostic: it edits
  // the grid's own rows through the grid's own field keys, so DV360, TTD,
  // Google Ads and GroundTruth all work off one session type. Google Ads and
  // non-MMGY DV360/TTD had no agent at all before this.
  //
  // The agent never mutates the plan. It proposes; the user confirms through
  // BulkEditPanel; the approved batch arrives as `lastStagedBatch` and is
  // folded into editedRows / pendingListOps — the same state a typed cell edit
  // writes — so it persists through the identical applier and PUT.
  //
  // Facebook sends 'facebook', NOT the 'fb' that launcherFamily returns: the
  // backend validates against services/bulkEdit/fieldMap.js#PLATFORMS and
  // agent_sessions carries a CHECK constraint that already permits 'facebook'
  // (widened for the optimization agent), so this is the spelling that reaches
  // the table without a second value for one platform. fieldMap's
  // normalizePlatform folds 'fb' / 'meta' onto it either way.
  const bulkPlatform = platformIsYahoo ? 'yahoo' : isGt
    ? 'groundtruth'
    : (platformIsGads ? 'gads' : (platformIsFb ? 'facebook' : (platformIsTtd ? 'ttd' : 'dv360')));
  // Row noun for the agent's orientation line. Derived here rather than reusing
  // the pane's `rowLabel`, which is declared further down and would be in its
  // temporal dead zone when this memo first runs.
  const bulkRowLabel = platformIsYahoo ? 'line flights' : (isGt || platformIsTtd)
    ? 'ad groups'
    : (platformIsGads
        ? (gadsKind === 'search' ? 'ad groups' : 'asset groups')
        : (platformIsFb ? FB_ROW_LABEL : 'line items'));
  const bulkDigest = useMemo(
    () => buildBulkDigest(reviewRows, editableFields),
    [reviewRows, editableFields],
  );

  // Fixed option sets for the agent (GT only): the grid's closed enums plus
  // the campaign card's selects, as { key: [{ label, value }] } so the preview
  // engine can canonicalise ("cpm" → "CPM") and stage the stored VALUE — for
  // the IAB category that is the bare code the launcher sends verbatim.
  const bulkFieldOptions = useMemo(() => {
    if (!isGt) return undefined;
    const out = {};
    for (const [key, opts] of Object.entries(GT_STATIC_OPTIONS)) {
      out[key] = opts.map((o) => ({ label: o, value: o }));
    }
    for (const f of gtCampaignSummaryFields()) {
      if (f.control === 'single-select' && f.options.length) {
        out[f.configKey] = f.options.map((o) => ({
          label: o,
          value: f.configKey === 'iab_category_resolved' ? o.split(' ')[0] : o,
        }));
      }
    }
    return out;
  }, [isGt]);

  // Campaign-scope settings for the agent (GT, pre-launch only — post-launch
  // the card is read-only and a staged campaign edit would have nowhere to
  // land). The agent's edits stage under GT_CAMPAIGN_ROW_ID and ride the same
  // save as the card's own edits.
  const bulkCampaignFieldKeys = useMemo(
    () => (isGt ? gtCampaignSummaryFields().filter((f) => f.editable).map((f) => f.configKey) : []),
    [isGt],
  );
  const bulkCampaign = useMemo(() => {
    if (!isGt || alreadyLaunched || !ctsReview?.result?.campaign) return undefined;
    const c = ctsReview.result.campaign;
    return {
      fields: Object.fromEntries(bulkCampaignFieldKeys.map((k) => [k, c?.[k] ?? null])),
      editableFields: bulkCampaignFieldKeys,
    };
  }, [isGt, alreadyLaunched, ctsReview, bulkCampaignFieldKeys]);

  // ── Row selection — the agent's scope ────────────────────────────────────
  // Ticked rows in the grid narrow every edit the bulk editor proposes. Held here
  // rather than in ReviewGrid because it is sent to the agent and has to survive
  // the grid's own re-renders.
  const [selectedRowIds, setSelectedRowIds] = useState(() => new Set());
  // Prune to rows that still exist. rowIds are POSITIONAL on GroundTruth and
  // Google Ads, so a selection carried across a save that added or removed rows
  // would silently re-point at different entities — and then scope an edit to
  // them. Dropping the unknown ones is the safe direction.
  useEffect(() => {
    setSelectedRowIds((prev) => {
      if (prev.size === 0) return prev;
      const live = rowIdSet(reviewRows);
      const next = new Set([...prev].filter((id) => live.has(String(id))));
      return next.size === prev.size ? prev : next;
    });
  }, [reviewRows]);
  const selectedRowIdList = useMemo(() => [...selectedRowIds], [selectedRowIds]);

  const bulkCreatePayload = useMemo(() => (
    campaignId && bulkDigest.rows.length && bulkPlatform && !(platformIsYahoo && alreadyLaunched)
      ? {
          platform: bulkPlatform,
          ...(platformIsYahoo ? { alreadyLaunched } : {}),
          surface: 'clw',
          campaignId,
          campaignName: campaignName || null,
          rows: bulkDigest.rows,
          editableFields: bulkDigest.editableFields,
          // List-op capabilities — a field can grant add/remove while having no
          // scalar writer (Copy TTD geo), so the agent needs both lists.
          listFields: serializeListFields(listFields),
          // Fixed option sets + campaign-scope settings (GT only, undefined
          // elsewhere — see the memos above).
          fieldOptions: bulkFieldOptions,
          campaign: bulkCampaign,
          rowLabel: bulkRowLabel,
          // The user's ticked rows. A HARD scope on every edit the agent
          // proposes — enforced server-side in services/bulkEdit/selector.js, not
          // left to the prompt.
          selectedRowIds: selectedRowIdList,
          // Except for Yahoo (pre-launch only), launch state is NOT sent: bulk editing is available in
          // every state, and telling the agent a plan is live only ever made it
          // refuse. The manual cell editor keeps its own read-only-after-launch
          // behaviour, which is unrelated to this path.
          // Inline so read_raw_plan can answer shape questions without a GCS
          // round-trip. GT reviews carry a CTS config rather than a launcher
          // template.
          launcherResult: isGt ? (ctsReview?.result || null) : (launcherResult || null),
        }
      : null
  ), [
    campaignId, campaignName, bulkPlatform, bulkDigest, bulkRowLabel, platformIsYahoo, alreadyLaunched,
    isGt, ctsReview, launcherResult, selectedRowIdList, listFields,
    bulkFieldOptions, bulkCampaign,
  ]);
  const bulkAgent = useBulkEditorSession({
    accountId,
    userId,
    autoStart: aiOpen && agentMode === 'bulk' && Boolean(bulkCreatePayload),
    createPayload: bulkCreatePayload || {},
  });
  if (reportProposalOutcomeRef) {
    reportProposalOutcomeRef.current = bulkAgent.gtChat ? bulkAgent.reportProposalOutcome : null;
  }

  // Stage an approved batch. Runs once per batch (keyed on the batch ts) so a
  // re-render can't double-apply it.
  const stagedBatchTs = useRef(null);
  // GroundTruth: row add/delete ops per staged preview, so an Undo that also
  // reverts an earlier batch's row ops knows the row set will change.
  const rowOpsByPreviewRef = useRef(new Map());
  const [bulkStageNote, setBulkStageNote] = useState(null);
  // Set by the staging effect, consumed by the auto-save effect below it. Two
  // steps because a save must see the edits the staging effect queued, and inside
  // that effect they have not landed yet.
  const [autoSaveRequest, setAutoSaveRequest] = useState(null);
  useEffect(() => {
    const batch = bulkAgent.lastStagedBatch;
    if (!batch || batch.ts === stagedBatchTs.current) return;
    stagedBatchTs.current = batch.ts;

    const { edits, listOps, rowOps, refused, accepted } = validateStaging(batch.staging, {
      platform: bulkPlatform, reviewRows, selectedRowIds, alreadyLaunched,
      editableFields,
      listFields: serializeListFields(listFields),
      knownRowIds: rowIdSet(reviewRows),
      // Campaign-scope keys (GT) — edits under the gt-campaign sentinel
      // validate against these; empty post-launch so they refuse instead of
      // staging into a save path that no longer exists.
      campaignFields: alreadyLaunched ? [] : bulkCampaignFieldKeys,
    });
    if (Object.keys(edits).length) setEditedRows?.((prev) => mergeEdits(prev, edits));
    if (Object.keys(listOps).length) setPendingListOps?.((prev) => mergeListOps(prev, listOps));
    if (rowOps.length) setPendingRowOps?.((prev) => mergeRowOps(prev, rowOps));
    // Row ops are called out separately: they change which rows exist, so the
    // grid only reflects them after a save rebuilds the rows.
    const rowOpNote = rowOps.length
      ? ` Includes ${rowOps.length} row add/delete op(s).`
      : '';
    setBulkStageNote({
      accepted,
      refused,
      // Deliberately says "Applied", not "Saved": the save has not run yet. The
      // outcome is appended at render time from autoSaveOutcome, so a failed save
      // cannot leave a note on screen claiming the change was persisted.
      text: accepted
        ? `Applied ${accepted} change${accepted === 1 ? '' : 's'}.${rowOpNote}`
        : 'Nothing was staged.',
      ts: batch.ts,
      // GroundTruth: which proposal card an Undo on this note belongs to.
      previewId: batch.preview_id || null,
    });
    if (batch.preview_id) rowOpsByPreviewRef.current.set(batch.preview_id, rowOps.length);
    // GroundTruth: a batch the grid refused entirely never saves — its card
    // says so rather than spinning on "Applying…".
    if (!accepted && isGt) {
      bulkAgent.reportProposalOutcome?.(
        batch.preview_id,
        'save_failed',
        `Nothing could be applied to the grid.${refused?.length ? ` ${refused.map((r) => r.reason).join(' ')}` : ''}`,
      );
    }
    // AUTO-SAVE. The user already made the decision that matters — they read the
    // preview and pressed Confirm — so making them then hunt for a Save button is
    // a second confirmation for the same act, and a batch left unsaved is a batch
    // lost to a refresh. Undo is what makes this safe rather than reckless, and it
    // is armed by the same call.
    //
    // Requested via state rather than by calling saveReviewEdits() here: this
    // effect has only just QUEUED the setEditedRows updates above, so the callback
    // in scope still closes over the pre-batch edits and would save nothing.
    if (accepted) {
      setAutoSaveRequest({
        ts: batch.ts,
        label: batch.summary || `${accepted} agent change${accepted === 1 ? '' : 's'}`,
        previewId: batch.preview_id || null,
        // A partly-refused batch must not read as wholly "Applied and saved".
        partial: refused?.length
          ? `${accepted} of ${accepted + refused.length} changes applied — skipped: ${refused.map((r) => r.reason).join(' ')}`
          : null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkAgent.lastStagedBatch]);

  // The other half of the auto-save. By the time this runs, the staged edits are
  // committed to state and `saveReviewEdits` has been rebuilt around them.
  const [autoSaveOutcome, setAutoSaveOutcome] = useState(null); // { ts, ok }
  useEffect(() => {
    if (!autoSaveRequest) return;
    const req = autoSaveRequest;
    setAutoSaveRequest(null);
    setAutoSaveOutcome(null);
    (async () => {
      const ok = await saveReviewEdits({ captureUndo: true, undoLabel: req.label, undoPreviewId: req.previewId });
      setAutoSaveOutcome({ ts: req.ts, ok: ok !== false });
      // GroundTruth: the proposal card shows the outcome, and keeps showing it
      // after a reload. A failed one stays pending in the grid; the next
      // successful save flips its card to saved (settleFailedAutoSaves).
      if (isGt && req.previewId) {
        if (ok === false) failedAutoSaveIdsRef?.current?.add(req.previewId);
        bulkAgent.reportProposalOutcome?.(
          req.previewId,
          ok !== false ? 'saved' : 'save_failed',
          ok !== false ? req.partial : 'The save failed — your changes are still pending; press Save to retry.',
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSaveRequest, saveReviewEdits]);

  // Keep the agent's row view current. The grid changes under it constantly
  // (a manual edit, a save, a fresh plan), and every change can invalidate an
  // outstanding preview — on GroundTruth and Google Ads row ids are positional,
  // so a stale preview can point at the wrong rows entirely. Debounced because
  // reviewRows is rebuilt on every keystroke in a cell.
  useEffect(() => {
    if (!bulkAgent.sessionId || !bulkDigest.rows.length) return;
    const t = setTimeout(() => {
      bulkAgent.refreshDigest({
        rows: bulkDigest.rows,
        ...(platformIsYahoo ? { alreadyLaunched } : {}),
        editableFields: bulkDigest.editableFields,
        // Ticking a row re-scopes the agent through this refresh — there is no
        // separate "selection changed" call, and no agent turn spent on one.
        selectedRowIds: selectedRowIdList,
        // Campaign-scope settings go stale the same way rows do (a manual card
        // edit + save while the session is open) — re-seed the agent's before
        // values alongside the rows. GT only; undefined elsewhere.
        ...(bulkCampaign !== undefined ? { campaign: bulkCampaign } : {}),
      });
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkAgent.sessionId, bulkDigest, selectedRowIdList, bulkCampaign, yahooAlreadyLaunched]);

  const confirmBulkPreview = useCallback(async (previewId) => {
    await bulkAgent.approvePreview(previewId, 'click');
  }, [bulkAgent]);

  const dismissBulkPreview = useCallback((previewId) => {
    // GroundTruth: the card stays, marked Cancelled, and the backend tells the
    // agent — a chat message here would close any open choice card.
    if (bulkAgent.gtChat) {
      bulkAgent.dismissPreview?.(previewId);
      return;
    }
    // Mark it server-side FIRST: without the marker, a session resume hands
    // the "pending" preview back and its Confirm card resurrects (UAT 0819).
    bulkAgent.dismissPreview?.(previewId);
    // Telling the agent is what makes Cancel meaningful — otherwise it sits
    // waiting on an approval that will never come.
    bulkAgent.sendMessage(`I'm not applying preview ${previewId}. Don't stage it.`);
  }, [bulkAgent]);

  // Undo on the latest batch's result note. On GroundTruth the proposal card
  // that batch came from is marked Undone once the revert actually landed — and
  // only when the snapshot being restored IS that batch's (a Re-check since
  // then replaces the undo entry, and reverting it undoes nothing of the batch).
  const undoBulkBatch = useCallback(async () => {
    const previewId = bulkStageNote?.previewId;
    const ownsSnapshot = Boolean(previewId) && undoEntry?.previewId === previewId;
    // Undoing a row-op batch changes which rows exist; that is the surface's
    // own doing, not a stale plan, so the conversation (and the card about to
    // say Undone) must survive it.
    const covered = Array.isArray(undoEntry?.previewIds) && undoEntry.previewIds.length
      ? undoEntry.previewIds : [previewId];
    // Any batch the snapshot reverts may have added or deleted rows — not only
    // this note's own (an earlier failed batch rode the same save).
    const armed = ownsSnapshot && covered.some((id) => (rowOpsByPreviewRef.current.get(id) || 0) > 0);
    if (armed) bulkAgent.expectRowChange?.();
    const ok = await revertUndo();
    if (!ok && armed) bulkAgent.cancelExpectedRowChange?.();
    // Every proposal that save committed is reverted with it — including an
    // earlier batch whose own auto-save had failed.
    if (ok && ownsSnapshot) covered.forEach((id) => bulkAgent.reportProposalOutcome?.(id, 'undone'));
  }, [revertUndo, bulkStageNote, bulkAgent, undoEntry]);

  // Active agent + tool-name pair. Determined by which review branch is
  // live. The AgentSidebar + chip-status useEffects read from this so we
  // don't fork the handler code per branch.
  const reviewAgent = gtQaAllowed ? gtAgent : (mmgyQaAllowed ? mmgyAgent : null);
  const qaAvailable = QA_AGENT_TAB_ENABLED && Boolean(reviewAgent);

  // ── Optimization agent (replaces the Review QA tab) ───────────────────────
  // Reads a finished campaign-optimizer run, summarises it, and applies the
  // suggestions the user picks — straight to the live platform campaign. The hook
  // resolves its own eligibility (needs an optimizer for the destination AND a
  // launched campaign), so the tab can explain itself when it is unavailable
  // instead of failing on open.
  const optimizeAgent = useOptimizationSession({
    accountId,
    userId,
    campaignId,
    destination: destination || (isGt ? 'groundtruth' : summary?.platform) || null,
    campaignName,
    // The same `optimize-campaign` gate the Optimize Campaign button reads.
    accountSettings,
    enabled: aiOpen && agentMode === 'optimize',
  });
  const optimizeReady = optimizeAgent.phase === OPT_PHASE.READY;

  // ── Campaign run history (the Runs tab) ─────────────────────────────────
  // The Campaign Grid's detail panel used to own this list. It is now a tab of
  // this sidebar, with its own message hydration + polling + per-suggestion
  // applies — see useCampaignRunHistory. It is deliberately NOT the optimization
  // hook: the history tab never opens an agent session, so listing runs must
  // not auto-start a conversation.
  const campaignRunHistory = useCampaignRunHistory({
    accountId,
    userId,
    campaignId,
    destination: destination || (isGt ? 'groundtruth' : summary?.platform) || null,
    platformCampaignId,
    enabled: aiOpen && agentMode === 'runs' && Boolean(campaignId),
  });

  // A plan can change platform under a mounted pane (a deep-link load stamps
  // the DSP late), and the QA tab can be withheld by flag — either way the user
  // must not be stranded in a mode with no agent behind it.
  useEffect(() => {
    if (!isSelectableMode(agentMode)) setAgentMode('bulk');
    else if (agentMode === 'qa' && !qaAvailable) setAgentMode('bulk');
  }, [agentMode, qaAvailable]);

  const activeAgent = agentMode === 'runs'
    // No conversation behind the Runs tab — the sidebar renders the history
    // component through `fallbackMessage`.
    ? null
    : agentMode === 'bulk'
      ? bulkAgent
      // Until a run is loaded there is no session — the sidebar renders
      // OptimizationRunGate through `fallbackMessage` instead.
      : (agentMode === 'optimize' ? (optimizeReady ? optimizeAgent : null) : reviewAgent);

  const agentModes = useMemo(() => [
    BULK_TAB,
    // Review QA is hidden by flag, not deleted — see agent/agentTabs.js. The
    // reviewer hooks above are still wired and still work; only this entry is
    // withheld.
    ...(QA_AGENT_TAB_ENABLED ? [{
      key: 'qa',
      label: 'Review QA',
      title: 'Walk the validator findings and suggest fixes',
      available: qaAvailable,
      unavailableReason: isGt
        ? 'The GroundTruth CTS reviewer is only available to accounts with GroundTruth enabled — bulk edit works here'
        : (isMmgyClw
          ? 'The MMGY reviewer is only available to MMGY accounts — bulk edit works here'
          : 'No reviewer agent for this platform — bulk edit works here'),
    }] : []),
    {
      key: 'optimize',
      label: 'Optimization',
      title: 'Review campaign analysis and apply the suggestions you pick',
      // Always selectable: the panel behind it is what explains an ineligible
      // campaign, and a reason the user can read beats a disabled tab whose
      // tooltip they have to find. The one thing gated here is a destination the
      // optimizer has never supported.
      available: !isGt,
      unavailableReason: 'GroundTruth campaigns have no optimizer — bulk edit works here',
    },
    {
      ...RUNS_TAB,
      // Run history needs a campaign to key messages on. The review pane always
      // has one in practice; the disabled state is the safety rail.
      available: Boolean(campaignId),
      unavailableReason: 'Upload a plan to create a campaign first — run history starts after that',
    },
  ], [qaAvailable, isGt, isMmgyClw, campaignId]);
  const activeAgentTools = isGt
    ? { applyTool: 'apply_config_edit', commitTool: 'commit_config_edits' }
    : { applyTool: 'apply_plan_edit', commitTool: 'commit_plan_edits' };
  // Per-chip status. Apply sends a structured request through the chat
  // so the agent calls apply_config_edit. On the SSE review_cts.updated
  // that follows, we flip every newly-applied chip to 'applied'.
  // Dismiss just hides a chip + tells the agent to skip.
  const [suggestionStatus, setSuggestionStatus] = useState({}); // key → 'applying' | 'staged' | 'applied' | 'dismissed'
  const [suggestionErrors, setSuggestionErrors] = useState({}); // key → message

  const applySuggestion = useCallback((s) => {
    // Read-only after launch: the plan is frozen, so the agent
    // can still answer questions but apply_*_edit must not fire.
    if (alreadyLaunched) return;
    if (!activeAgent) return;
    if (suggestionStatus[s.key] === 'applying' || suggestionStatus[s.key] === 'applied') return;
    setSuggestionStatus((prev) => ({ ...prev, [s.key]: 'applying' }));
    // Send a structured apply request via chat. The agent stages the
    // mutation in-memory, then commits to persist + re-validate. The
    // original source upload (Excel for GT, PDF/Excel IO for MMGY) is
    // never touched — edits operate on the parsed JSON only.
    const valueJson = JSON.stringify(s.suggested_value);
    const { applyTool, commitTool } = activeAgentTools;
    activeAgent.sendMessage(
      `Apply this suggested edit:\n`
      + `1. Call ${applyTool} with path="${s.path}", value=${valueJson}, approval_source="user_clicked_apply".\n`
      + `2. Call ${commitTool} to persist + revalidate.\n`
      + `3. Summarise what changed and whether the new validator output cleared the original finding.`,
    );
  }, [activeAgent, activeAgentTools, suggestionStatus, alreadyLaunched]);

  const applyAllSuggestions = useCallback((pending) => {
    if (alreadyLaunched) return;
    if (!activeAgent) return;
    if (!pending || pending.length === 0) return;
    setSuggestionStatus((prev) => {
      const next = { ...prev };
      for (const s of pending) next[s.key] = 'applying';
      return next;
    });
    const { applyTool, commitTool } = activeAgentTools;
    activeAgent.sendMessage(
      `Apply all ${pending.length} pending suggested edits in a single batch:\n`
      + `1. Call ${applyTool} for each pending edit with approval_source: 'user_clicked_apply'. `
      + `These are all staged in memory and are fast — no GCS round-trip per call.\n`
      + `2. After all are staged, call ${commitTool} exactly ONCE to persist + revalidate.\n`
      + `3. After commit, summarise what changed and whether the validator output improved.\n\n`
      + `Pending edits:\n`
      + pending.map((s, i) => `${i + 1}. ${s.path} → ${JSON.stringify(s.suggested_value)}`).join('\n'),
    );
  }, [activeAgent, activeAgentTools, alreadyLaunched]);

  const dismissSuggestion = useCallback((s) => {
    if (!activeAgent) return;
    setSuggestionStatus((prev) => ({ ...prev, [s.key]: 'dismissed' }));
    activeAgent.sendMessage(
      `Dismiss the suggested edit for ${s.path}. Skip it for now and continue.`,
    );
  }, [activeAgent]);

  const clearSuggestionError = useCallback((key) => {
    setSuggestionErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // ─── Bulk fix: union grid edits + agent suggestions ─────────
  // pendingBulkFixes is the de-duped list of (name, field, value) triples
  // we will POST to /api/v2/cts-review/fix-by-name. Grid edits win when
  // both sources touch the same (adgroup_name, field) so the user's
  // typed-in value takes priority over the LLM's suggestion.
  const pendingBulkFixes = useMemo(() => {
    if (!isGt) return [];
    const adgroups = (ctsReview?.result?.adgroups) || [];
    const collections = {
      adgroups,
      creatives:    (ctsReview?.result?.creatives) || [],
      creative_new: (ctsReview?.result?.creative_new) || [],
      dcm_tags:     (ctsReview?.result?.dcm_tags) || [],
      tags:         (ctsReview?.result?.tags) || [],
      celtra_tags:  (ctsReview?.result?.celtra_tags) || [],
      innovid_tags: (ctsReview?.result?.innovid_tags) || [],
    };
    const dedup = new Map(); // `${nameLower}|${field}` -> { names:[name], field_name, new_value, source }

    // 1) Grid edits. rowId is `gt-ag-${idx}` (see buildGtReviewRows).
    for (const [rowId, fields] of Object.entries(editedRows || {})) {
      const m = /^gt-ag-(\d+)$/.exec(rowId);
      if (!m) continue;
      const ag = adgroups[Number(m[1])];
      if (!ag) continue;
      const name = ag.ad_group_name || ag.name;
      if (!name) continue;
      for (const [rowKey, raw] of Object.entries(fields)) {
        const field = GT_ROW_TO_AG[rowKey] || rowKey;
        // Reuse the numeric coercion saveReviewEdits uses so the
        // fixer doesn't get a string where the validator expects a Number.
        const editorType = GT_FIELD_EDITORS[rowKey];
        let value = raw;
        if (editorType === 'currency' || editorType === 'number') {
          const n = Number(String(raw).replace(/[^0-9.\-]/g, ''));
          value = Number.isFinite(n) ? n : raw;
        }
        const k = `${String(name).toLowerCase()}|${field}`;
        dedup.set(k, { names: [name], field_name: field, new_value: value, source: 'grid' });
      }
    }

    // 2) Pending agent suggestions. Only handle simple paths of the form
    //    `<collection>[N].<field>` — nested paths (e.g. targeting.zips[3])
    //    are skipped for v1 since they don't fit the fixer's flat schema.
    const suggestions = gtAgent?.suggestions || [];
    for (const s of suggestions) {
      const status = suggestionStatus[s.key];
      if (status === 'applying' || status === 'applied' || status === 'dismissed') continue;
      const path = String(s.path || '');
      const pm = /^([a-zA-Z_][a-zA-Z0-9_]*)\[(\d+)\]\.([a-zA-Z_][a-zA-Z0-9_]*)$/.exec(path);
      if (!pm) continue;
      const [, collection, idxStr, field] = pm;
      const arr = collections[collection];
      if (!arr) continue;
      const entity = arr[Number(idxStr)];
      if (!entity) continue;
      const name = entity.ad_group_name || entity.name
        || entity.creative_name || entity.placement_name;
      if (!name) continue;
      const k = `${String(name).toLowerCase()}|${field}`;
      if (dedup.has(k)) continue; // grid edit wins
      dedup.set(k, { names: [name], field_name: field, new_value: s.suggested_value, source: 'agent', key: s.key });
    }

    return Array.from(dedup.values());
  }, [isGt, ctsReview, editedRows, gtAgent?.suggestions, suggestionStatus]);

  // Clear toast timer on unmount so a late setBulkFixToast doesn't fire
  // after the component is gone.
  useEffect(() => () => {
    if (bulkFixToastTimer.current) clearTimeout(bulkFixToastTimer.current);
  }, []);

  const runBulkFix = useCallback(async () => {
    if (alreadyLaunched || bulkFixRunning || pendingBulkFixes.length === 0) return;
    setBulkFixRunning(true);
    if (bulkFixToastTimer.current) {
      clearTimeout(bulkFixToastTimer.current);
      bulkFixToastTimer.current = null;
    }
    setBulkFixToast(null);
    // Flip agent suggestion chips to 'applying' so they reflect the
    // deterministic path is in-flight (parallel to the agent's own
    // status events for apply_config_edit).
    const agentKeys = pendingBulkFixes.filter((f) => f.source === 'agent').map((f) => f.key);
    if (agentKeys.length) {
      setSuggestionStatus((prev) => {
        const next = { ...prev };
        for (const k of agentKeys) next[k] = 'applying';
        return next;
      });
    }
    try {
      const body = {
        accountId,
        userId,
        platformAccountId,
        campaignId,
        fixes: pendingBulkFixes.map(({ names, field_name, new_value }) => ({ names, field_name, new_value })),
        ...(appliedConfigGcsPath
          ? { configJsonGcsPath: appliedConfigGcsPath }
          : { campaignConfig: ctsReview?.result || null }),
      };
      const res = await api.post(getApiUrl('/api/v2/cts-review/fix-by-name'), body);
      const data = res.data || {};
      const appliedCount = (data.changes || []).length;
      const notFoundCount = (data.not_found || []).length;
      if (data.result) {
        // appliedConfigGcsPath is the campaign revision the server saved this
        // fix as (same content as fixed_config_gcs_path, but it is the head a
        // resume restores); absent when the batch changed nothing.
        onReviewCtsUpdated?.(
          data,
          data.appliedConfigGcsPath || data.fixed_config_gcs_path || null,
          { headAt: data.headAt || null },
        );
      }
      // Flip applied chips. Match by (name, field) so a single suggestion
      // whose path collided with a grid edit (skipped above) is left as-is.
      const changedKeys = new Set((data.changes || []).map((c) => `${String(c.name).toLowerCase()}|${c.field_name}`));
      const appliedAgentKeys = pendingBulkFixes
        .filter((f) => f.source === 'agent' && changedKeys.has(`${f.names[0].toLowerCase()}|${f.field_name}`))
        .map((f) => f.key);
      if (appliedAgentKeys.length) {
        setSuggestionStatus((prev) => {
          const next = { ...prev };
          for (const k of appliedAgentKeys) next[k] = 'applied';
          return next;
        });
      }
      setBulkFixToast({
        kind: appliedCount > 0 ? 'success' : 'warn',
        text: `${appliedCount} applied${notFoundCount ? ` · ${notFoundCount} not found` : ''}`,
      });
    } catch (err) {
      // Roll back any 'applying' flips so the user can retry.
      if (agentKeys.length) {
        setSuggestionStatus((prev) => {
          const next = { ...prev };
          for (const k of agentKeys) delete next[k];
          return next;
        });
      }
      setBulkFixToast({
        kind: 'error',
        text: err?.response?.data?.error || err.message || 'Bulk fix failed',
      });
    } finally {
      setBulkFixRunning(false);
      bulkFixToastTimer.current = setTimeout(() => setBulkFixToast(null), 4000);
    }
  }, [
    alreadyLaunched, bulkFixRunning, pendingBulkFixes,
    accountId, userId, platformAccountId, campaignId,
    appliedConfigGcsPath, ctsReview, onReviewCtsUpdated,
  ]);

  // When the agent applies an edit, the backend sends review_cts.updated
  // with the fresh validation payload + new versioned Excel GCS path +
  // new review_cts JSON GCS path. Bubble all three up via
  // onReviewCtsUpdated so the parent can refresh the grid AND swap
  // launch path. Also flip any matching chips to 'applied'.
  useEffect(() => {
    if (!gtAgent.latestReviewCts) return;
    if (onReviewCtsUpdated) {
      onReviewCtsUpdated(gtAgent.latestReviewCts, gtAgent.latestConfigGcsPath, {
        committedAt: gtAgent.latestCommittedAt || null,
      });
    }
    const applied = gtAgent.latestAppliedEdits || [];
    if (applied.length) {
      setSuggestionStatus((prev) => {
        const next = { ...prev };
        for (const e of applied) next[e.key] = 'applied';
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gtAgent.latestReviewCts]);

  // When apply_*_edit succeeds, the edit is STAGED — flip the chip
  // from 'applying' to 'staged'. The matching review_cts.updated /
  // final_plan.updated event (after commit_*_edits) flips staged →
  // applied via latestAppliedEdits.
  useEffect(() => {
    const staged = activeAgent?.lastEditStaged;
    if (!staged || !staged.edit_key) return;
    setSuggestionStatus((prev) => ({ ...prev, [staged.edit_key]: 'staged' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAgent?.lastEditStaged]);

  // When apply_*_edit fails, flip the matching chip out of
  // 'applying' (back to no-status so the user can retry) and stash
  // the error for display below the chip.
  useEffect(() => {
    const fail = activeAgent?.lastApplyFailure;
    if (!fail) return;
    const key = fail.edit_key;
    if (key) {
      setSuggestionStatus((prev) => {
        // Clear 'applying' if that was the state; leave applied/dismissed alone.
        if (prev[key] !== 'applying') return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setSuggestionErrors((prev) => ({ ...prev, [key]: fail.error }));
    } else {
      // No edit_key (agent called with missing fields) — clear all
      // 'applying' chips so none are stuck.
      setSuggestionStatus((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) if (next[k] === 'applying') delete next[k];
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAgent?.lastApplyFailure]);

  // Clear stuck "applying…" chips when the agent finishes a turn.
  // Apply-all flows are particularly affected: if the first edit
  // fails, the agent typically doesn't call apply_*_edit for the
  // rest, so those chips never get an apply.failed event. Once the
  // agent is back to waiting_for_user, anything still 'applying' is
  // unresolved — clear it so the user can retry or dismiss.
  useEffect(() => {
    if (activeAgent?.status !== 'waiting_for_user' && activeAgent?.status !== 'streaming') return;
    setSuggestionStatus((prev) => {
      const stuck = Object.entries(prev).filter(([, v]) => v === 'applying');
      if (stuck.length === 0) return prev;
      const next = { ...prev };
      for (const [k] of stuck) {
        delete next[k];
        // Park a generic "not applied this turn" error so the user
        // knows the chip is back to clickable. Real per-edit failures
        // still overwrite this via lastApplyFailure.
        setSuggestionErrors((perr) => (
          perr[k] ? perr : { ...perr, [k]: 'The agent didn\'t apply this edit in the last turn. Click to retry, or dismiss.' }
        ));
      }
      return next;
    });
  }, [activeAgent?.status]);


  // Sidekick agent panel width (user-resizable from the left edge). Persisted
  // independently of the column widths since the panel applies regardless
  // of which tab / DV360 vs GT branch is active.
  const [aiPanelWidth, setAiPanelWidth] = useState(() => {
    try {
      const raw = sessionStorage.getItem('clw_ai_panel_width');
      const n = raw ? parseInt(raw, 10) : NaN;
      // 520 default: the bulk editor's confirmation card carries a
      // before -> after diff table, which is unreadable at 360.
      return Number.isFinite(n) && n >= 280 ? n : 520;
    } catch { return 520; }
  });
  const persistAiPanelWidth = useCallback((next) => {
    try { sessionStorage.setItem('clw_ai_panel_width', String(next)); } catch {}
  }, []);
  // Drag handle on the LEFT edge of the AI sidebar — width grows as the
  // cursor moves LEFT, so dx is inverted.
  const startAiResize = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = aiPanelWidth;
    document.body.classList.add('clw-col-resizing');
    const onMove = (ev) => {
      const dx = startX - ev.clientX;
      const next = Math.max(280, Math.min(900, Math.round(startWidth + dx)));
      setAiPanelWidth(next);
      persistAiPanelWidth(next);
    };
    const onUp = () => {
      document.body.classList.remove('clw-col-resizing');
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [aiPanelWidth, persistAiPanelWidth]);


  // ── MMGY agent findings overlay ─────────────────────────────
  // Each `suggest_plan_edit` from the MMGY DV360/TTD agent is doing two
  // jobs: chip-in-sidebar + finding-on-the-grid. We map the agent's
  // suggestions to row.annotations entries so the existing cell color
  // pipeline (cell-m / cell-q / cell-i CSS) and issues-bar rendering
  // pick them up for free.
  const effectiveReviewRows = useMemo(() => {
    if (!isMmgyClw || !mmgyAgent?.suggestions?.length) return reviewRows;
    const byRow = new Map();
    for (const s of mmgyAgent.suggestions) {
      if (!s.row_id || !s.field_key) continue;
      const sev = String(s.severity || 'warning').toLowerCase();
      // type follows the existing CSS convention: m=error, q=warning, i=info.
      const type = sev === 'error' ? 'm' : sev === 'info' ? 'i' : 'q';
      if (!byRow.has(s.row_id)) byRow.set(s.row_id, {});
      byRow.get(s.row_id)[s.field_key] = {
        type,
        msg: s.reason,
        agentFinding: true,
        path: s.path,
      };
    }
    if (byRow.size === 0) return reviewRows;
    return reviewRows.map((r) => {
      const findings = byRow.get(r.rowId);
      if (!findings) return r;
      return { ...r, annotations: { ...(r.annotations || {}), ...findings } };
    });
  }, [reviewRows, mmgyAgent?.suggestions, isMmgyClw]);

  // Filter rows by search + issues-only — sources from the overlay
  // so MMGY findings drive the issues-only filter too.
  const visibleRows = useMemo(() => {
    let rows = effectiveReviewRows;
    if (gridSearch.trim()) {
      const q = gridSearch.toLowerCase();
      rows = rows.filter((r) =>
        String(r.name || '').toLowerCase().includes(q)
        || String(r.market || '').toLowerCase().includes(q)
        || String(r.tactic || '').toLowerCase().includes(q)
      );
    }
    if (filterIssuesOnly) {
      rows = rows.filter((r) => Object.keys(r.annotations || {}).length > 0);
    }
    return rows;
  }, [effectiveReviewRows, gridSearch, filterIssuesOnly]);

  const totalImps = useMemo(() => reviewRows.reduce((s, r) => s + (Number(r.imps) || 0), 0), [reviewRows]);
  // Pending edits = inline cell edits + staged list ops (adds / edits /
  // removes). Both are flushed by the same saveReviewEdits call so the
  // button counter and disabled-state should reflect either kind of change.
  const pendingOpCount = useMemo(() => {
    let n = 0;
    for (const row of Object.values(pendingListOps || {})) {
      for (const arr of Object.values(row || {})) n += (arr?.length || 0);
    }
    return n;
  }, [pendingListOps]);
  // Row ops count too — this drives the Save button's disabled state, so
  // omitting them would leave a staged row delete unsaveable.
  const pendingEditCount = Object.keys(editedRows).length + pendingOpCount + (pendingRowOpCount || 0);

  // 4-card summary strip — sourced from effectiveReviewRows so MMGY
  // agent findings contribute to the counts that drive the issues-bar
  // visibility + the summary tiles.
  const okCount = effectiveReviewRows.length;
  const qCount = effectiveReviewRows.reduce((s, r) => s + Object.values(r.annotations || {}).filter((a) => a.type === 'q').length, 0);
  const mCount = effectiveReviewRows.reduce((s, r) => s + Object.values(r.annotations || {}).filter((a) => a.type === 'm').length, 0);

  const detailRow = detailRowId ? reviewRows.find((r) => r.rowId === detailRowId) : null;

  if (!active) return null;
  const hasData = isGt ? Boolean(ctsReview?.result?.adgroups?.length) : Boolean(launcherResult);
  // A GT envelope can be present and still carry no plan — the conversion
  // failed. That is NOT the same as "nothing has run yet", which is all this
  // pane used to say, so a dead run looked like a slow one.
  const ctsFailure = isGt ? gtCtsFailure(ctsReview) : null;
  if (!hasData || reviewRows.length === 0) {
    // The header stays a HEADER — badge, campaign name, a one-line status, the
    // exit. The error/empty content used to render inside it, cramming a
    // full-width hero card into the title column while the pane's whole body
    // sat empty below; the hero now lives in the body, centered, like every
    // other empty state.
    const statusLine = summaryError
      ? 'Summary unavailable'
      : ctsFailure
        ? 'Plan build failed'
        : (isGt ? 'No CTS review data available yet.' : 'No parsed plan data available yet.');
    return (
      <div className={`clw-pane review active`}>
        <div className="clw-review-header">
          <div className="clw-rh-badge">Review &amp; Approve</div>
          <div>
            <div className="clw-rh-title">{campaignName || 'Campaign'}</div>
            <div className="clw-rh-sub">{statusLine}</div>
          </div>
          <div className="clw-rh-right">
            {initialUrlCampaign ? (
              // Deep-linked (from the grid, the launch board or a campaign
              // link): there is no upload step to go back to, so offer the same
              // exit the populated header does. Without it a campaign whose plan
              // has no rows strands the user on a dead-end pane.
              <button
                type="button"
                className="clw-btn clw-btn-secondary clw-btn-sm"
                onClick={() => reviewNavigate(backTo)}
                title={backTarget.title}
              >
                {backLabel}
              </button>
            ) : (
              <button className="clw-btn clw-btn-secondary clw-btn-sm" onClick={onBack}>← Back to upload</button>
            )}
          </div>
        </div>

        <div className="clw-review-empty-body">
          {summaryError ? (
            <div className="clw-ps-hero clw-ps-hero-fail">
              <div className="clw-ps-hero-icon clw-ps-hero-icon-fail" aria-hidden="true">!</div>
              <div className="clw-ps-hero-body">
                <div className="clw-ps-hero-title">Could not load parse summary</div>
                <div className="clw-ps-hero-sub">{summaryError}</div>
              </div>
            </div>
          ) : ctsFailure ? (
            <div className="clw-ps-hero clw-ps-hero-fail">
              <div className="clw-ps-hero-icon clw-ps-hero-icon-fail" aria-hidden="true">!</div>
              <div className="clw-ps-hero-body">
                <div className="clw-ps-hero-title">Could not build a plan from this IO</div>
                <div className="clw-ps-hero-sub">{ctsFailure.message}</div>
                {ctsFailure.detail && (
                  <div className="clw-ps-hero-sub">{ctsFailure.detail}</div>
                )}
                {ctsFailure.ioParsed && (
                  <div className="clw-ps-hero-sub">
                    The IO itself was read successfully — only the conversion into a
                    campaign plan failed.
                  </div>
                )}
                {/* No retry here (UAT 0820: don't offer it) — the way out is
                    back to wherever the operator came from, grid-aware like
                    the header's exit. */}
                <div className="clw-ps-hero-actions">
                  <button
                    type="button"
                    className="clw-btn clw-btn-primary clw-btn-sm"
                    onClick={() => reviewNavigate(backTo)}
                    title={backTarget.title}
                  >
                    {backLabel}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="clw-ps-hero">
              <div className="clw-ps-hero-icon" aria-hidden="true">…</div>
              <div className="clw-ps-hero-body">
                <div className="clw-ps-hero-title">Nothing to review yet</div>
                <div className="clw-ps-hero-sub">{statusLine}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const blocking = isGt && Array.isArray(ctsReview?.errors) && ctsReview.errors.length > 0;
  // What one row IS, in the grid footer and the agent sidebar's context line.
  // Google Ads was missing here, so a Search plan's rows were called "line
  // items" — visibly wrong next to the bulk editor, which calls the same rows ad
  // groups (or asset groups on PMax / Demand Gen). Reuses bulkRowLabel so the
  // two can't drift apart again.
  const rowLabel = platformIsYahoo ? 'line flights' : (platformIsGads || platformIsFb)
    ? bulkRowLabel
    : (isGt ? 'ad groups' : (summary?.platform === 'TTD' ? 'ad groups' : 'line items'));
  // A Facebook plan is one campaign with ad sets under it — no insertion orders.
  // Count-aware: "1 IO" / "2 IOs", never "1 IOs" (a Google Ads Search plan is
  // usually exactly one).
  const ioLabel = (() => {
    const one = summary?.ioCount === 1;
    if (isGt || platformIsFb || platformIsYahoo) return one ? 'campaign' : 'campaigns';
    if (summary?.platform === 'TTD') return one ? 'campaign' : 'campaigns';
    return one ? 'IO' : 'IOs';
  })();

  // Manual audience refresh is offered for any in-review TTD/DV360 plan,
  // regardless of whether a previous refresh finished — so the user can always
  // re-resolve audiences against the live platform (force:true via
  // onRefreshAudiences). Launched campaigns are read-only (the backend rejects
  // the mutation with 409), so the control is hidden there.
  const audStatus = bulkAudienceRefresh?.status || 'idle';
  const canRefreshAudiences = !isGt && !alreadyLaunched
    && (summary?.platform === 'TTD' || summary?.platform === 'DV360');
  // Render the chip when there's a status to show OR a manual refresh to offer.
  const showAudChip = audStatus !== 'idle' || canRefreshAudiences;

  return (
    <div
      className={`clw-pane review active${isYahooResult(launcherResult) ? ' yahoo-review' : ''}${detailRow ? ' has-dpanel' : ''}${aiOpen ? ' has-aipanel' : ''}`}
      style={{ '--ai-panel-w': `${aiPanelWidth}px` }}
    >
      {/* Header */}
      <div className="clw-review-header">
        <div className="clw-rh-badge-col">
          <div className="clw-rh-badge">Review &amp; Approve</div>
          <button
            type="button"
            className="clw-btn clw-btn-secondary clw-btn-sm"
            onClick={() => reviewNavigate(backTo)}
            title={backTarget.title}
          >
            {backLabel}
          </button>
        </div>
        <div className="clw-rh-main">
          <div className="clw-rh-title" title={campaignName || summary?.campaignName || 'New campaign'}>{campaignName || summary?.campaignName || 'New campaign'}</div>
          <div className="clw-rh-sub">
            {isGt && <>CTS-validated · </>}
            {fmtInt(reviewRows.length)} {rowLabel}
            {summary?.ioCount ? ` · ${fmtInt(summary.ioCount)} ${ioLabel}` : ''}
            {/* Platform label. For a loaded plan the authoritative platform is
                summary.platform (derived from launcherResult.platform), so it
                wins over `destination` — a create-flow UI-state that
                fetchPlanningReview never sets and can be stale (e.g. a TTD plan
                showing "DV360" after viewing a DV360 campaign). During create
                (no launcherResult yet) summary is null and destination wins. */}
            {' · '}<em>{(isGt ? 'GroundTruth' : (summary?.platform || destination || 'DV360')).toUpperCase()}</em>
            {/* Bulk audience-refresh status — inline next to the platform label.
                The on-load job re-resolves every audience row against the live
                TTD / DV360 API; the grid below stays fully interactive while it
                runs. */}
            {showAudChip && (
              <span className={`clw-aud-refresh-chip --${audStatus}`}>
                {audStatus === 'running' && (
                  <span className="clw-aud-refresh-msg">
                    <span className="clw-aud-refresh-spin" aria-hidden="true">⟳</span>
                    Refreshing audiences…
                  </span>
                )}
                {audStatus === 'done' && (
                  <span className="clw-aud-refresh-msg">
                    ✓ Audiences refreshed{typeof bulkAudienceRefresh?.total === 'number'
                      ? ` — ${bulkAudienceRefresh.matched}/${bulkAudienceRefresh.total} matched`
                      : ''}
                  </span>
                )}
                {audStatus === 'error' && (
                  <span className="clw-aud-refresh-msg" title={bulkAudienceRefresh?.error || 'Unknown error'}>
                    Couldn't refresh audiences
                  </span>
                )}
                {/* Manual refresh — offered in every non-running state
                    (idle / done / error) so users can always re-resolve
                    audiences, even after a prior run completed. Hidden while a
                    run is in flight and on launched (read-only) campaigns. */}
                {audStatus !== 'running' && canRefreshAudiences && (
                  <button
                    type="button"
                    className="clw-aud-refresh-btn"
                    onClick={() => onRefreshAudiences?.()}
                    title="Re-resolve every audience against the platform"
                  >
                    {audStatus === 'error' ? 'Retry' : 'Refresh audiences'}
                  </button>
                )}
              </span>
            )}
          </div>
        </div>
        <div className="clw-rh-right">
          {/* Launch failures and save warnings NO LONGER render here. They are
              multi-sentence, often several at once, and a button cluster is the
              one place on the page with no room for them — the old inline spans
              spilled across the toolbar and hid every message after the first in
              a native `title` tooltip. They render as a full-width strip under
              this header instead; see PlanNotices below. */}
          {/* Original IO download — GT flow only. sourceFile comes from
              planFiles in-session, or the cts-snapshot on cross-device
              resume; when neither has it (pre-snapshot drafts) the
              button simply doesn't render. */}
          {isGt && sourceFile?.gcsPath && (
            <button
              type="button"
              className="clw-btn clw-btn-secondary clw-btn-sm"
              onClick={downloadSourceFile}
              disabled={downloadingSource}
              title={downloadSourceError
                ? `Download failed: ${downloadSourceError} — click to retry`
                : `Download the original IO file (${sourceFile.originalName || sourceFile.gcsPath.split('/').pop()})`}
            >
              {downloadingSource ? 'Preparing…'
                : downloadSourceError ? 'Retry download'
                : '⬇ Download IO file'}
            </button>
          )}
          {/* Original uploads (plan, IO, template) recorded in campaign_files —
              every platform, unlike the GT-only IO button above. */}
          {campaignId && (
            <SourceFilesButton
              campaignId={campaignId}
              accountId={accountId}
              userId={userId}
            />
          )}
          <button
            type="button"
            className="clw-btn clw-btn-secondary clw-btn-sm"
            onClick={() => setSummaryOpen((v) => !v)}
            title={summaryOpen ? 'Collapse summary header' : 'Expand summary header'}
          >
            {summaryOpen ? 'Hide summary ▲' : 'Show summary ▼'}
          </button>
          {!initialUrlCampaign && (
            <button className="clw-btn clw-btn-secondary clw-btn-sm" onClick={onBack}>← Back to upload</button>
          )}
          {/* Field-level change history + undo for launched / imported
              campaigns — every post-launch edit is tracked and reversible. */}
          {alreadyLaunched && (
            <ChangeLogButton
              campaignId={campaignId}
              accountId={accountId}
              userId={userId}
            />
          )}
          {/* Every GT save is a revision; History lists them and restores
              any one (the restore is itself a new revision). Still off for
              DV360 / TTD, whose saves persist through planning-review. */}
          {isGt && (
            <GtVersionHistoryButton
              fetchVersions={fetchGtConfigVersions}
              onRevert={revertGtConfig}
              readOnly={alreadyLaunched}
              disabled={removingCreatives}
            />
          )}
          <button
            className="clw-btn clw-btn-secondary clw-btn-sm"
            onClick={saveReviewEdits}
            disabled={savingReview || removingCreatives || pendingEditCount === 0}
            title={pendingEditCount > 0 ? `${pendingEditCount} edits unsaved` : 'No edits'}
          >
            {savingReview ? 'Saving…' : pendingEditCount > 0 ? `Save ${pendingEditCount} edit${pendingEditCount === 1 ? '' : 's'}` : 'Saved'}
          </button>
          <button
            className="clw-btn clw-btn-launch"
            onClick={approveAndLaunch}
            disabled={localReviewOnly || launching || launchSuccess || alreadyLaunched || blocking || removingCreatives}
            title={
              localReviewOnly ? GT_REVIEW_ONLY_MESSAGE
              : alreadyLaunched ? 'This campaign has already been launched'
              : blocking ? 'Resolve CTS errors before launching'
              : ''
            }
          >
            {localReviewOnly ? 'Local review only — launch disabled'
              : alreadyLaunched ? '✓ Already launched'
              : launchSuccess ? '✓ Launched'
              : launching ? 'Launching…'
              : (isGt ? 'Approve & launch GroundTruth →' : 'Approve & launch →')}
          </button>
        </div>
      </div>

      {/* Save- and launch-time notices, full width and one message per row.
          Errors first: a failed save is a different fact from a plan warning,
          and stacking two strips for one action reads as two failures. */}
      {((launchBlockers?.length > 0) || (launchError && !launching)) && (
        <PlanNotices
          defaultSeverity="error"
          notices={launchBlockers?.length > 0 ? launchBlockers : [launchError]}
          onDismiss={dismissLaunchError}
        />
      )}
      {saveWarnings?.length > 0 && (
        <PlanNotices
          defaultSeverity="warning"
          notices={saveWarnings}
          onDismiss={dismissSaveWarnings}
        />
      )}
      {importWarnings?.length > 0 && (
        <PlanNotices
          defaultSeverity="info"
          notices={importWarnings}
          onDismiss={dismissImportWarnings}
        />
      )}

      {/* GT review: what Additional Instructions / AM notes changed, before
          approval. Renders nothing for a review that ran without instructions.
          Its blocking findings are also in ctsReview.errors, which is what
          keeps Approve & launch disabled. */}
      {isGt && (
        <GtInstructionReviewPanel
          ctsReview={ctsReview}
          onAcknowledge={alreadyLaunched ? null : (issueId) => saveReviewEdits({
            revalidateOnly: true,
            instructionAcks: [issueId],
            captureUndo: true,
            undoLabel: 'Keep current values',
          })}
          busy={savingReview}
        />
      )}

      {/* GT IO review: Upload Creatives progress, outcome and projected
          assignments. Hidden until an upload starts or the plan carries an
          uploaded bundle; blocking findings are also in ctsReview.errors. */}
      {isGt && creativeUpload && (
        <GtCreativeUploadPanel
          ctsReview={ctsReview}
          upload={creativeUploadState || {}}
          onReload={onReloadActiveRevision}
        />
      )}

      {/* Consolidated summary block — formerly the separate summary pane.
          Renders the full parse-summary above the grid: stat tiles, the
          campaign-configuration card, and the per-platform breakdown
          (DV360/TTD: switcher across package/tactic/format/month; GT:
          ad-groups table + QA pivot panel). The existing issues bar
          below still handles per-row jumpable findings. Wrapped in a
          capped-height scrollable container so the grid below stays
          fully visible while reviewers can still see all summary detail. */}
      {summaryOpen && (
      <div className="clw-review-summary-scroll">
      {/* A launched campaign's real delivery, above the plan tiles. Null (and
          so unrendered) for plan-stage campaigns and for anything the grid has
          never pulled — see DeliveredMetricsStrip.js. */}
      <DeliveredMetricsStrip metrics={deliveredMetrics} />
      <ConsolidatedReviewSummary
        slot="tiles"
        isGt={isGt}
        platformSetup={deliveredMetrics}
        platformAudiences={platformAudiences}
        summary={summary}
        summaryError={summaryError}
        launcherResult={launcherResult}
        ctsReview={ctsReview}
        campaignEdits={editedRows?.[GT_CAMPAIGN_ROW_ID]}
        onCampaignEdit={isGt && !alreadyLaunched ? stageCampaignEdit : null}
        platformAccountId={platformAccountId}
        reviewRows={reviewRows}
        creativeFiles={creativeFiles}
        breakdownView={breakdownView}
        setBreakdownView={setBreakdownView}
        qaPivotKey={qaPivotKey}
        setQaPivotKey={setQaPivotKey}
        destination={destination}
        campaignName={campaignName}
        blocking={blocking}
        okCount={okCount}
        qCount={qCount}
        mCount={mCount}
        totalImps={totalImps}
      />
      </div>
      )}

      {isYahooResult(launcherResult) && (
        <YahooFindings result={launcherResult} rows={effectiveReviewRows} onJump={jumpToCell} />
      )}

      {/* Issues bar — DV360/TTD use row.annotations (now including MMGY
          agent findings overlaid via effectiveReviewRows). GT also
          surfaces campaign-level errors/warnings/infos from ctsReview
          that aren't tied to a specific adgroup. The DV360/TTD count
          includes info-level findings (iCount) so an info-only finding
          from the MMGY agent still surfaces the bar. */}
      {!isYahooResult(launcherResult) && (() => {
        if (isGt) {
          return ((ctsReview?.errors?.length || 0) + (ctsReview?.warnings?.length || 0) + (ctsReview?.infos?.length || 0)) > 0;
        }
        // DV360 / TTD: any annotation of any severity opens the bar.
        return effectiveReviewRows.some((r) => Object.keys(r.annotations || {}).length > 0);
      })() && (
        <div className="clw-issues-bar" data-testid="clw-issues-bar">
          <div className="clw-issues-bar-head">
            <span className="title">
              {isGt
                ? `⚠ ${fmtInt((ctsReview?.errors?.length || 0) + (ctsReview?.warnings?.length || 0) + (ctsReview?.infos?.length || 0))} CTS finding${((ctsReview?.errors?.length || 0) + (ctsReview?.warnings?.length || 0) + (ctsReview?.infos?.length || 0)) === 1 ? '' : 's'}${ctsFindingsStale ? ' — from before your last save' : ''}`
                : (() => {
                    const totalAnn = effectiveReviewRows.reduce((s, r) => s + Object.keys(r.annotations || {}).length, 0);
                    return `⚠ ${fmtInt(totalAnn)} flagged item${totalAnn === 1 ? '' : 's'} — click to jump to row`;
                  })()}
            </span>
            {isGt && (
              <span className="clw-bulk-fix-wrap">
                <button
                  className="clw-issues-bulk-fix"
                  onClick={runBulkFix}
                  disabled={bulkFixRunning || pendingBulkFixes.length === 0 || alreadyLaunched}
                  title={
                    pendingBulkFixes.length === 0
                      ? 'No pending edits or agent suggestions to apply'
                      : `Apply ${pendingBulkFixes.length} pending fix${pendingBulkFixes.length === 1 ? '' : 'es'} to all matching ad groups (bypasses the LLM agent)`
                  }
                >
                  {bulkFixRunning
                    ? 'Applying…'
                    : (pendingBulkFixes.length > 0
                        ? `Bulk Fix (${pendingBulkFixes.length})`
                        : 'Bulk Fix')}
                </button>
                {/* Re-check — run the validator against the plan as it stands.
                    The findings otherwise only ever refresh as a side effect of
                    a save, and Save is disabled once nothing is pending: a user
                    staring at a finding they have already fixed had no control
                    left to press, and Approve & launch stays disabled while it
                    stands. Deliberately available with zero pending edits. */}
                <button
                  className="clw-issues-bulk-fix"
                  onClick={() => saveReviewEdits({
                    revalidateOnly: true,
                    // It rides the ordinary save, so any pending edit is
                    // committed on the way through. That must be undoable like
                    // every other write — a control labelled "Re-check" is the
                    // last place a user expects to lose a way back.
                    captureUndo: true,
                    undoLabel: 'Re-check',
                  })}
                  // `undoing` matters: revertUndo sets it instead of
                  // savingReview, and without it Re-check is the one enabled
                  // write control during an undo — two PUTs racing the same
                  // endpoint.
                  disabled={savingReview || bulkFixRunning || undoing || removingCreatives || alreadyLaunched}
                  title={'Re-run CTS validation against the plan as it stands. '
                    + 'Use this when a finding quotes a value the plan no longer has. '
                    + 'Any unsaved edits are saved first.'}
                >
                  {savingReview ? 'Re-checking…' : 'Re-check'}
                </button>
                {bulkFixToast && (
                  <span className={`clw-bulk-fix-toast ${bulkFixToast.kind}`}>{bulkFixToast.text}</span>
                )}
              </span>
            )}
            <button className="clw-issues-toggle" onClick={() => setIssuesBarOpen((v) => !v)}>
              {issuesBarOpen ? 'Hide ▲' : 'Show ▼'}
            </button>
          </div>
          {issuesBarOpen && (
            <div className="clw-issues-list">
              {isGt ? (
                <>
                  {/* Per-row jumpable items: every ctsReview.issues entry
                      that we can bind to an ad group + field. Clicking
                      scrolls the matching row into view, opens the cell
                      detail panel, and briefly flashes the cell. Sorted
                      by severity (errors → warnings → infos) so the
                      blocking findings are always at the top. */}
                  {[...(ctsReview?.issues || [])]
                    .sort((a, b) => {
                      const rank = (s) => (String(s || '').toLowerCase() === 'error' ? 0 : String(s || '').toLowerCase() === 'warning' ? 1 : 2);
                      return rank(a.severity) - rank(b.severity);
                    })
                    .map((it, i) => {
                    const target = it.adgroup_name || it.ad_group_name || it.adGroupName || it.target;
                    const matchRow = target
                      ? (reviewRows.find((r) => r.name === target)
                         || reviewRows.find((r) => normalizeAdgroupName(r.name) === normalizeAdgroupName(target)))
                      : null;
                    const field = resolveGtIssueField(it);
                    const sev = String(it.severity || 'warning').toLowerCase();
                    const type = sev === 'error' ? 'm' : sev === 'info' ? 'i' : 'q';
                    const dotCls = type;
                    return (
                      <div
                        key={`gt-issue-${i}`}
                        className={`clw-issue-bullet ${type} ${matchRow ? 'jumpable' : ''}`}
                        onClick={matchRow ? () => jumpToCell(matchRow.rowId, field) : undefined}
                        title={matchRow ? 'Jump to row + open cell detail' : undefined}
                      >
                        <span className={`clw-issue-dot ${dotCls}`} />
                        <span className="clw-issue-text">
                          {displayFinding(it.message || it.msg) || 'Validation issue'}
                          {matchRow && <> — <em>{matchRow.name}</em></>}
                          <span className="clw-issue-hint">{it.category || sev}</span>
                        </span>
                        {matchRow && <span className="clw-issue-arrow">→</span>}
                      </div>
                    );
                  })}
                  {/* Campaign-level errors/warnings/infos without an
                      adgroup binding (e.g. Location Lists rule). Render
                      as plain bullets — no jump target. Falls back to
                      the validator's flat .errors/.warnings/.infos
                      arrays if .issues isn't populated. */}
                  {(!ctsReview?.issues?.length) && (
                    <>
                      {(ctsReview?.errors || []).map((msg, i) => (
                        <div key={`gt-err-${i}`} className="clw-issue-bullet m">
                          <span className="clw-issue-dot m" />
                          <span className="clw-issue-text">{displayFinding(msg)}<span className="clw-issue-hint">error</span></span>
                        </div>
                      ))}
                      {(ctsReview?.warnings || []).map((msg, i) => (
                        <div key={`gt-warn-${i}`} className="clw-issue-bullet q">
                          <span className="clw-issue-dot q" />
                          <span className="clw-issue-text">{displayFinding(msg)}<span className="clw-issue-hint">warning</span></span>
                        </div>
                      ))}
                      {(ctsReview?.infos || []).map((msg, i) => (
                        <div key={`gt-info-${i}`} className="clw-issue-bullet">
                          <span className="clw-issue-dot" />
                          <span className="clw-issue-text">{msg}<span className="clw-issue-hint">info</span></span>
                        </div>
                      ))}
                    </>
                  )}
                </>
              ) : (
                effectiveReviewRows.flatMap((r) => Object.entries(r.annotations || {}).map(([field, ann]) => (
                  <div key={`${r.rowId}-${field}`} className={`clw-issue-bullet ${ann.type} jumpable`} onClick={() => jumpToCell(r.rowId, field)}>
                    <span className={`clw-issue-dot ${ann.type}`} />
                    <span className="clw-issue-text">
                      {ann.msg || 'Field needs attention'} — <em>{r.name}</em>
                      <span className="clw-issue-hint">{field}{ann.agentFinding ? ' · agent finding' : ''}</span>
                    </span>
                    <span className="clw-issue-arrow">→</span>
                  </div>
                )))
              )}
            </div>
          )}
        </div>
      )}

      {/* Grid — shared with the Launch QA page (see ReviewGrid.js). */}
      <ReviewGrid
        reviewRows={reviewRows}
        visibleRows={visibleRows}
        getRowValue={getRowValue}
        editedRows={editedRows}
        groupCols={groupCols}
        groupKeys={groupKeys}
        cols={cols}
        effectiveActiveGroup={effectiveActiveGroup}
        isGt={isGt}
        activeGroup={activeGroup}
        setActiveGroup={setActiveGroup}
        gridSearch={gridSearch}
        setGridSearch={setGridSearch}
        filterIssuesOnly={filterIssuesOnly}
        setFilterIssuesOnly={setFilterIssuesOnly}
        detailRow={detailRow}
        detailCellField={detailCellField}
        setDetailRowId={setDetailRowId}
        setDetailCellField={setDetailCellField}
        editingCell={editingCell}
        setEditingCell={setEditingCell}
        editingDraft={editingDraft}
        setEditingDraft={setEditingDraft}
        commitCellEdit={commitCellEdit}
        cancelCellEdit={cancelCellEdit}
        editableFields={editableFields}
        fieldEditors={fieldEditors}
        fieldOptions={fieldOptions}
        readOnlyReasons={readOnlyReasons}
        creativeUpload={creativeUpload || null}
        creativeRemove={creativeRemove || null}
        ctsReview={ctsReview}
        pendingDeletions={pendingDeletions}
        toggleListDeletion={toggleListDeletion}
        listFields={listFields}
        pendingListOps={pendingListOps}
        addListItem={addListItem}
        editListItem={editListItem}
        onLookup={onLookup}
        onRefreshLookup={onRefreshLookup}
        // Field editing stays available after launch (launched edits route
        // through saveLaunchedEdits → edit-campaign requests). Row removal is
        // copy-grid-only, so this surface has no row-op gate to preserve.
        alreadyLaunched={platformIsYahoo && alreadyLaunched}
        // Row multi-select — scopes every edit the bulk editor proposes.
        selectedRowIds={selectedRowIds}
        onSelectionChange={setSelectedRowIds}
        campaignName={campaignName}
        exportSourceName={exportSourceName}
        showAgentButton={!platformIsYahoo || !alreadyLaunched}
        aiOpen={aiOpen && !(platformIsYahoo && alreadyLaunched)}
        setAiOpen={setAiOpen}
        widthsKey={platformIsYahoo ? 'clw_col_widths_yahoo' : isGt ? 'clw_col_widths_gt' : 'clw_col_widths_dv'}
        showBreakdownTab
        breakdownSlot={(
                        <div className="clw-review-grid-pivot">
                          <ConsolidatedReviewSummary
                            slot="pivot"
                            isGt={isGt}
                            summary={summary}
                            launcherResult={launcherResult}
                            ctsReview={ctsReview}
                            reviewRows={reviewRows}
                            creativeFiles={creativeFiles}
                            breakdownView={breakdownView}
                            setBreakdownView={setBreakdownView}
                            qaPivotKey={qaPivotKey}
                            setQaPivotKey={setQaPivotKey}
                            destination={destination}
                            campaignName={campaignName}
                            blocking={blocking}
                            okCount={okCount}
                            qCount={qCount}
                            mCount={mCount}
                            totalImps={totalImps}
                          />
                        </div>
        )}
        summaryBar={(
          <div className="clw-gsumbar">
            <span><strong>{fmtInt(visibleRows.length)}</strong> of {fmtInt(reviewRows.length)} {rowLabel}</span>
            {isGt ? (
              <span>Budget: <strong>{summary?.totalBudget != null ? fmtCurrency(summary.totalBudget) : '—'}</strong></span>
            ) : (
              <span>Impressions: <strong>{fmtInt(totalImps)}</strong></span>
            )}
            <span>Platform: <strong>{(destination || (isGt ? 'GroundTruth' : summary?.platform) || '').toUpperCase()}</strong></span>
            {pendingEditCount > 0 && <span style={{ color: 'var(--gold)' }}><strong>{pendingEditCount}</strong> unsaved edit{pendingEditCount === 1 ? '' : 's'}</span>}
            {launchError && <span style={{ color: 'var(--red)' }}>{launchError}</span>}
          </div>
        )}
      >
          <AgentSidebar
            open={aiOpen && !(platformIsYahoo && alreadyLaunched)}
            setOpen={setAiOpen}
            onResizeStart={startAiResize}
            session={activeAgent}
            modes={agentModes}
            mode={agentMode}
            setMode={setAgentMode}
            feedItems={agentMode === 'runs' ? null : agentMode === 'bulk' ? [
              // Each notice carries the timestamp of the thing it reports, so the
              // sidebar can interleave it with the conversation instead of pinning
              // it under everything. See agent/feedOrder.js.
              //
              // The confirmation card is the exception: `pin: 'end'` keeps it
              // BELOW the agent's explanation, and previewReadyToShow holds it back
              // until that explanation exists. A preview arrives on the tool
              // result, seconds before the agent finishes describing it, so by
              // timestamp alone the Confirm button landed above the text saying
              // what you would be confirming.
              //
              // GroundTruth keeps EVERY proposal card (and its choice cards) in
              // chronological position instead — see agent/gtChatFeed.js.
              ...(bulkAgent.gtChat ? buildGtBulkFeedItems(bulkAgent, {
                onConfirm: confirmBulkPreview,
                onDismiss: dismissBulkPreview,
                savesImmediately: true,
              }) : [previewReadyToShow(bulkAgent) && {
                key: `preview-${bulkAgent.activePreview.preview_id}`,
                pin: 'end',
                node: (
                  <BulkEditPanel
                    preview={bulkAgent.activePreview}
                    onConfirm={confirmBulkPreview}
                    onDismiss={dismissBulkPreview}
                    approving={bulkAgent.approving}
                    approveError={bulkAgent.approveError}
                    // Confirming auto-saves now (#3), so promising a Save step
                    // the user no longer has to take would be a lie.
                    savesImmediately
                  />
                ),
              }]),
              bulkStageNote && {
                key: `staged-${bulkStageNote.ts}`,
                ts: bulkStageNote.ts,
                node: (
                  (() => {
                    // The save outcome for THIS batch, or null while it is still
                    // in flight. Matched on ts so a later batch's result cannot
                    // relabel an earlier note.
                    const saved = autoSaveOutcome && autoSaveOutcome.ts === bulkStageNote.ts
                      ? autoSaveOutcome : null;
                    const failed = saved && !saved.ok;
                    return (
                      <div className={`clw-bulk-result ${failed || !bulkStageNote.accepted ? 'warn' : 'ok'}`}>
                        <span>
                          {bulkStageNote.text}
                          {bulkStageNote.accepted && !saved && <> Saving…</>}
                          {saved?.ok && <> Saved.</>}
                          {failed && <> The save failed — your changes are still pending, press Save to retry.</>}
                          {bulkStageNote.refused?.length > 0 && (
                            <> Skipped: {bulkStageNote.refused.map((r) => r.reason).join(' ')}</>
                          )}
                        </span>
                        {/* Undo — the counterweight to auto-saving. Offered only
                            once the save actually landed: on a failed save the
                            changes are still pending, so Save is the fix and Undo
                            would be reverting to a state already on screen. */}
                        {saved?.ok && undoEntry && undoEntry.ts >= bulkStageNote.ts && (
                          <span className="clw-bulk-undo">
                            <button type="button" onClick={bulkAgent.gtChat ? undoBulkBatch : revertUndo} disabled={undoing || removingCreatives}>
                              {undoing ? 'Undoing…' : 'Undo'}
                            </button>
                            <button type="button" className="dismiss" onClick={dismissUndo} title="Keep the change">
                              ✕
                            </button>
                          </span>
                        )}
                      </div>
                    );
                  })()
                ),
              },
              bulkAgent.lastBulkFailure && !bulkAgent.lastBulkFailure.awaitingApproval && {
                key: `failed-${bulkAgent.lastBulkFailure.ts}`,
                ts: bulkAgent.lastBulkFailure.ts,
                node: (
                  <div className="clw-bulk-result warn">
                    <span>{bulkAgent.lastBulkFailure.error}</span>
                  </div>
                ),
              },
            ].filter(Boolean) : agentMode === 'optimize' ? [
              previewReadyToShow(optimizeAgent) && {
                key: `opt-preview-${optimizeAgent.activePreview.preview_id}`,
                pin: 'end',
                node: (
                  <BulkEditPanel
                    preview={optimizeAgent.activePreview}
                    onConfirm={(id) => optimizeAgent.approvePreview(id, 'click')}
                    onDismiss={(id) => optimizeAgent.sendMessage(
                      `I'm not applying preview ${id}. Don't apply it.`,
                    )}
                    approving={optimizeAgent.approving}
                    approveError={optimizeAgent.approveError}
                  />
                ),
              },
              // Quick actions, when there is nothing more urgent on screen.
              // Hidden once a preview is live: the Confirm card IS the action at
              // that point, and offering "Apply all" beside it invites a second
              // batch on top of one the user has not answered yet.
              !previewReadyToShow(optimizeAgent)
                && !optimizeAgent.applying
                && optimizeAgent.digest
                && optimizeAgent.messages.length > 0 && {
                key: 'opt-actions',
                pin: 'end',
                node: (
                  <OptimizationActionBar
                    digest={optimizeAgent.digest}
                    busy={isAgentWorking(optimizeAgent)}
                    onAsk={(text) => optimizeAgent.sendMessage(text)}
                  />
                ),
              },
              optimizeAgent.applying && {
                key: 'opt-applying',
                pin: 'end',
                node: (
                  <div className="clw-bulk-result ok">
                    <span>Sending the edits to the platform…</span>
                  </div>
                ),
              },
              optimizeAgent.applyReport && {
                key: `opt-applied-${optimizeAgent.applyReport.ts}`,
                ts: optimizeAgent.applyReport.ts,
                node: (() => {
                  const results = optimizeAgent.applyReport.results || [];
                  const failed = results.filter((r) => !r.ok);
                  return (
                    <div className={`clw-bulk-result ${failed.length ? 'warn' : 'ok'}`}>
                      <span>
                        {`Dispatched ${results.length - failed.length} of ${results.length} edit run(s) to the platform.`}
                        {' Each takes a minute or two.'}
                        {failed.length > 0 && (
                          <> Failed: {failed.map((f) => `${f.label || `#${f.index}`} — ${f.error}`).join('; ')}</>
                        )}
                      </span>
                    </div>
                  );
                })(),
              },
              optimizeAgent.lastOptimizationFailure && !optimizeAgent.lastOptimizationFailure.awaitingApproval && {
                key: `opt-failed-${optimizeAgent.lastOptimizationFailure.ts}`,
                ts: optimizeAgent.lastOptimizationFailure.ts,
                node: (
                  <div className="clw-bulk-result warn">
                    <span>{optimizeAgent.lastOptimizationFailure.error}</span>
                  </div>
                ),
              },
            ].filter(Boolean) : null}
            scopeNote={agentMode === 'optimize' && optimizeAgent.phase !== OPT_PHASE.INELIGIBLE ? (
              // Above the conversation, and present for the WHOLE life of the tab
              // — the window and focus used to live in the run gate, which the
              // sidebar renders only when there is no session, so they vanished
              // the moment a run loaded.
              <OptimizationRunBar
                destination={optimizeAgent.context?.destination || destination || null}
                hasRun={Boolean(optimizeAgent.run)}
                running={optimizeAgent.phase === OPT_PHASE.RUNNING}
                disabled={optimizeAgent.phase === OPT_PHASE.INELIGIBLE || optimizeAgent.phase === OPT_PHASE.LOADING}
                disabledReason={optimizeAgent.phase === OPT_PHASE.INELIGIBLE ? optimizeAgent.context?.reason : null}
                onRun={optimizeAgent.startRun}
              />
            ) : agentMode === 'bulk' && selectedRowIds.size > 0 ? (
              <div className="clw-ai-scope">
                <span>
                  Scoped to <strong>{selectedRowIds.size}</strong> selected {selectedRowIds.size === 1 ? 'row' : 'rows'} —
                  edits won&apos;t touch anything else.
                </span>
                <button type="button" onClick={() => setSelectedRowIds(new Set())}>
                  Clear
                </button>
              </div>
            ) : null}
            showContext={agentMode !== 'runs'}
            config={agentMode === 'runs' ? {
              title: 'Campaign runs',
              note: 'optimizer + edit history',
              autoQaMessage: null,
              // The Runs tab is a list, not a conversation. Render it raw in
              // the feed and drop the composer entirely.
              fallbackRaw: true,
              hideInput: true,
              fallbackMessage: (
                <CampaignRunHistory
                  campaignId={campaignId}
                  runs={campaignRunHistory.runs}
                  loading={campaignRunHistory.loading}
                  error={campaignRunHistory.error}
                  refreshing={campaignRunHistory.refreshing}
                  onRefresh={campaignRunHistory.refresh}
                  onApplySuggestion={campaignRunHistory.applySuggestion}
                  isApplied={campaignRunHistory.isSuggestionApplied}
                  applyingKeys={campaignRunHistory.applyingKeys}
                  notice={campaignRunHistory.notice}
                />
              ),
            } : agentMode === 'bulk' ? {
              title: 'Bulk editor',
              note: 'edits many rows at once',
              // GroundTruth: plain words and no "Try asking" list, shared with
              // the launched-campaign details page (agent/gtBulkPlaceholder.js).
              placeholder: platformIsYahoo ? YAHOO_BULK_PLACEHOLDER : isGt ? gtBulkPlaceholder(bulkRowLabel) : {
                title: 'What do you want to change?',
                body: (
                  <>I can change <strong>dates</strong>, <strong>budgets</strong> and <strong>geo</strong> across many {bulkRowLabel} at once — and duplicate or delete rows. I'll show you exactly what changes and ask before anything is staged.</>
                ),
                sectionLabel: 'Try asking',
                prompts: [
                  `Move every ${bulkRowLabel.replace(/s$/, '')} to a 2026-09-01 → 2026-09-30 flight.`,
                  'Which rows have no end date?',
                  'Add Denver to the geo on every Texas row.',
                  'Cut the budget 15% on the lowest-spending rows.',
                  'Show me a summary of the plan.',
                ],
              },
              autoQaMessage: null,
              loadingMessage: `Reading your ${bulkRowLabel} — one moment.`,
              inputPlaceholder: `Describe the change across your ${bulkRowLabel}…`,
              suggestionHint: null,
            } : agentMode === 'optimize' ? {
              title: 'Optimization',
              note: 'reviews analysis and applies approved suggestions',
              placeholder: optimizeReady ? {
                title: 'What do you want to apply?',
                body: (
                  <>I&apos;ve read this campaign&apos;s analysis reports. I can <strong>explain</strong> any
                  suggestion, and <strong>apply</strong> the ones you pick — those go straight to the
                  live campaign, so I&apos;ll show you exactly what changes and ask first.</>
                ),
                sectionLabel: 'Try asking',
                prompts: [
                  'Which suggestions would move the needle most?',
                  'Apply the high-priority ones.',
                  'Why does it want to change that bid?',
                  'Which of these are just advice, not changes?',
                ],
              } : null,
              autoQaMessage: null,
              loadingMessage: 'Reading the campaign analysis reports — summarising the findings.',
              inputPlaceholder: 'Ask about a suggestion, or say which to apply…',
              inputPlaceholderNoSession: optimizeAgent.phase === OPT_PHASE.RUNNING
                ? 'Waiting for campaign analysis…'
                : 'Run campaign analysis to get started',
              // Before a run is loaded there is no session, so this slot carries
              // the run lifecycle: eligibility, the Run button, the in-flight
              // spinner, or the failure.
              fallbackMessage: (
                <OptimizationRunGate
                  phase={optimizeAgent.phase}
                  context={optimizeAgent.context}
                  runError={optimizeAgent.runError}
                  platformLabel={(optimizeAgent.context?.platform || bulkPlatform || '').toUpperCase()}
                  // MadHive's optimizer needs a reporting-metrics ZIP. The grid
                  // panel used to host this upload; CLW now hosts the same modal
                  // so the Optimization tab is not a dead end.
                  onUploadRun={optimizeAgent.context?.needsReportingMetrics
                    ? () => setOptimizeUploadOpen(true)
                    : null}
                />
              ),
              suggestionHint: null,
            } : isGt ? {
              title: 'Sidekick agent',
              note: 'can review & suggest edits',
              placeholder: {
                title: 'How can I help?',
                body: (
                  <>I'm reviewing your GroundTruth Excel against CTS rules. I can <strong>explain findings</strong>, <strong>suggest edits</strong> to specific cells, <strong>compare ad groups</strong>, or <strong>approve for launch</strong>. Click a cell first to ask about it in context.</>
                ),
                sectionLabel: 'Try asking',
                prompts: [
                  'Walk me through the errors and warnings.',
                  'Which ad groups are missing a product value?',
                  { label: <>Explain the CTS rule for <em>Adgroup Product</em>.</>, draft: 'Explain the CTS rule for Adgroup Product.' },
                  'Compare budget across ad groups.',
                  'Is this plan ready to launch?',
                ],
              },
              autoQaMessage: 'Running the auto-QA pass — walking every error, warning, and info from the validator and preparing concrete Excel edits for each finding. This usually takes 1–3 minutes depending on how many findings the validator surfaced.',
              inputPlaceholder: 'Ask about a finding, suggest an edit, or say "approve"…',
              suggestionHint: 'Click a suggestion to apply it via the agent. The agent edits the parsed JSON, re-runs CTS validation, and refreshes the grid below. The original Excel is never modified.',
            } : isMmgyClw ? {
              title: 'Sidekick agent',
              note: 'can review & suggest edits',
              placeholder: {
                title: 'How can I help?',
                body: isMmgyDv360 ? (
                  <>I'm reviewing your MMGY DV360 plan. I can <strong>explain fields</strong>, <strong>suggest edits</strong> to specific line items, <strong>compare insertion orders</strong>, or <strong>approve for launch</strong>. Click a cell first to ask about it in context.</>
                ) : (
                  <>I'm reviewing your MMGY TTD plan. I can <strong>explain fields</strong>, <strong>suggest edits</strong> to specific ad groups or campaigns, <strong>compare budgets</strong>, or <strong>approve for launch</strong>. Click a cell first to ask about it in context.</>
                ),
                sectionLabel: 'Try asking',
                prompts: isMmgyDv360 ? [
                  'Summarise the plan — IOs, line items, budget, flight.',
                  'Which line items have suspicious daily caps?',
                  { label: <>Explain <em>pacing.dailyMaxImpressions</em>.</>, draft: 'Explain pacing.dailyMaxImpressions.' },
                  'Are any line items missing a displayName?',
                  'Is this plan ready to launch?',
                ] : [
                  'Summarise the plan — campaigns, ad groups, budget, flight.',
                  'Which ad groups have an unresolved geographyId?',
                  { label: <>Explain <em>tactic</em> derivation.</>, draft: 'Explain how the tactic field is derived.' },
                  'Are any campaigns missing a CampaignName?',
                  'Is this plan ready to launch?',
                ],
              },
              autoQaMessage: `Running the auto-QA pass — summarising the ${isMmgyDv360 ? 'DV360' : 'TTD'} plan and flagging obvious issues. Usually finishes in under a minute.`,
              inputPlaceholder: 'Ask about a field, suggest an edit, or say "approve"…',
              suggestionHint: 'Click a suggestion to apply it via the agent. The agent edits the parsed plan JSON, re-validates, and refreshes the grid below. The original IO is never modified.',
            } : {
              title: 'Sidekick agent',
              note: 'can review & suggest edits',
              inputPlaceholderNoSession: 'Agent unavailable for this destination',
              fallbackMessage: `Hi — your plan parsed into ${fmtInt(reviewRows.length)} line items. Click any cell to see full context.`,
            }}
            context={{
              activeGroup: effectiveActiveGroup,
              rowsCount: reviewRows.length,
              rowLabel,
            }}
            suggestionStatus={suggestionStatus}
            suggestionErrors={suggestionErrors}
            onApplySuggestion={applySuggestion}
            onApplyAllSuggestions={applyAllSuggestions}
            onDismissSuggestion={dismissSuggestion}
            onClearSuggestionError={clearSuggestionError}
          />
      </ReviewGrid>

      {optimizeUploadOpen && (
        <OptimizeCampaignModal
          isOpen
          onClose={() => setOptimizeUploadOpen(false)}
          campaign={{
            id: campaignId,
            destination: destination || optimizeAgent.context?.destination,
            name: campaignName,
            platform_campaign_id: platformCampaignId,
          }}
          platformCampaignId={optimizeAgent.context?.platformCampaignId || platformCampaignId}
          accountId={accountId}
          user={{ userId }}
          onOptimizeComplete={(data) => {
            setOptimizeUploadOpen(false);
            optimizeAgent.startRun(
              data.customPrompt || null,
              data.dateParams || null,
              data.reportingMetricsPath || null,
            );
          }}
        />
      )}
    </div>
  );
}
